import { useState, useEffect, useCallback, useRef } from "react";
import { ukDate } from "@/lib/utils";
import { portalEnv, LOCAL, DEVELOP, STAGING } from "@/lib/portalEnv";
import { directorSignedBy } from "@/lib/signerMatch";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import { GeoBlockDialog, runGeoGate } from "@/components/GeoBlockDialog";
import AUSDocumentGeneration from "@/pages/aus/DocumentGeneration";
import { StandingOrderUpload } from "@/pages/workflow/StandingOrderUpload";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FileText,
  Send,
  CreditCard,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  ExternalLink,
  DollarSign,
  Banknote,
  Calculator,
  Search,
  Clock,
  RefreshCw,
  Mail,
  Eye,
  Download,
  Lock,
  BadgeCheck,
  MessageSquare,
  Upload,
  Activity
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { WorkflowProgress } from "@/components/WorkflowProgress";
import { StatusBadge, CurrencyBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import DocumentGeneration from "@/pages/legal/DocumentGeneration";
import { BeneficiaryCard } from "@/components/BeneficiaryCard";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import {
  getApplication,
  getApplicationBeneficiary,
  generateContract,
  sendToDocuSign,
  markDocuSignSigned,
  sendTestPayment,
  resetWorkflowElements,
  confirmTestPayment,
  confirmDisbursement,
  downloadDisbursementPdf,
  verifyCompany,
  requestNameChange,
  refreshMondayApplication,
  pullContractBankDetails,
  searchCompany,
  searchCompaniesHouse,
  searchAusBusinesses,
  selectCompany,
  searchLoanDiskLoan,
  linkLoanDiskLoan,
  markAsNewLoan,
  validateLoanDiskLoan,
  confirmLoanDiskValidation,
  calculateDisbursement,
  requestDisbursementApproval as requestDisbursementApprovalOld,
  searchLoanDiskByBorrower,
  getLoanDiskLoanBySF,
  confirmNetInHand,
  requestNetInHandApproval,
  getTodaysAirtablePayments,
  toggleConsolidation,
  addConsolidationLender,
  updateConsolidationLender,
  removeConsolidationLender,
  verifyLenderCoP,
  runCoPCheck,
  mockAcceptCoP,
  getCapayMode,
  setTrustee,
  requestConsolidationApproval,
  resetConsolidationForReentry,
  clearBankRejection,
  resetBankDetails,
  getPortalSettings,
  getStandingOrderEmailPreview,
  listSoEmailAttachments,
  uploadSoEmailAttachment,
  deleteSoEmailAttachment,
  getDirectory,
  initiateStandingOrder,
  checkMondayStatus,
  searchDocuSignEnvelope,
  updateDocuSignStatus,
  refreshDocuSignStatus,
  linkDocuSignEnvelope,
  getDocuSignSigningDetails,
  overrideDocuSignRisk,
  getDisbursementChecklist,
  overrideDirectorMatch,
  requestDisbursementApproval,
  verifyPasswordForDisbursement,
  executeDisbursement,
  getDisbursementStatus,
  getPaymentTracking,
  uploadDocusignToLoanDisk,
  getPrefundingQuestions,
  completePrefundingCall,
  fetchPrefundingAircallRecording,
  getPrefundingCallStatus,
  resetPrefundingCall,
  savePrefundingDraft,
  uploadPrefundingRecording,
  resetNetInHand,
  getCapayAccountBySf,
} from "@/lib/api";
import { sanitizedHtmlProps } from '@/lib/sanitizeEmailHtml';
import api from "@/lib/api";
import { SettlementAccountDialog, settlementGateFromError, errorDetailText } from "@/pages/aus/SettlementAccountDialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { DISBURSEMENT_PERMISSIONS } from "@/lib/permissions";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GCCollectionPanel } from "@/pages/workflow/GCCollectionPanel";
import LoanDiskCreateWizard from "@/pages/workflow/LoanDiskCreateWizard";
import { BecsMandatePanel } from "@/components/BecsMandatePanel";
import PreFundingCallDialog from "@/pages/workflow/PreFundingCallDialog";
import { ExtraCallsPanel } from "@/pages/workflow/ExtraCallsPanel";
import PrefundingSmsScheduler from "@/pages/workflow/PrefundingSmsScheduler";
import { InfoTip } from "@/components/InfoTip";
import { FileSignature, ShieldAlert } from "lucide-react";
import { Trash2, Plus, AlertTriangle, Phone, RotateCcw } from "lucide-react";
import { Paperclip, Landmark } from "lucide-react";
import { copProviderLabel } from "@/lib/copProvider";
import { disbursementFailureMessage } from "@/lib/disbursementOutcome";
import { openStandingOrderAttachment } from "@/lib/protectedFile";

// Base stages (all applications)
const baseStages = [
  { id: 1, key: "contract", label: "Contract & DocuSign", icon: FileText },
  { id: 2, key: "bank", label: "Bank Verification (CoP)", icon: Building2 },
  { id: 3, key: "standing", label: "Initiate Payment", icon: CreditCard },
  { id: 4, key: "loandisk", label: "Add Loan to LoanDisk", icon: "£" },
];

// Hard prerequisites before a loan may be created in LoanDisk — mirrors the
// backend gate in loandisk_direct.create_loan_direct.
const LOAN_GATES = [
  { key: "docusign", label: "DocuSign contracts signed & returned", met: (a) => a?.docusign_status === "completed" },
  { key: "cop", label: "Bank account verified (CoP confirmed)", met: (a) => a?.bank_details_status === "verified" },
  { key: "so-sent", label: "Standing Order email sent", met: (a) => !!a?.standing_order_initiated },
  { key: "so-file", label: "Standing Order attachment added", met: (a) => !!a?.standing_order_file_uploaded },
];

function LoanGate({ application, onOpen, testId }) {
  const aus = application?.currency === "AUD" || ["AUS", "AU"].includes((application?.country || "").toUpperCase());
  const gateLabel = (g) => {
    if (!aus) return g.label;
    if (g.key === "so-sent") return "GC Direct Debit (BECS) mandate active";
    if (g.key === "cop") return "Bank account verified (Capay CoP or manual)";
    return g.label;
  };
  const gates = LOAN_GATES.filter((g) => !(aus && g.key === "so-file"));
  const unmet = gates.filter((g) => !g.met(application));
  return (
    <div className="space-y-2">
      <div className="p-3 border rounded-lg bg-white space-y-1" data-testid="loan-gate-checklist">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
          Required before the loan can be added
        </p>
        {gates.map((g) => {
          const ok = g.met(application);
          return (
            <div key={g.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-700" : "text-red-600"}`}
              data-testid={`loan-gate-${g.key}`}>
              {ok ? <CheckCircle className="w-3.5 h-3.5 shrink-0" /> : <XCircle className="w-3.5 h-3.5 shrink-0" />}
              {gateLabel(g)}
            </div>
          );
        })}
      </div>
      <Button
        className="bg-emerald-600 hover:bg-emerald-700"
        onClick={onOpen}
        disabled={unmet.length > 0}
        title={unmet.length ? "Complete the items above first" : undefined}
        data-testid={testId}
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Loan to LoanDisk
      </Button>
      {unmet.length > 0 && (
        <p className="text-xs text-red-600" data-testid="loan-gate-blocked-note">
          {aus
            ? "The loan goes up together with the signed contracts and the active Direct Debit mandate — complete the items above first."
            : "The loan goes up together with the signed contracts and Standing Order — complete the items above first."}
        </p>
      )}
    </div>
  );
}

// Get stages based on whether it's a Top Up
const getStages = (isTopUp) => {
  if (isTopUp) {
    // Ordering (9 Aug 2026, upgrade list item 6): Net-in-Hand is confirmed
    // BEFORE the nominal is sent — this matches the backend send-test-payment
    // gate (which requires the full checklist incl. net_in_hand_confirmed),
    // so the UI no longer shows a nominal stage that refuses to fire.
    return [
      ...baseStages,
      { id: 5, key: "netinhand", label: "Confirm Net in Hand", icon: Calculator },
      { id: "6a", key: "nominal", label: "Send Nominal Payment", icon: DollarSign },
      { id: "6b", key: "prefunding", label: "Pre-Funding Call", icon: Phone },
      { id: 7, key: "disbursement", label: "Disburse Loan", icon: CheckCircle },
      { id: 8, key: "tracking", label: "Payment Tracking", icon: Activity },
    ];
  }
  return [
    ...baseStages,
    { id: "5a", key: "nominal", label: "Send Nominal Payment", icon: DollarSign },
    { id: "5b", key: "prefunding", label: "Pre-Funding Call", icon: Phone },
    { id: 6, key: "disbursement", label: "Disburse Loan", icon: CheckCircle },
    { id: 7, key: "tracking", label: "Payment Tracking", icon: Activity },
  ];
};

// Blank critical-info answers for the graded pre-funding call
const EMPTY_CRITICAL = {
  sort_code: "", account_number: "", nominal_amount: "",
  payment_frequency: "", payment_amount: "", start_date: "",
};

export default function WorkflowChecklist() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const isAus = application?.currency === "AUD" || ["AUS", "AU"].includes((application?.country || "").toUpperCase());
  const curSym = isAus ? "A$" : "£";
  const payoutRail = isAus ? "Capay" : "GC";   // payout rail for lender payoffs + disbursements (repayments run on GoCardless in both regions)
  const [capayMode, setCapayMode] = useState(null);
  useEffect(() => {
    if (isAus) getCapayMode().then((d) => setCapayMode(d?.mode)).catch(() => {});
  }, [isAus]);
  // AUS — Capay settlement (repayment) account; created automatically before any money leaves
  const [settlementAcc, setSettlementAcc] = useState(undefined);
  const [settlementGate, setSettlementGate] = useState(null);
  const loadSettlementAcc = async (sf) => {
    if (!sf) return;
    try { const r = await getCapayAccountBySf(sf); setSettlementAcc(r.account || null); } catch { setSettlementAcc(null); }
  };
  useEffect(() => {
    if (isAus && application?.application_id) loadSettlementAcc(application.application_id);
  }, [isAus, application?.application_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const openSettlementGate = async (flow) => {
    try {
      const r = await api.post("/capay/customers/preview", { sf_number: application.application_id, application_id: appId });
      setSettlementGate({ flow, sf_number: application.application_id, form: r.data?.form || {}, missing_fields: r.data?.missing_fields || [],
        message: "Create the customer's Capay settlement (repayment) account. It is created automatically when the nominal is sent — you can do it now." });
    } catch (e) { toast.error(errorDetailText(e, "Could not load the settlement account form")); }
  };
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Dialog states
  const [testPaymentDialog, setTestPaymentDialog] = useState(false);
  const [manualNominalDialog, setManualNominalDialog] = useState(false);
  const [nominalStatus, setNominalStatus] = useState(null);
  const [nominalStatusLoading, setNominalStatusLoading] = useState(false);
  const [manualNominalAmount, setManualNominalAmount] = useState("");
  const [manualNominalRef, setManualNominalRef] = useState("");
  const [resetElementsDialog, setResetElementsDialog] = useState(false);
  const [syncingMonday, setSyncingMonday] = useState(false);
  const [resetSelection, setResetSelection] = useState({});
  const [disbursementDialog, setDisbursementDialog] = useState(false);
  const [geoBlock, setGeoBlock] = useState(null);
  const [confirmedAmount, setConfirmedAmount] = useState("");
  
  // Formal disbursement process state
  const [disbursementStep, setDisbursementStep] = useState(1); // 1: Checklist, 2: Approval, 3: Password, 4: Confirm Amount
  const [disbursementChecklist, setDisbursementChecklist] = useState(null);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [confirmAmountInput, setConfirmAmountInput] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // "Also pay the lender payoffs now" (Paul SF-37219, 12 Oct 2026) — mirrors the Consolidation panel's payable rules
  const [payLendersNow, setPayLendersNow] = useState(false);
  const lenderPayableReason = (l) => {
    if (["paid", "paid_manually"].includes(l.payout_status || "")) return null;
    if (l.payout_status === "processing") return "already being paid";
    if (!application?.consolidation_approved) return "consolidation not approved";
    if (!["verified", "partial_match"].includes(l.cop_status || "")) return `CoP ${l.cop_status || "unchecked"}`;
    if (!(parseFloat(l.amount || 0) > 0)) return "amount is zero";
    if (!(l.sort_code && l.account_number)) return "bank details missing";
    return null;
  };
  const unpaidLenders = (application?.consolidation_lenders || []).filter((l) => !["paid", "paid_manually"].includes(l.payout_status || ""));
  const unpayableLenders = unpaidLenders.filter((l) => lenderPayableReason(l));
  const allLendersPayable = unpaidLenders.length > 0 && unpayableLenders.length === 0;
  useEffect(() => { setPayLendersNow(allLendersPayable); }, [allLendersPayable, application?.id]);
  
  // Disbursement calculation state
  const [disbursementCalc, setDisbursementCalc] = useState(null);

  // Stage 7 — post-funding payment tracking
  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  
  // LoanDisk state
  const [loandiskSearchResult, setLoandiskSearchResult] = useState(null);
  const [loandiskLoans, setLoandiskLoans] = useState([]);
  const [selectLoanDialog, setSelectLoanDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [validationResults, setValidationResults] = useState(null);
  const [selectedLoanForValidation, setSelectedLoanForValidation] = useState(null);
  const [loandiskAutoSearchDone, setLoandiskAutoSearchDone] = useState(false);
  const [loandiskDiscrepancies, setLoandiskDiscrepancies] = useState(null);
  
  // Net in Hand state (for Top Ups)
  const [existingSFSearch, setExistingSFSearch] = useState("");
  const [existingLoans, setExistingLoans] = useState([]);
  const [selectedExistingLoan, setSelectedExistingLoan] = useState(null);
  const [existingLoanSearching, setExistingLoanSearching] = useState(false);
  const [airtablePayments, setAirtablePayments] = useState({ total_amount: 0, payments: [], found: false });
  
  // Portal settings state
  const [portalSettings, setPortalSettings] = useState({
    consolidation_expiry_hours: 2
  });
  
  // Net in Hand expiry timer state
  const [netInHandSecondsRemaining, setNetInHandSecondsRemaining] = useState(null);
  const [netInHandExpired, setNetInHandExpired] = useState(false);
  
  // Standing Order email preview state
  const [standingOrderDialog, setStandingOrderDialog] = useState(false);
  const [standingOrderPreview, setStandingOrderPreview] = useState(null);
  const [soAttachments, setSoAttachments] = useState([]);
  const [soAttUploading, setSoAttUploading] = useState(false);
  const [soRecipient, setSoRecipient] = useState("");
  const [soCc, setSoCc] = useState([]);
  const [soBcc, setSoBcc] = useState([]);
  const [soStartDate, setSoStartDate] = useState("");
  const [portalUsers, setPortalUsers] = useState([]);
  const [wizardDealId, setWizardDealId] = useState(null);
  const [ausWizard, setAusWizard] = useState(null);
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  
  // Contract & DocuSign state (new flow)
  const [checkingMondayStatus, setCheckingMondayStatus] = useState(false);
  const [mondayStatusResult, setMondayStatusResult] = useState(null);
  const [searchingDocuSign, setSearchingDocuSign] = useState(false);
  const [docuSignEnvelope, setDocuSignEnvelope] = useState(null);
  const [signingDetailsOpen, setSigningDetailsOpen] = useState(false);
  const [signingDetails, setSigningDetails] = useState(null);
  const [loadingSigningDetails, setLoadingSigningDetails] = useState(false);
  const [docuSignAutoCheckDone, setDocuSignAutoCheckDone] = useState(false);
  
  // Companies House verification state
  const [verifyingCompany, setVerifyingCompany] = useState(false);
  const [companySearchResults, setCompanySearchResults] = useState(null);
  const [showCompanySearch, setShowCompanySearch] = useState(false);
  const [chVerificationData, setChVerificationData] = useState(null);
  const [requestingNameChange, setRequestingNameChange] = useState(false);
  const [pullingBank, setPullingBank] = useState(false);
  const [chAutoVerifyDone, setChAutoVerifyDone] = useState(false);
  
  // Get current user for mock email
  const { user, hasAnyPermission } = useAuth();
  // The buttons that send money out are offered only to a user the API will accept.
  const canDisburse = hasAnyPermission(DISBURSEMENT_PERMISSIONS);
  // Setting a DocuSign status by hand is a dev-environment tool for mock envelopes. The API
  // refuses anyone without `can_configure_system` and any environment but these three, so the
  // buttons are shown only where a click can succeed; a build that declares no environment shows none.
  const canSimulateDocuSign =
    hasAnyPermission(["can_configure_system"]) &&
    [LOCAL, DEVELOP, STAGING].includes(portalEnv());
  const noDisbursementNote = canDisburse ? null : (
    <p className="text-xs text-muted-foreground" data-testid="disbursement-permission-note">
      Sending payments from this screen needs the disbursement permission.
    </p>
  );
  
  // Consolidation state
  const [newLender, setNewLender] = useState({
    lender_name: "",
    amount: "",
    sort_code: "",
    account_number: "",
    account_name: ""
  });
  const [addingLender, setAddingLender] = useState(false);
  const [verifyingLenderCoP, setVerifyingLenderCoP] = useState(null);
  const [lenderCatalog, setLenderCatalog] = useState([]);
  const [customLenderName, setCustomLenderName] = useState(false);

  // LoanDisk direct-creation wizard
  const [loanDiskWizardOpen, setLoanDiskWizardOpen] = useState(false);

  // Load third-party lender catalog (Portal Settings) for the consolidation dropdown
  useEffect(() => {
    if (!application) return;
    api.get(`/contracts/third-party-lenders?region=${isAus ? "AUS" : "UK"}`)
      .then(({ data }) => setLenderCatalog(data.lenders || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.id, isAus]);

  // Stage 7 — pull live GC payment tracking once the deal is funded
  const loadTracking = useCallback(async () => {
    try {
      setTrackingLoading(true);
      const data = await getPaymentTracking(appId);
      setTracking(data);
    } catch (e) {
      console.error("Failed to load payment tracking:", e);
    } finally {
      setTrackingLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    if (application?.disbursement_confirmed) loadTracking();
  }, [application?.disbursement_confirmed, loadTracking]);
  
  // Pre-Funding Call state
  const [prefundingQuestions, setPrefundingQuestions] = useState([]);
  const [prefundingResponses, setPrefundingResponses] = useState({});
  const [prefundingNotes, setPrefundingNotes] = useState("");
  const [prefundingRecording, setPrefundingRecording] = useState(null);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [prefundingDialogOpen, setPrefundingDialogOpen] = useState(false);
  const [prefundingCritical, setPrefundingCritical] = useState({ ...EMPTY_CRITICAL });
  const [prefundingDraftInfo, setPrefundingDraftInfo] = useState(null);

  const prefundingRequiredQs = prefundingQuestions.filter((q) => q.required);
  const prefundingAnsweredRequired = prefundingRequiredQs.filter((q) => {
    const a = prefundingResponses[q.id];
    return (q.input_type || "checkbox") === "checkbox" ? a === true : a != null && String(a).trim() !== "";
  }).length;
  
  // Get dynamic stages based on application type (AUS relabels the fundamentals)
  const stages = (application ? getStages(application.is_topup) : getStages(false)).map((s) => {
    if (!isAus) return s;
    if (s.key === "standing") return { ...s, label: "Direct Debit (GC BECS)" };
    if (s.key === "bank") return { ...s, label: "Bank Verification (Manual)" };
    if (s.key === "loandisk") return { ...s, icon: "A$" };
    return s;
  });
  
  // Check if application is funded (locked for editing)
  const isFunded = application?.workflow_status === "funded" || application?.disbursement_confirmed;

  // Calculate initial seconds remaining from expiry timestamp
  const calculateSecondsRemaining = useCallback((expiresAt) => {
    if (!expiresAt) return null;
    try {
      const expiry = new Date(expiresAt);
      const now = new Date();
      const diff = Math.floor((expiry - now) / 1000);
      return diff > 0 ? diff : 0;
    } catch {
      return null;
    }
  }, []);

  // Update timer when application changes - skip if funded
  useEffect(() => {
    // Don't run timer if deal is funded
    if (isFunded) {
      setNetInHandSecondsRemaining(null);
      setNetInHandExpired(false);
      return;
    }
    
    if (application?.net_in_hand_confirmed) {
      // A confirmed figure with no expiry is expired, as the backend treats it.
      const seconds = calculateSecondsRemaining(application.net_in_hand_expires_at) ?? 0;
      setNetInHandSecondsRemaining(seconds);
      setNetInHandExpired(seconds === 0);
    } else {
      setNetInHandSecondsRemaining(null);
      setNetInHandExpired(false);
    }
  }, [application, calculateSecondsRemaining, isFunded]);

  // Countdown timer effect - skip if funded
  useEffect(() => {
    // Don't run timer if deal is funded
    if (isFunded) return;
    if (netInHandSecondsRemaining === null || netInHandSecondsRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setNetInHandSecondsRemaining(prev => {
        if (prev <= 1) {
          setNetInHandExpired(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [netInHandSecondsRemaining, isFunded]);

  // Format seconds as MM:SS
  const formatTimeRemaining = (seconds) => {
    if (seconds === null || seconds <= 0) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Recalculate net in hand — keeps the ORIGINAL loan: re-fetches it with a
  // fresh LoanDisk balance and auto re-confirms, so the user never has to
  // re-search after the 15-minute expiry.
  const handleResetNetInHand = async () => {
    const previousSF = (application?.existing_loan_sf ||
      selectedExistingLoan?.loan_application_id || "").trim();
    try {
      setProcessing("reset_net_in_hand");
      // Clear the net in hand confirmation to allow recalculation
      await resetNetInHand(appId);
      setAirtablePayments({ total_amount: 0, payments: [], found: false });
      if (previousSF) {
        let reloaded = false;
        try {
          const result = await getLoanDiskLoanBySF(previousSF);
          if (result.found && result.loan && !result.loan.is_restructured) {
            const fresh = result.loan;
            setExistingLoans([fresh]);
            setSelectedExistingLoan(fresh);
            setExistingSFSearch(previousSF);
            reloaded = true;
            const borrowerName = fresh.borrower_business_name || fresh.borrower_name;
            try {
              if (!fresh.loan_application_id) {
                throw new Error("The loan has no application number to confirm against");
              }
              const confirmRes = await confirmNetInHand(
                appId,
                fresh.loan_application_id
              );
              toast.success(
                `Net in Hand recalculated: ${curSym}${confirmRes.net_in_hand?.toLocaleString()} — ${previousSF} re-used with a fresh balance`,
                { duration: 8000 }
              );
            } catch (confirmErr) {
              toast.warning(
                `${previousSF} re-loaded with a fresh balance — review and press Confirm to finish the recalculation`,
                { duration: 9000 }
              );
            }
            await fetchAirtablePayments(previousSF, borrowerName);
          }
        } catch (reloadErr) {
          // fall through to the cleared state below
        }
        if (!reloaded) {
          setSelectedExistingLoan(null);
          setExistingLoans([]);
          toast.warning(
            `Net in Hand reset — could not re-load ${previousSF} (it may be restructured or unavailable). Search for the loan again.`,
            { duration: 9000 }
          );
        }
      } else {
        setSelectedExistingLoan(null);
        setExistingLoans([]);
        toast.info("Net in Hand reset - please recalculate");
      }
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Failed to reset Net in Hand");
    } finally {
      setProcessing(null);
    }
  };

  useEffect(() => {
    loadData();
  }, [appId]);

  // Auto-search DocuSign when workflow opens and monday status is "Doc's Out"
  const [docuSignAutoSearchDone, setDocuSignAutoSearchDone] = useState(false);
  
  useEffect(() => {
    if (application && 
        application.monday_status?.toLowerCase() === "doc's out" && 
        !docuSignAutoSearchDone &&
        (!application.docusign_status || application.docusign_status === "not_sent" || 
         !["signed", "completed"].includes(application.docusign_status))) {
      autoSearchDocuSign();
    }
  }, [application, docuSignAutoSearchDone]);

  const autoSearchDocuSign = async () => {
    if (!application?.application_id) return;
    
    try {
      setDocuSignAutoSearchDone(true);
      setSearchingDocuSign(true);
      const result = await searchDocuSignEnvelope(appId);
      setDocuSignEnvelope(result.envelope);
      
      if (result.status === "found") {
        // Silently refresh data if envelope found
        loadData();
      }
    } catch (error) {
      console.error("Auto DocuSign search failed:", error);
    } finally {
      setSearchingDocuSign(false);
    }
  };

  // Auto-run the DocuSign signer / fraud check when the envelope has completed but the check never ran.
  // Keyed on docusign_fraud_checked_at (not the recipients list — the status refresh fills that in too,
  // which used to stop this from firing and left the nominal locked on "DocuSign fraud check done").
  useEffect(() => {
    if (application &&
        application.docusign_status === "completed" &&
        application.docusign_envelope_id &&
        !application.docusign_envelope_id.startsWith("MOCK-") &&
        !application.docusign_fraud_checked_at &&
        !docuSignAutoCheckDone) {
      autoFetchDocuSignDetails();
    }
  }, [application, docuSignAutoCheckDone]);

  const autoFetchDocuSignDetails = async () => {
    if (!application?.docusign_envelope_id) return;
    
    try {
      setDocuSignAutoCheckDone(true);
      setLoadingSigningDetails(true);
      const details = await getDocuSignSigningDetails(appId);
      setSigningDetails(details);
      
      // Refresh to get the updated signer data stored in application
      loadData();
    } catch (error) {
      console.error("Auto DocuSign details fetch failed:", error);
    } finally {
      setLoadingSigningDetails(false);
    }
  };

  // Auto-search LoanDisk when workflow opens
  useEffect(() => {
    if (application && !application.loandisk_validated && !loandiskAutoSearchDone) {
      autoSearchLoanDisk();
    }
  }, [application, loandiskAutoSearchDone]);

  // Auto-verify Companies House when workflow opens (if not already verified)
  useEffect(() => {
    if (application && !application.ch_verification_status && !chAutoVerifyDone) {
      autoVerifyCompaniesHouse();
    } else if (application?.ch_verification_status) {
      // If already verified, load existing data into state
      setChVerificationData({
        verification_status: application.ch_verification_status,
        verified: application.ch_verification_status === "verified",
        company_name_ch: application.ch_company_name,
        legal_name: application.ch_legal_name,
        trading_name: application.ch_trading_name,
        is_sole_trader: !!application.ch_legal_name && /sole|individual/i.test(application.ch_company_type || ""),
        company_number: application.ch_company_number,
        company_status: application.ch_company_status,
        company_type: application.ch_company_type,
        date_of_creation: application.ch_date_of_creation,
        registered_address: application.ch_registered_address,
        has_insolvency_history: application.ch_has_insolvency_history,
        warnings: application.ch_warnings || [],
        directors: application.ch_directors || []
      });
    }
  }, [application, chAutoVerifyDone]);

  const autoVerifyCompaniesHouse = async () => {
    if (!application?.company_name) return;
    
    try {
      setChAutoVerifyDone(true);
      setVerifyingCompany(true);
      const result = await verifyCompany(appId);
      setChVerificationData(result);
      
      if (result.verified) {
        toast.success(isAus ? "Company verified via ASIC" : "Company verified via Companies House");
      } else if (result.verification_status === "partial_match") {
        toast.info("Company found - please confirm match");
      } else if (result.warnings?.length > 0) {
        toast.warning("Company verification has warnings");
      }
      
      // Refresh application data to get updated fields
      loadData();
    } catch (error) {
      console.error("Auto company verification failed:", error);
      toast.error(errorDetailMessage(error, isAus ? "ASIC verification failed" : "Companies House verification failed"));
    } finally {
      setVerifyingCompany(false);
    }
  };

  const handleManualVerifyCompany = async () => {
    try {
      setVerifyingCompany(true);
      const result = await verifyCompany(appId);
      setChVerificationData(result);
      
      if (result.verified) {
        toast.success(isAus ? "Company verified via ASIC" : "Company verified via Companies House");
      } else if (result.verification_status === "partial_match") {
        toast.info("Company found - please confirm match");
      } else if (result.warnings?.length > 0) {
        toast.warning("Company verification has warnings");
      }
      
      loadData();
    } catch (error) {
      console.error("Company verification failed:", error);
      toast.error(error.response?.data?.detail || (isAus ? "ASIC verification failed" : "Companies House verification failed"));
    } finally {
      setVerifyingCompany(false);
    }
  };

  const handleSelectCompanyMatch = async (companyNumber) => {
    try {
      setVerifyingCompany(true);
      const result = await selectCompany(appId, companyNumber);
      setChVerificationData(result);
      setShowCompanySearch(false);
      toast.success("Company selected and verified");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to select company");
    } finally {
      setVerifyingCompany(false);
    }
  };

  const handlePullContractBankDetails = async () => {
    try {
      setPullingBank(true);
      const result = await pullContractBankDetails(appId);
      if (result.queued) {
        toast.success(
          result.auto_verified
            ? `Bank details pulled from the contract (${isAus ? "BSB" : "sort"} ${result.sort_code || "?"}, acct …${result.account_number_last4 || "?"}) — CoP full match, verified automatically ✓`
            : `Bank details pulled from the contract (${isAus ? "BSB" : "sort"} ${result.sort_code || "?"}, acct …${result.account_number_last4 || "?"}) — queued for credit approval` +
              (result.cop_status ? `, CoP: ${result.cop_status.replaceAll("_", " ")}` : ", CoP check run"));
      } else {
        toast.info(result.reason || "Nothing to pull");
      }
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      if (error.response?.status === 404) toast.warning(detail);
      else toast.error("Pull failed: " + detail);
    } finally {
      setPullingBank(false);
    }
  };

  const handleRequestNameChange = async () => {
    try {
      setRequestingNameChange(true);
      const result = await requestNameChange(appId);
      toast.success(result.slack_ok
        ? "Credit notified on Slack — the application name stays as-is until they action it"
        : "Request logged, but the Slack message didn't send — check Slack settings");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send the request to Credit");
    } finally {
      setRequestingNameChange(false);
    }
  };

  const autoSearchLoanDisk = async () => {
    if (!application?.application_id) return;
    
    try {
      setLoandiskAutoSearchDone(true);
      // Search by the application's SF number
      const result = await searchLoanDiskLoan(appId, application.application_id);
      
      if (result.found && result.loans && result.loans.length > 0) {
        setLoandiskLoans(result.loans);
        setLoandiskSearchResult(result);
        
        // If exactly one loan found, auto-validate it
        if (result.loans.length === 1) {
          const loan = result.loans[0];
          setSelectedLoanForValidation(loan);
          // Auto-validate to get comparison with Monday data
          try {
            const validationResult = await validateLoanDiskLoan(appId, loan);
            setValidationResults(validationResult);
            
            // Check for discrepancies
            const discrepancies = validationResult.validation_results?.filter(r => !r.match) || [];
            setLoandiskDiscrepancies(discrepancies);
          } catch (e) {
            console.error("Auto-validation failed:", e);
          }
        }
      } else {
        setLoandiskSearchResult({ found: false });
      }
    } catch (error) {
      console.error("Auto-search LoanDisk failed:", error);
    }
  };

  const offerRefreshTried = useRef(false);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch portal settings (in parallel with app data)
      const [appData, settingsData] = await Promise.all([
        getApplication(appId),
        getPortalSettings().catch(() => ({
          consolidation_expiry_hours: 2
        }))
      ]);
      
      // Offer info (payment amount/frequency/term) lives on Monday — pull it
      // through once if it's missing so pre-funding & SO panels aren't blank
      let finalAppData = appData;
      if (appData?.monday_item_id && (!appData.payment_amount || !appData.payment_frequency) && !offerRefreshTried.current) {
        offerRefreshTried.current = true;
        try {
          await refreshMondayApplication(appId);
          finalAppData = await getApplication(appId);
        } catch {
          /* non-fatal — keep the original data */
        }
      }
      
      setApplication(finalAppData);
      setPortalSettings(settingsData);
      
      const benData = await getApplicationBeneficiary(appId);
      setBeneficiary(benData.beneficiary);
      
      // Load pre-funding questions based on deal type and consolidation status
      const dealType = finalAppData.is_topup ? "topup" : "new";
      const hasConsolidation = finalAppData.consolidation_lenders?.length > 0 || finalAppData.has_consolidation;
      try {
        const questions = await getPrefundingQuestions(dealType, hasConsolidation, false, appId);
        setPrefundingQuestions(questions);
      } catch (e) {
        console.error("Failed to load pre-funding questions:", e);
      }
      
      // Load disbursement calculation once the nominal payment is confirmed
      // (and Net in Hand for top ups)
      const shouldLoadDisbursement = appData.test_payment_confirmed && !appData.disbursement_confirmed &&
        (!appData.is_topup || appData.net_in_hand_confirmed);
        
      if (shouldLoadDisbursement) {
        try {
          const calcData = await calculateDisbursement(appId);
          setDisbursementCalc(calcData);
        } catch (e) {
          console.error("Failed to calculate disbursement:", e);
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load application data");
    } finally {
      setLoading(false);
    }
  };

  const getStageStatus = (stageKey) => {
    if (!application) return "pending";
    
    switch (stageKey) {
      case "contract":
        // Combined Contract & DocuSign stage
        if (application.docusign_status === "signed" || application.docusign_status === "completed") {
          return "completed";
        }
        if (application.docusign_status === "sent" || application.docusign_status === "viewed") {
          return "in_progress";
        }
        if (application.contract_generated) {
          return "in_progress";
        }
        return "pending";
      case "bank":
        // Bank verification (CoP)
        return application.bank_details_status === "verified" ? "completed" :
               application.bank_details_status === "pending_approval" ? "in_progress" : "pending";
      case "standing":
        return application.standing_order_initiated ? "completed" : "pending";
      case "loandisk":
        return application.loandisk_validated ? "completed" :
               application.loandisk_loan_id ? "in_progress" : "pending";
      case "netinhand":
        // Net in Hand stage (Top Ups only)
        return application.net_in_hand_confirmed ? "completed" :
               application.net_in_hand_approval_pending ? "in_progress" : "pending";
      case "nominal":
        if (application.test_payment_confirmed) return "completed";
        return application.test_payment_sent ? "in_progress" : "pending";
      case "prefunding":
        // Pre-Funding Call stage (graded pass/fail)
        if (application.prefunding_call_completed) return "completed";
        return application.prefunding_call_failed ? "failed" : "pending";
      case "disbursement":
        if (application.disbursement_confirmed) return "completed";
        return (application.test_payment_confirmed && application.prefunding_call_completed &&
                (!application.is_topup || application.net_in_hand_confirmed)) ? "in_progress" : "pending";
      case "tracking":
        if (application.disbursement_gc_status === "paid") return "completed";
        return application.disbursement_confirmed ? "in_progress" : "pending";
      default:
        return "pending";
    }
  };

  // Check if consolidation lenders are expired (using dynamic setting)
  const isConsolidationExpired = () => {
    if (!application?.is_consolidation || !application?.consolidation_lenders?.length) return false;
    if (!application?.consolidation_updated_at) return true; // No timestamp = expired
    
    const updatedAt = new Date(application.consolidation_updated_at);
    const now = new Date();
    const hoursElapsed = (now - updatedAt) / (1000 * 60 * 60);
    return hoursElapsed >= portalSettings.consolidation_expiry_hours;
  };

  // Check if all stages before disbursement are complete
  const canProceedToDisbursement = () => {
    if (!application) return false;
    
    // Check DocuSign
    const docusignComplete = application.docusign_status === "signed" || application.docusign_status === "completed";
    if (!docusignComplete) return false;
    
    // Check Bank Verification (CoP)
    if (application.bank_details_status !== "verified") return false;
    
    // Check Standing Order
    if (!application.standing_order_initiated) return false;
    
    // Check LoanDisk
    if (!application.loandisk_validated) return false;
    
    // For Top Ups: check Net in Hand
    if (application.is_topup && !application.net_in_hand_confirmed) return false;
    
    // Nominal (test) payment must be confirmed for EVERY deal
    if (!application.test_payment_confirmed) return false;
    
    // Pre-funding call must be completed & passed
    if (!application.prefunding_call_completed || application.prefunding_call_failed) return false;
    
    // Must be assigned to a funding specialist
    if (!application.assigned_to) return false;
    
    // Check Consolidation approval and expiry
    if (application.is_consolidation) {
      if (!application.consolidation_approved) return false;
      if (isConsolidationExpired()) return false;
    }
    
    return true;
  };

  // Net in Hand handlers (for Top Ups)
  const fetchAirtablePayments = async (existingSfNumber, existingCompanyName = null) => {
    try {
      // For the existing loan, we want to check if payments were received today
      // Pass the EXISTING loan's SF# and company name to search Airtable
      const result = await getTodaysAirtablePayments(appId, existingSfNumber, existingCompanyName);
      setAirtablePayments(result);
      if (result.found && result.total_amount > 0) {
        toast.info(`Found ${curSym}${result.total_amount.toLocaleString()} in today's Airtable payments`);
      }
    } catch (error) {
      console.error("Failed to fetch Airtable payments:", error);
      setAirtablePayments({ total_amount: 0, payments: [], found: false });
    }
  };

  const handleSyncFromMonday = async () => {
    try {
      setSyncingMonday(true);
      const result = await refreshMondayApplication(appId);
      const changes = result?.changes || {};
      const labels = {
        loan_amount: "Loan amount", payment_amount: "Payment amount",
        payment_frequency: "Payment frequency", loan_term: "Loan term",
        broker_name: "Broker", commission_percent: "Commission %",
      };
      const fmt = (f, v) => (f === "loan_amount" || f === "payment_amount")
        ? `${curSym}${Number(v || 0).toLocaleString()}` : String(v ?? "—");
      const parts = Object.entries(changes).map(
        ([f, c]) => `${labels[f] || f}: ${fmt(f, c.old)} → ${fmt(f, c.new)}`
      );
      if (parts.length) {
        toast.success(`Synced from Monday — ${parts.join(" · ")}`, { duration: 10000 });
      } else {
        toast.info("Synced from Monday — already up to date");
      }
      if ((result?.stages_reset || []).length) {
        toast.warning(
          `Re-confirm required: ${result.stages_reset.join(", ")} (confirmed against the old amount)`,
          { duration: 12000 }
        );
      }
      (result?.warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Monday sync failed");
    } finally {
      setSyncingMonday(false);
    }
  };

  const handleSearchExistingLoanBySF = async () => {
    if (!existingSFSearch.trim()) return;
    
    try {
      setExistingLoanSearching(true);
      const result = await getLoanDiskLoanBySF(existingSFSearch.trim(), isAus ? "AUS" : "UK");
      
      if (result.found && result.loan) {
        setExistingLoans([result.loan]);
        if (result.loan.is_restructured) {
          setSelectedExistingLoan(null);
          toast.error(
            result.warning ||
              "This loan was RESTRUCTURED — its balance is rolled into the replacement loan and can't be used as a top-up balance.",
            { duration: 9000 }
          );
        } else {
          setSelectedExistingLoan(result.loan);
          toast.success("Existing loan found");
          // Fetch Airtable payments for the existing loan SF# and company name
          const borrowerName = result.loan.borrower_business_name || result.loan.borrower_name;
          await fetchAirtablePayments(existingSFSearch.trim(), borrowerName);
        }
      } else {
        setSelectedExistingLoan(null);
        setExistingLoans([]);
        toast.info("No loan found with that SF number");
      }
    } catch (error) {
      toast.error("Failed to search for existing loan");
    } finally {
      setExistingLoanSearching(false);
    }
  };

  const handleSearchExistingLoanByName = async () => {
    try {
      setExistingLoanSearching(true);
      const companyName = application.company_name?.replace(" (TU)", "").replace("(TU)", "").trim();
      const result = await searchLoanDiskByBorrower(companyName, isAus ? "AUS" : "UK");
      
      if (result.loans && result.loans.length > 0) {
        setExistingLoans(result.loans);
        toast.success(`Found ${result.loans.length} live loan(s)`);
        if (result.restructured_excluded > 0) {
          toast.info(
            `${result.restructured_excluded} restructured loan(s) hidden — that balance is already rolled into the replacement loan`,
            { duration: 9000 }
          );
        }
        // Reset Airtable payments - will be fetched when user selects a loan
        setAirtablePayments({ total_amount: 0, payments: [], found: false });
      } else {
        setExistingLoans([]);
        if (result.restructured_excluded > 0) {
          toast.info(
            `No live loans — ${result.restructured_excluded} restructured loan(s) excluded (balance rolled into replacement loan)`,
            { duration: 9000 }
          );
        } else {
          toast.info("No live loans found for this borrower");
        }
      }
    } catch (error) {
      toast.error("Failed to search for existing loans");
    } finally {
      setExistingLoanSearching(false);
    }
  };

  const handleSelectExistingLoan = async (loan) => {
    if (loan.is_restructured) {
      toast.error(
        "This loan was RESTRUCTURED — its balance is rolled into the replacement loan and can't be used as a top-up balance.",
        { duration: 9000 }
      );
      return;
    }
    setSelectedExistingLoan(loan);
    // Fetch Airtable payments for this loan's SF# and company name
    const sfNumber = loan.loan_application_id || loan.loan_id;
    const borrowerName = loan.borrower_business_name || loan.borrower_name;
    if (sfNumber) {
      await fetchAirtablePayments(sfNumber, borrowerName);
    }
  };

  const handleConfirmNetInHand = async () => {
    if (!selectedExistingLoan) {
      toast.error("Please select an existing loan first");
      return;
    }
    
    // The server reads the loan by its application number; a loan without one has
    // nothing to be read by.
    if (!selectedExistingLoan.loan_application_id) {
      toast.error("This loan has no application (SF) number in LoanDisk, so its net in hand can't be confirmed");
      return;
    }

    const existingBalance = selectedExistingLoan.outstanding_balance_parsed || 
                           parseFloat(selectedExistingLoan.outstanding_balance || 0);
    
    try {
      setProcessing("net_in_hand_confirm");
      // The backend now calculates net_in_hand including Airtable payments
      const result = await confirmNetInHand(
        appId,
        selectedExistingLoan.loan_application_id
      );
      toast.success(`Net in Hand confirmed: ${curSym}${result.net_in_hand?.toLocaleString()}`);
      loadData();
    } catch (error) {
      // Name mismatch - need approval
      if (error.response?.data?.detail?.includes("mismatch")) {
        toast.error("Borrower name mismatch - requesting approval");
        const todaysPayment = airtablePayments.total_amount || 0;
        const calculatedNetInHand = application.loan_amount - existingBalance + todaysPayment;
        await handleRequestNetInHandApproval("Borrower name mismatch", existingBalance, calculatedNetInHand);
      } else {
        toast.error(error.response?.data?.detail || "Failed to confirm");
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleRequestNetInHandApproval = async (reason, existingBalance = 0, calculatedNetInHand = 0) => {
    try {
      setProcessing("net_in_hand_approval");
      await requestNetInHandApproval(appId, {
        existingSF: selectedExistingLoan?.loan_application_id || existingSFSearch,
        existingLoanFound: !!selectedExistingLoan,
        existingBalance: existingBalance || selectedExistingLoan?.outstanding_balance_parsed || 0,
        existingBorrower: selectedExistingLoan?.borrower_business_name,
        nameMismatch: reason.includes("mismatch"),
        calculatedNetInHand: calculatedNetInHand || (application.loan_amount - existingBalance),
        approvalReason: reason
      });
      toast.success("Approval request sent to Credit Manager");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request approval");
    } finally {
      setProcessing(null);
    }
  };

  // Confirmation of Payee (CoP) check handler
  const handleRunCoPCheck = async (manual = false) => {
    try {
      setProcessing("cop_check");
      
      // Who ran the check comes from the session; only the manual flag is sent.
      const result = await runCoPCheck(appId, { manual });
      
      if (result.match_status === "manual_verified") {
        toast.success("✓ Bank details marked verified (manual)");
      } else if (result.match_status === "full_match") {
        toast.success("✓ FULL MATCH - Bank details verified via Confirmation of Payee!");
      } else if (result.match_status === "name_divergence") {
        toast.warning("⚠ CoP matched at the bank, but the account name differs from the borrower — sent to Credit for review");
      } else if (result.match_status === "partial_match") {
        toast.warning(result.returned_name
          ? `⚠ CLOSE MATCH — the bank holds this account as "${result.returned_name}" — sent to Credit to confirm`
          : "⚠ PARTIAL MATCH - sent to Credit for review", { duration: 12000 });
      } else if (result.match_status === "no_match") {
        toast.error(result.returned_name
          ? `✗ NO MATCH — the bank holds this account as "${result.returned_name}". Verify and resubmit.`
          : "✗ NO MATCH - Bank details do not match. Please verify and resubmit.", { duration: 12000 });
      } else {
        toast.error(result.message || "CoP check error - sent to Credit for review");
      }
      
      loadData();
    } catch (error) {
      // A refusal (403 from the gate, 400 for missing bank details) carries its
      // own reason; showing it keeps a refusal from reading as a CoP result.
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Failed to run CoP check");
    } finally {
      setProcessing(null);
    }
  };

  const handleMockAcceptCop = async () => {
    try {
      setProcessing("cop_check");
      await mockAcceptCoP(appId);
      toast.success("✓ CoP accepted (sandbox mock) — bank details verified");
      loadData();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Mock CoP accept failed"));
    } finally {
      setProcessing(null);
    }
  };

  // ==================== TRUSTEE VERIFICATION (AUS trusts) ====================
  const [trusteeName, setTrusteeName] = useState("");
  const [trusteeAbn, setTrusteeAbn] = useState("");
  const [verifyingTrustee, setVerifyingTrustee] = useState(false);

  const handleVerifyTrustee = async () => {
    try {
      setVerifyingTrustee(true);
      const result = await setTrustee(appId, trusteeName.trim(), trusteeAbn.trim());
      if (result.verification_status === "verified") {
        toast.success(`✓ Trustee verified on ABR/ASIC: ${result.trustee_name}${result.trustee_abn ? ` (ABN ${result.trustee_abn})` : ""}`);
      } else {
        toast.warning(result.warnings?.[0] || "Trustee saved but could not be confirmed on ABR/ASIC");
      }
      setTrusteeName("");
      setTrusteeAbn("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify trustee");
    } finally {
      setVerifyingTrustee(false);
    }
  };

  // ==================== PDF DOWNLOAD HANDLER ====================
  
  const handleDownloadPdf = async () => {
    try {
      setProcessing("download_pdf");
      const blob = await downloadDisbursementPdf(appId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Disbursement_Summary_${application.sf_number || application.application_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download PDF");
    } finally {
      setProcessing(null);
    }
  };

  // ==================== CONSOLIDATION HANDLERS ====================
  
  const handleToggleConsolidation = async (checked) => {
    try {
      setProcessing("toggle_consolidation");
      await toggleConsolidation(appId, checked);
      toast.success(checked ? "Consolidation enabled" : "Consolidation disabled");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to toggle consolidation");
    } finally {
      setProcessing(null);
    }
  };

  const handleAddLender = async () => {
    if (!newLender.lender_name || !newLender.amount || !newLender.sort_code || 
        !newLender.account_number || !newLender.account_name) {
      toast.error("Please fill in all lender fields");
      return;
    }
    
    try {
      setAddingLender(true);
      await addConsolidationLender(appId, {
        lender_name: newLender.lender_name,
        amount: parseFloat(newLender.amount),
        sort_code: newLender.sort_code,
        account_number: newLender.account_number,
        account_name: newLender.account_name
      }, "user");
      toast.success(`Added ${newLender.lender_name}`);
      setNewLender({ lender_name: "", amount: "", sort_code: "", account_number: "", account_name: "" });
      loadData();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to add lender"));
    } finally {
      setAddingLender(false);
    }
  };

  const handleRemoveLender = async (lenderId, lenderName) => {
    try {
      setProcessing(`remove_lender_${lenderId}`);
      await removeConsolidationLender(appId, lenderId);
      toast.success(`Removed ${lenderName}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove lender");
    } finally {
      setProcessing(null);
    }
  };

  // ── Lender payouts via GC (upgrade item 3, Aug 2026) ──
  const refreshLenderPayout = (lenderId) =>
    api.post(`/applications/${appId}/consolidation-lenders/${lenderId}/payout-refresh`);

  const pollLenderPayout = async (lenderId, attempts = 30) => {
    for (let i = 0; i < attempts; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        // On the last poll ask the server to recover a send that never completed, so a row
        // does not stay "processing" for good. It leaves one that is still going out alone.
        if (i === attempts - 1) await refreshLenderPayout(lenderId);
        const { data } = await api.get(`/applications/${appId}`);
        const l = data?.consolidation_lenders?.find((x) => x.id === lenderId);
        if (l && l.payout_status !== "processing") {
          await loadData();
          if (l.payout_status === "paid") toast.success(`Lender paid via ${isAus ? "Capay" : "GoCardless"} (${l.payout_gc_payment_id || "ok"})`);
          else if (l.payout_status === "paused_insufficient_funds") toast.warning(l.payout_error || "Lender payout paused — insufficient funds in the payout account. Please request admin to add funds, then retry.", { duration: 12000 });
          else toast.error(`Lender payout failed: ${l.payout_error || "unknown error"}`);
          return;
        }
      } catch { /* keep polling */ }
    }
    loadData();
  };

  const handlePayLender = async (lender) => {
    const ok = window.confirm(
      `Pay ${curSym}${(lender.amount || 0).toLocaleString()} to ${lender.account_name || lender.lender_name} ` +
      `(••${lender.sort_code?.slice(-2)} / ••••${lender.account_number?.slice(-4)}) via ${isAus ? "Capay" : "GoCardless"}?\n\n` +
      `In live mode this sends REAL money.`);
    if (!ok) return;
    try {
      setProcessing(`pay_lender_${lender.id}`);
      await api.post(`/applications/${appId}/consolidation-lenders/${lender.id}/payout`);
      toast.info(`Payout queued — sending via ${isAus ? "Capay" : "GoCardless"}…`);
      await loadData();
      pollLenderPayout(lender.id);
    } catch (error) {
      const detail = errorDetailMessage(error, "Payout failed to start");
      if (detail.toLowerCase().includes("insufficient funds")) {
        toast.warning(detail, { duration: 12000 });
        loadData();
      } else {
        toast.error(detail);
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleRefreshLenderPayout = async (lender) => {
    try {
      setProcessing(`refresh_payout_${lender.id}`);
      const { data } = await refreshLenderPayout(lender.id);
      if (data?.recovered) toast.warning("The send never completed — you can retry the payout");
      else if (data?.message === "Send still in flight") toast.info(`Payout is still being sent via ${isAus ? "Capay" : "GoCardless"}`);
      else toast.success("Payout status updated");
      await loadData();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to check payout status"));
    } finally {
      setProcessing(null);
    }
  };

  const handleManualPayoutLender = async (lender) => {
    const note = window.prompt(
      `Mark ${lender.lender_name} ${curSym}${(lender.amount || 0).toLocaleString()} as paid OUTSIDE ${isAus ? "Capay" : "GoCardless"}?\n\nOptional note (reference / how it was paid):`, "");
    if (note === null) return;
    try {
      setProcessing(`manual_payout_${lender.id}`);
      await api.post(`/applications/${appId}/consolidation-lenders/${lender.id}/manual-payout`, { note });
      toast.success("Lender marked as paid manually");
      await loadData();
    } catch (error) {
      toast.error(errorDetailMessage(error, "Failed to mark as paid"));
    } finally {
      setProcessing(null);
    }
  };

  const handleResetConsolidationForReentry = async () => {
    try {
      setProcessing("reset_consolidation");
      // Reset consolidation approval status and update timestamp so lenders can be edited
      await resetConsolidationForReentry(appId);
      toast.success("Consolidation reset - please re-enter current balances");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset consolidation");
    } finally {
      setProcessing(null);
    }
  };

  const handleClearBankRejection = async () => {
    try {
      setProcessing("clear_bank_rejection");
      await clearBankRejection(appId);
      toast.success("Bank rejection cleared - you can now re-verify the details");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear bank rejection");
    } finally {
      setProcessing(null);
    }
  };

  const handleResetBankDetails = async () => {
    try {
      setProcessing("reset_bank_details");
      await resetBankDetails(appId);
      toast.success("Bank details reset - you can now submit new bank details");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset bank details");
    } finally {
      setProcessing(null);
    }
  };

  // Pre-Funding Call handlers (checkbox + typed answers)
  const handlePrefundingCheckboxChange = (questionId, checked) => {
    setPrefundingResponses(prev => ({
      ...prev,
      [questionId]: checked
    }));
  };

  const handlePrefundingValueChange = (questionId, value) => {
    setPrefundingResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleUploadRecording = async (file) => {
    try {
      setUploadingRecording(true);
      const result = await uploadPrefundingRecording(appId, file);
      setPrefundingRecording(result.filename);
      toast.success("Recording uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload recording");
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleCompletePrefundingCall = async () => {
    try {
      setProcessing("prefunding_call");
      
      const toNum = (v) => (v === "" || v == null ? null : parseFloat(v));
      const callData = {
        checklist_responses: prefundingResponses,
        notes: prefundingNotes || null,
        call_recording_filename: prefundingRecording || null,
        critical_info: {
          sort_code: prefundingCritical.sort_code,
          account_number: prefundingCritical.account_number,
          payment_frequency: prefundingCritical.payment_frequency,
          payment_amount: toNum(prefundingCritical.payment_amount),
          start_date: prefundingCritical.start_date || null,
          nominal_amount: toNum(prefundingCritical.nominal_amount),
        },
      };
      
      const res = await completePrefundingCall(appId, callData, user?.email || "user");
      if (res.grade === "fail") {
        toast.error(
          `Pre-funding call FAILED — ${(res.failed_items || []).join("; ")}`,
          { duration: 12000 }
        );
        setPrefundingDialogOpen(false);
        setPrefundingResponses({});
        setPrefundingNotes("");
        setPrefundingCritical({ ...EMPTY_CRITICAL });
        setPrefundingDraftInfo(null);
        loadData();
        return;
      }
      toast.success(res.test_payment_confirmed
        ? "Pre-funding call PASSED — nominal amount also confirmed!"
        : "Pre-funding call PASSED!");
      setPrefundingDialogOpen(false);
      setPrefundingDraftInfo(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete pre-funding call");
    } finally {
      setProcessing(null);
    }
  };

  // FULL redo after a failed call — previous answers are never carried over
  const handleRedoFailedCall = () => {
    setPrefundingResponses({});
    setPrefundingNotes("");
    setPrefundingCritical({ ...EMPTY_CRITICAL });
    setPrefundingDraftInfo(null);
    setPrefundingDialogOpen(true);
  };

  // ── Draft save / resume — call dropped mid pre-funding call ─────────────
  const handleSavePrefundingDraft = async (silent = false) => {
    const hasContent = Object.keys(prefundingResponses).length > 0 || prefundingNotes ||
      Object.values(prefundingCritical || {}).some((v) => v && String(v).trim() !== "");
    if (!hasContent || application?.prefunding_call_completed) return;
    try {
      const r = await savePrefundingDraft(appId, {
        checklist_responses: prefundingResponses,
        notes: prefundingNotes || "",
        critical_info: prefundingCritical,
      }, user?.email || "user");
      setApplication((prev) => prev ? { ...prev, prefunding_call_draft: {
        checklist_responses: prefundingResponses, notes: prefundingNotes || "",
        critical_info: prefundingCritical, saved_by: user?.email || "user", saved_at: r.saved_at,
      } } : prev);
      if (!silent) toast.success("Call progress saved — resume anytime from this stage");
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || "Failed to save the call draft");
    }
  };

  const openPrefundingDialog = () => {
    const draft = application?.prefunding_call_draft;
    const nothingLocal = Object.keys(prefundingResponses).length === 0 && !prefundingNotes &&
      !Object.values(prefundingCritical || {}).some((v) => v && String(v).trim() !== "");
    if (draft && !application?.prefunding_call_completed) {
      if (nothingLocal) {
        setPrefundingResponses(draft.checklist_responses || {});
        setPrefundingNotes(draft.notes || "");
        setPrefundingCritical({
          ...EMPTY_CRITICAL, ...(draft.critical_info || {}),
          account_number: draft.critical_info?.account_number || draft.critical_info?.account_last4 || "",
        });
      }
      setPrefundingDraftInfo({ saved_at: draft.saved_at, saved_by: draft.saved_by });
    } else {
      setPrefundingDraftInfo(null);
    }
    setPrefundingDialogOpen(true);
  };

  const handlePrefundingDialogChange = (v) => {
    if (!v && !application?.prefunding_call_completed) handleSavePrefundingDraft(true);
    setPrefundingDialogOpen(v);
  };

  // Attach the Aircall recording AFTER the call ended (you're on the line
  // when you click Complete, so the recording doesn't exist yet)
  const handleFetchAircallRecording = async () => {
    try {
      setProcessing("fetch_recording");
      const res = await fetchPrefundingAircallRecording(appId);
      if (res.status === "success") {
        toast.success(`Aircall recording attached${res.filename ? ` — ${res.filename}` : ""}`);
        loadData();
      } else {
        toast.warning(res.message || "No recording found yet — Aircall takes a few minutes to process it. Try again shortly.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch the Aircall recording");
    } finally {
      setProcessing(null);
    }
  };

  const handleResetPrefundingCall = async () => {
    try {
      setProcessing("reset_prefunding");
      await resetPrefundingCall(appId);
      setPrefundingResponses({});
      setPrefundingNotes("");
      setPrefundingRecording(null);
      toast.success("Pre-funding call reset - you can complete it again");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset pre-funding call");
    } finally {
      setProcessing(null);
    }
  };

  const handleVerifyLenderCoP = async (lenderId, lenderName) => {
    try {
      setVerifyingLenderCoP(lenderId);
      const result = await verifyLenderCoP(appId, lenderId);
      const bank = result.cop_returned_name ? ` — bank holds the account as "${result.cop_returned_name}"` : "";
      if (result.cop_status === "verified") {
        toast.success(`✓ Full Match - CoP verified for ${lenderName}`);
      } else if (result.cop_status === "partial_match") {
        toast.warning(`⚠ Close match for ${lenderName}${bank} — check before paying`, { duration: 12000 });
      } else if (result.cop_status === "no_match") {
        toast.error(`✗ No match for ${lenderName}${bank}`, { duration: 12000 });
      } else {
        toast.error(`CoP check error for ${lenderName}`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to verify CoP");
    } finally {
      setVerifyingLenderCoP(null);
    }
  };

  const handleRequestConsolidationApproval = async () => {
    try {
      setProcessing("consolidation_approval");
      await requestConsolidationApproval(appId);
      toast.success("Consolidation sent for credit approval");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request approval");
    } finally {
      setProcessing(null);
    }
  };

  // ==================== CONTRACT & DOCUSIGN HANDLERS (NEW FLOW) ====================
  
  const handleCheckMondayStatus = async () => {
    try {
      setCheckingMondayStatus(true);
      const result = await checkMondayStatus(appId);
      setMondayStatusResult(result);
      
      if (result.is_docs_out) {
        toast.success("Documents are out! Searching DocuSign...");
        // Auto-search DocuSign when docs are out
        handleSearchDocuSign();
      } else {
        toast.info(`Monday status: ${result.monday_status || 'Unknown'}`);
      }
      loadData();
    } catch (error) {
      toast.error("Failed to check Monday.com status");
    } finally {
      setCheckingMondayStatus(false);
    }
  };

  const handleSearchDocuSign = async () => {
    try {
      setSearchingDocuSign(true);
      const result = await searchDocuSignEnvelope(appId);
      setDocuSignEnvelope(result.envelope);
      
      if (result.status === "found") {
        toast.success("DocuSign envelope found!");
      } else if (result.mocked) {
        toast.info(result.message || "Using mock data");
      }
      loadData();
    } catch (error) {
      toast.error("Failed to search DocuSign");
    } finally {
      setSearchingDocuSign(false);
    }
  };

  const handleSimulateDocuSignProgress = async (newStatus) => {
    try {
      setProcessing("docusign_status");
      await updateDocuSignStatus(appId, newStatus);
      toast.success(`DocuSign status updated to: ${newStatus}`);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update status");
    } finally {
      setProcessing(null);
    }
  };

  const handleRefreshDocuSignStatus = async () => {
    try {
      setProcessing("docusign_refresh");
      const result = await refreshDocuSignStatus(appId);
      if (result.status === "mocked") {
        toast.info("This is a mock envelope - status must be updated manually");
      } else if (result.status === "relinked") {
        toast.success(result.message || "Wrong envelope unlinked — now linked to this deal's agreement");
        setDocuSignEnvelope(null);
      } else if (result.status === "unlinked") {
        toast.warning(result.message || "Wrong envelope unlinked — no agreement found for this deal yet");
        setDocuSignEnvelope(null);
      } else if (result.status_changed) {
        toast.success(`DocuSign status updated: ${result.previous_status} → ${result.current_status}`);
      } else {
        toast.info(`DocuSign status unchanged: ${result.current_status}`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh DocuSign status");
    } finally {
      setProcessing(null);
    }
  };

  const handleLinkDocuSignEnvelope = async () => {
    const envelopeId = prompt("Enter DocuSign Envelope ID:");
    if (!envelopeId || !envelopeId.trim()) return;
    
    try {
      setProcessing("docusign_link");
      const result = await linkDocuSignEnvelope(appId, envelopeId.trim());
      toast.success(`Envelope linked! Status: ${result.envelope_status_label}`);
      setDocuSignEnvelope({
        envelope_id: result.envelope_id,
        status: result.envelope_status,
        status_label: result.envelope_status_label,
        subject: result.subject,
        recipients: result.recipients
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link envelope");
    } finally {
      setProcessing(null);
    }
  };

  const handleViewSigningDetails = async () => {
    try {
      setLoadingSigningDetails(true);
      const details = await getDocuSignSigningDetails(appId);
      setSigningDetails(details);
      setSigningDetailsOpen(true);
      
      if (details.needs_review) {
        toast.warning(`⚠️ Fraud alert: ${details.fraud_flags.length} issue(s) detected - Review required!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch signing details");
    } finally {
      setLoadingSigningDetails(false);
    }
  };

  const handleOverrideRisk = async () => {
    const reason = prompt("Enter reason for overriding this risk (min 5 characters):");
    if (!reason || reason.trim().length < 5) {
      toast.error("Please provide a valid reason (minimum 5 characters)");
      return;
    }
    
    try {
      setLoadingSigningDetails(true);
      const result = await overrideDocuSignRisk(appId, reason.trim());
      toast.success(result.message);
      
      // Refresh signing details
      const details = await getDocuSignSigningDetails(appId);
      setSigningDetails(details);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to override risk");
    } finally {
      setLoadingSigningDetails(false);
    }
  };

  const handleUploadToLoanDisk = async () => {
    if (!application.loandisk_loan_id) {
      toast.error("No LoanDisk loan linked - please add the loan to LoanDisk first");
      return;
    }
    
    if (!application.loandisk_validated) {
      toast.error("LoanDisk loan must be validated before uploading documents");
      return;
    }
    
    if (!confirm("Upload DocuSign envelope documents to LoanDisk?")) return;
    
    try {
      setProcessing("upload_loandisk");
      const result = await uploadDocusignToLoanDisk(appId);
      
      if (result.status === "success") {
        toast.success("DocuSign documents uploaded to LoanDisk! May take up to 30 mins to appear.");
        loadData();
      } else {
        toast.error(result.message || "Upload failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload to LoanDisk");
    } finally {
      setProcessing(null);
    }
  };

  // Legacy handlers (kept for compatibility)
  const handleGenerateContract = async () => {
    try {
      setProcessing("contract");
      await generateContract(appId);
      toast.success("Contract generated successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate contract");
    } finally {
      setProcessing(null);
    }
  };

  const handleSendDocuSign = async () => {
    try {
      setProcessing("docusign");
      await sendToDocuSign(appId);
      toast.success("Sent to DocuSign (MOCKED)");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send to DocuSign");
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkSigned = async () => {
    try {
      setProcessing("docusign_sign");
      await markDocuSignSigned(appId);
      toast.success("DocuSign marked as signed");
      loadData();
    } catch (error) {
      toast.error("Failed to mark as signed");
    } finally {
      setProcessing(null);
    }
  };

  // Standing Order handlers
  const loadSoAttachments = async () => {
    try {
      const res = await listSoEmailAttachments(appId);
      setSoAttachments(res.attachments || []);
    } catch {
      setSoAttachments([]);
    }
  };

  const handleSoAttachmentUpload = async (files) => {
    const list = [...(files || [])];
    if (!list.length) return;
    setSoAttUploading(true);
    for (const f of list) {
      try {
        await uploadSoEmailAttachment(appId, f);
        toast.success(`${f.name} attached — it will go out with the SO email`);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setSoAttUploading(false);
    loadSoAttachments();
  };

  const handleSoAttachmentDelete = async (attId, name) => {
    try {
      await deleteSoEmailAttachment(appId, attId);
      toast.info(`${name} removed`);
      loadSoAttachments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove attachment");
    }
  };

  const handleSoAttachmentOpen = async (attId, name) => {
    try {
      await openStandingOrderAttachment(appId, attId, name);
    } catch {
      toast.error("Couldn't open the attachment");
    }
  };

  const handleShowStandingOrderPreview = async () => {
    try {
      setLoadingEmailPreview(true);
      const preview = await getStandingOrderEmailPreview(appId);
      setStandingOrderPreview(preview);
      setSoRecipient(preview.to_email || "");
      setSoCc(preview.contact_email ? [preview.contact_email] : []);
      setSoBcc([]);
      setSoStartDate(preview.first_payment_iso || "");
      setStandingOrderDialog(true);
      loadSoAttachments();
      if (portalUsers.length === 0) {
        getDirectory().then(setPortalUsers).catch(() => setPortalUsers([]));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load email preview");
    } finally {
      setLoadingEmailPreview(false);
    }
  };

  const handleSoStartDateChange = async (iso) => {
    setSoStartDate(iso);
    if (!iso) return;
    try {
      const preview = await getStandingOrderEmailPreview(appId, iso);
      setStandingOrderPreview(preview);
    } catch {
      /* keep the existing preview on failure */
    }
  };

  const handleInitiateStandingOrder = async (skipEmail = false) => {
    if (!skipEmail && (!soRecipient.trim() || !soRecipient.includes("@"))) {
      toast.error("Enter a valid recipient email (or use 'Mark as sent without emailing')");
      return;
    }
    try {
      setProcessing("standing");
      const result = await initiateStandingOrder(appId, {
        to_email: soRecipient.trim(),
        cc: soCc,
        bcc: soBcc,
        start_date: soStartDate,
        skip_email: skipEmail,
      });
      toast.success(skipEmail
        ? "Standing order marked as sent (no email) — handled externally"
        : `Standing order email ${result.resend ? "re-sent" : "sent"} to ${result.email_sent_to} from applications@`
          + (result.cc?.length ? ` (cc: ${result.cc.join(", ")})` : "")
          + (soBcc.length ? ` (bcc: ${soBcc.length} internal)` : ""));
      setStandingOrderDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate standing order");
    } finally {
      setProcessing(null);
    }
  };

  // LoanDisk handlers
  const handleSearchLoanDisk = async (customSearchTerm = null) => {
    try {
      setProcessing("loandisk_search");
      setValidationResults(null);
      setSelectedLoanForValidation(null);
      const result = await searchLoanDiskLoan(appId, customSearchTerm || searchTerm || null);
      setLoandiskSearchResult(result);
      setLoandiskLoans(result.loans || []);
      
      if (result.found && result.loans?.length > 0) {
        toast.success(`Found ${result.loans.length} loan(s) in LoanDisk - Click to validate`);
      } else {
        toast.info("No loans found in LoanDisk. Please ensure collections has added the loan.");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search LoanDisk");
    } finally {
      setProcessing(null);
    }
  };

  const handleSelectLoanForValidation = async (loan) => {
    try {
      setProcessing("loandisk_validate");
      setSelectedLoanForValidation(loan);
      const result = await validateLoanDiskLoan(appId, loan);
      setValidationResults(result);
    } catch (error) {
      toast.error("Failed to validate loan data");
    } finally {
      setProcessing(null);
    }
  };

  const handleConfirmValidation = async () => {
    if (!selectedLoanForValidation) {
      toast.error("No loan selected for validation");
      return;
    }
    try {
      setProcessing("loandisk_confirm");
      await confirmLoanDiskValidation(appId, selectedLoanForValidation);
      toast.success("Loan validated and linked successfully!");
      setLoandiskLoans([]);
      setLoandiskSearchResult(null);
      setValidationResults(null);
      setSelectedLoanForValidation(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm validation");
    } finally {
      setProcessing(null);
    }
  };

  const handleLinkLoan = async (loanId) => {
    try {
      setProcessing("loandisk_link");
      await linkLoanDiskLoan(appId, loanId);
      toast.success("Existing loan linked successfully");
      setSelectLoanDialog(false);
      setLoandiskLoans([]);
      setValidationResults(null);
      setSelectedLoanForValidation(null);
      loadData();
    } catch (error) {
      toast.error("Failed to link loan");
    } finally {
      setProcessing(null);
    }
  };

  const handleClearSearch = () => {
    setLoandiskLoans([]);
    setLoandiskSearchResult(null);
    setValidationResults(null);
    setSelectedLoanForValidation(null);
    setLoandiskDiscrepancies(null);
  };

  const handleRequestDiscrepancyReview = async (validationResultsData) => {
    try {
      setProcessing("loandisk_discrepancy");
      
      // Extract only the discrepancies (mismatches)
      const discrepancies = validationResultsData.filter(r => !r.match);
      
      // Create a discrepancy review request
      await api.post("/loandisk-discrepancy-requests", {
        application_id: application.id,
        sf_number: application.application_id,
        company_name: application.company_name,
        loandisk_loan_id: selectedLoanForValidation?.loan_id,
        discrepancies: discrepancies
      });
      
      toast.success("Discrepancy review request sent to Credit team");
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Failed to send discrepancy review request");
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkAsNewLoan = async () => {
    try {
      setProcessing("loandisk_new");
      await markAsNewLoan(appId);
      toast.success("Marked as new loan - ready to add to LoanDisk");
      loadData();
    } catch (error) {
      toast.error("Failed to mark as new loan");
    } finally {
      setProcessing(null);
    }
  };

  const RESET_ELEMENTS = [
    { key: "docusign", label: "Contract signing (DocuSign)", hint: "Clears envelope link, statuses, signers & fraud check so new docs can be issued and re-found",
      status: (a) => a.docusign_status && a.docusign_status !== "not_sent" ? `Status: ${a.docusign_status}` : null },
    { key: "fraud_check", label: "Fraud check & signer verification", hint: "Clears cached signers, risk level and any override — re-run from 'View Signing Details'",
      status: (a) => a.docusign_fraud_checked_at ? `Checked ${a.docusign_fraud_checked_at.slice(0, 10)}${a.docusign_signers?.length ? ` • Signers: ${a.docusign_signers.join(", ")}` : ""}` : null },
    { key: "company_verification", label: isAus ? "ASIC verification" : "Companies House verification",
      hint: isAus ? "Clears ASIC/ABR company data & directors so verification can be re-run" : "Clears CH company data & directors so verification can be re-run",
      status: (a) => a.ch_verification_status ? `Status: ${a.ch_verification_status}` : null },
    { key: "bank_verification", label: "Bank account verification (CoP)", hint: "Clears bank verification status — details must be resubmitted and re-verified",
      status: (a) => a.bank_details_status && a.bank_details_status !== "not_provided" ? `Status: ${a.bank_details_status}` : null },
    { key: "standing_order", label: "Standing order", hint: "Clears the standing-order initiated flag",
      status: (a) => a.standing_order_initiated ? `Initiated ${(a.standing_order_initiated_at || "").slice(0, 10)}` : null },
    { key: "loandisk", label: "LoanDisk borrower/loan validation", hint: "Unlinks the LoanDisk borrower/loan so it can be re-created or re-linked",
      status: (a) => a.loandisk_validated ? `Validated • Loan ID: ${a.loandisk_loan_id || "n/a"}` : (a.loandisk_loan_id ? `Loan ID: ${a.loandisk_loan_id}` : null) },
    { key: "test_payment", label: "Test payment", hint: "Cancels any pending GoCardless payment and clears sent/confirmed state so a fresh nominal payment can be issued",
      status: (a) => a.test_payment_confirmed ? "Confirmed" : (a.test_payment_sent ? `Sent — awaiting confirmation${a.test_payment_gc_status === "pending_approval" ? " (GC: pending approval)" : ""}` : null) },
    { key: "disbursement", label: "Disbursement approval & confirmation", hint: "Clears manager approval and marks the approval requests as reset. Not available once the deal is funded or being sent",
      status: (a) => a.disbursement_confirmed ? "Confirmed / funded" : (a.disbursement_manager_approved ? "Manager approved" : (a.disbursement_approval_pending ? "Approval pending" : null)) },
  ];

  // The two elements the server refuses on a funded deal (or without the disbursement
  // permission); the dialog withholds them rather than offering a reset that 409s or 403s.
  const MONEY_RESET_KEYS = ["test_payment", "disbursement"];
  const disbursementIsLocked = (a) =>
    !!a && (!!a.disbursement_confirmed || !!a.funded_at || !!a.disbursement_gc_payment_id ||
      ["sending", "skipped_not_configured", "send_unknown"].includes(a.disbursement_gc_send_status));

  const canResetElements = user?.role === "Admin" || user?.permissions?.includes("can_override_risk");
  const isAdminUser = user?.role === "Admin";
  const resetSelectedKeys = Object.keys(resetSelection).filter((k) => resetSelection[k]);

  const [directorOverrideOpen, setDirectorOverrideOpen] = useState(false);
  const [directorOverrideReason, setDirectorOverrideReason] = useState("");
  const [overridingDirector, setOverridingDirector] = useState(false);

  const handleOverrideDirectorMatch = async () => {
    if ((directorOverrideReason || "").trim().length < 5) {
      toast.error("Please give a short reason (who signed and why the name differs)");
      return;
    }
    try {
      setOverridingDirector(true);
      await overrideDirectorMatch(appId, directorOverrideReason.trim());
      toast.success("Director signature match overridden — payments unblocked");
      setDirectorOverrideOpen(false);
      setDirectorOverrideReason("");
      await loadData();
      try {
        const checklist = await getDisbursementChecklist(appId);
        setDisbursementChecklist(checklist);
      } catch { /* dialog may be closed */ }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Override failed");
    } finally {
      setOverridingDirector(false);
    }
  };

  const handleResetElements = async () => {
    if (!resetSelectedKeys.length) return;
    try {
      setProcessing("reset_elements");
      const res = await resetWorkflowElements(appId, resetSelectedKeys);
      toast.success(`Reset: ${res.reset.join(", ")}`);
      if (res.gc_cancel) {
        if (res.gc_cancel.ok) {
          toast.success(`Pending GoCardless payment ${res.gc_cancel.payment_id} cancelled`);
        } else {
          toast.warning(`GoCardless payment ${res.gc_cancel.payment_id} could NOT be cancelled automatically (${res.gc_cancel.error}) — cancel it in the GC dashboard before re-sending`, { duration: 15000 });
        }
      }
      setResetElementsDialog(false);
      setResetSelection({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset elements");
    } finally {
      setProcessing(null);
    }
  };

  // Dean (Aug 2026): only the assigned user (or an admin) can send the nominal
  const canSendNominal = isAdminUser || !application?.assigned_to ||
    [user?.email, user?.name].some(v => (v || "").trim().toLowerCase() === (application?.assigned_to || "").trim().toLowerCase());

  const handleSendTestPayment = async (resend = false) => {
    if (isAus) {
      const gate = await runGeoGate("nominal");
      if (!gate.allowed) { setGeoBlock({ flow: "nominal", geo: gate.geo }); return; }
    }
    try {
      setProcessing("test_payment");
      const result = await sendTestPayment(appId, (stage) => {
        // Progressive UI while the background job runs (typical LIVE flow:
        // pending → sending [GC customer + bank + create + approve] → succeeded/failed).
        if (stage === "sending") toast.loading(isAus ? "Contacting Capay…" : "Contacting GoCardless…", { id: "test-payment-progress" });
      }, resend);
      toast.dismiss("test-payment-progress");
      if (["pending_approval", "verifying"].includes(result.gc_status)) {
        toast.warning(`GoCardless: the payment is in status '${result.gc_status}' — approve it in the GC dashboard (Payments → Sent) or it will never reach the customer`, { duration: 15000 });
      }
      if (result.dm_sent) {
        toast.success(`Test payment sent (ref ${result.payment_id}) — amount hidden, DM'd to ${result.dm_recipient} on Slack`);
      } else {
        toast.success(`Test payment sent via ${isAus ? "Capay" : "GoCardless"} (ref ${result.payment_id}) — amount hidden`);
        if (result.warning) toast.warning(result.warning);
      }
      loadData();
    } catch (error) {
      toast.dismiss("test-payment-progress");
      const gate = settlementGateFromError(error);
      if (gate) { if (gate.code === "capay_va_required") setSettlementGate({ ...gate, flow: "nominal" }); toast.error(gate.message, { duration: 12000 }); return; }
      const detail = errorDetailText(error, "Failed to send test payment");
      toast.error(detail);
      // Stale page (someone else already sent / is sending) → pull fresh state
      if (error.response?.status === 409 || error.response?.status === 403) loadData();
      // Director name-format mismatch is admin-overridable — offer it right here
      if (typeof detail === "string" && detail.includes("Director signed documents") && isAdminUser) {
        setDirectorOverrideOpen(true);
        toast.info("The director signature check is blocking — as an admin you can override it in the dialog that just opened", { duration: 9000 });
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleConfirmTestPayment = async () => {
    try {
      setProcessing("confirm_test");
      await confirmTestPayment(appId, parseFloat(confirmedAmount));
      toast.success("Test payment confirmed — disbursement unlocked");
      setTestPaymentDialog(false);
      setConfirmedAmount("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Amount doesn't match. Please verify.");
      loadData();
    } finally {
      setProcessing(null);
    }
  };

  // Live rail status for the Send Nominal box (Dean, 4 Sep 2026) — shows WHY
  // a payment is held (e.g. genuine CoP no-match) with refresh + cancel.
  const refreshNominalStatus = async () => {
    try {
      setNominalStatusLoading(true);
      const r = await api.get(`/applications/${appId}/nominal-payment-status`);
      setNominalStatus(r.data);
    } catch {
      setNominalStatus(null);
    } finally {
      setNominalStatusLoading(false);
    }
  };

  useEffect(() => {
    if (application?.test_payment_sent && !application?.test_payment_confirmed) refreshNominalStatus();
    else setNominalStatus(null);
  }, [application?.test_payment_sent, application?.test_payment_id, application?.test_payment_confirmed]); // eslint-disable-line react-hooks/exhaustive-deps

  const nominalBadgeCls = (s) => {
    const v = (s || "").toLowerCase();
    if (["executed", "paid", "completed"].includes(v)) return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (["pending_approval", "verifying", "pending approval", "waiting funds"].includes(v)) return "bg-amber-100 text-amber-800 border-amber-300";
    if (["cancelled", "failed", "bounced", "rejected"].includes(v)) return "bg-red-100 text-red-700 border-red-200";
    return "bg-blue-100 text-blue-700 border-blue-200";
  };

  const handleCancelNominal = async () => {
    if (!window.confirm("Cancel this nominal payment at GoCardless? No money will move, and a fresh nominal can be re-sent.")) return;
    try {
      setProcessing("cancel_nominal");
      const res = await api.post(`/applications/${appId}/nominal-payment/cancel`);
      toast.success(`Nominal payment cancelled (status: ${res.data.status})`);
      await refreshNominalStatus();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
      refreshNominalStatus();
    } finally {
      setProcessing(null);
    }
  };

  const handleConfirmDisbursement = async () => {
    try {
      setProcessing("disbursement");
      await confirmDisbursement(appId, {
        disbursement_type: disbursementCalc?.disbursement_type || "new_loan",
        net_in_hand: disbursementCalc?.calculated_disbursement,
        final_amount: application.disbursement_approved_amount || disbursementCalc?.calculated_disbursement || application.loan_amount,
      });
      toast.success("Disbursement confirmed! Deal completed.");
      setDisbursementDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm disbursement");
    } finally {
      setProcessing(null);
    }
  };

  // Load disbursement checklist when dialog opens
  const loadDisbursementChecklist = async () => {
    try {
      const checklist = await getDisbursementChecklist(appId);
      setDisbursementChecklist(checklist);
    } catch (error) {
      toast.error("Failed to load checklist");
    }
  };

  // New formal disbursement handlers
  const handleRequestDisbursementApproval = async () => {
    try {
      setProcessing("request_disbursement_approval");
      const r = await requestDisbursementApproval(appId);
      if (r?.auto_approved) {
        toast.success(`Self-authorised (loan ${curSym}${r.loan_amount?.toLocaleString?.() || r.loan_amount} ≤ your ${curSym}${r.requester_disbursement_limit?.toLocaleString?.() || r.requester_disbursement_limit} limit) — skipping manager approval`);
        // Reload data first so `disbursement_manager_approved=true` is
        // reflected on the local `application` state, THEN advance to Step 3
        await loadData();
        setDisbursementStep(3);
      } else {
        toast.success("Disbursement approval request sent to Manager");
        loadData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to request approval";
      toast.error(detail);
      // Director name-format mismatch is admin-overridable — offer it right here
      if (typeof detail === "string" && detail.includes("director_signed") && isAdminUser) {
        setDirectorOverrideOpen(true);
        toast.info("The director signature check is blocking — as an admin you can override it in the dialog that just opened", { duration: 9000 });
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleVerifyPasswordForDisbursement = async () => {
    try {
      setProcessing("verify_password");
      await verifyPasswordForDisbursement(appId, passwordInput);
      setPasswordVerified(true);
      toast.success("Password verified");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid password");
    } finally {
      setProcessing(null);
    }
  };

  const handleExecuteDisbursement = async () => {
    const expectedTotal = disbursementCalc?.calculated_disbursement + 
      (application?.consolidation_lenders?.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0) || 0);
    
    const enteredAmount = parseFloat(confirmAmountInput);
    
    // Client-side tolerance tightened to £0.05 (was £1). Server enforces
    // the same rule with a hard 400 — this is just to give a friendlier
    // toast before the round-trip.
    if (Math.abs(enteredAmount - expectedTotal) > 0.05) {
      toast.error(`Amount mismatch! Expected ${curSym}${expectedTotal.toLocaleString()}, you entered ${curSym}${enteredAmount.toLocaleString()}`);
      return;
    }
    
    try {
      setProcessing("execute_disbursement");
      if (isAus) {
        const gate = await runGeoGate("disbursement");
        if (!gate.allowed) { setProcessing(null); setGeoBlock({ flow: "disbursement", geo: gate.geo }); return; }
      }
      const result = await executeDisbursement(appId, enteredAmount, payLendersNow && allLendersPayable);

      // NEW (Jul 24 2026 CF-520 fix): the endpoint no longer waits for
      // GoCardless. It queues the send + returns immediately with
      // `status="queued"`. We poll disbursement-status until the deal is
      // funded or the GC send fails.
      if (result.status === "already_disbursed") {
        toast.success("This deal is already disbursed.");
        setDisbursementDialog(false);
        loadData();
        return;
      }

      toast.info(isAus
        ? "Recording the disbursement — completing the deal…"
        : "Disbursement sent to GoCardless — waiting for confirmation…");

      const reference = result.reference;
      const deadline = Date.now() + 3 * 60 * 1000;  // 3-minute poll ceiling
      let final = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        let status;
        try {
          status = await getDisbursementStatus(appId);
        } catch (e) {
          continue;   // poll again — transient network blip
        }
        if (status.disbursement_confirmed) { final = { ok: true, status }; break; }
        if (status.gc_send_status === "failed") { final = { ok: false, status }; break; }
        if (status.gc_send_status === "send_unknown") { final = { ok: false, unknown: true, status }; break; }
        if (status.gc_send_status === "paused_insufficient_funds") { final = { ok: false, paused: true, status }; break; }
        // else keep polling
      }

      if (final && final.ok) {
        const lp = final.status.lender_payouts || [];
        toast.success(
          `Disbursement complete! Ref: ${reference}` +
          (final.status.gc_payment_id ? ` · GC ${final.status.gc_payment_id}` : "") +
          (lp.length ? ` · ${lp.length} lender payoff${lp.length > 1 ? "s" : ""} being sent via ${payoutRail} — the Consolidation panel updates as each one lands` : "")
        );
        setDisbursementDialog(false);
        loadData();
      } else if (final && !final.ok) {
        if (final.paused) {
          toast.warning(
            <span data-testid="disbursement-paused-toast">
              {final.status.gc_send_error ||
                "Disbursement paused — insufficient funds in the payout account. Please request admin to add funds, then retry."}
            </span>,
            { duration: 15000 }
          );
          setDisbursementDialog(false);
          loadData();
        } else {
          // Whether a retry is safe depends on the outcome, so the wording comes from one place.
          toast.error(disbursementFailureMessage(final.status));
          if (final.unknown || final.status.gc_payment_id) loadData();
        }
      } else {
        toast.warning(
          "Disbursement is still processing after 3 minutes — check the deal page in a moment. " +
          "It will finish in the background."
        );
        setDisbursementDialog(false);
        loadData();
      }
    } catch (error) {
      const gate = settlementGateFromError(error);
      if (gate) {
        if (gate.code === "capay_va_required") { setDisbursementDialog(false); setSettlementGate({ ...gate, flow: "disbursement" }); }
        toast.error(gate.message, { duration: 12000 });
        setProcessing(null);
        return;
      }
      const detail = errorDetailText(error, "Failed to execute disbursement");
      if (typeof detail === "string" && detail.toLowerCase().includes("insufficient funds")) {
        toast.warning(<span data-testid="disbursement-paused-toast">{detail}</span>, { duration: 15000 });
        setDisbursementDialog(false);
      } else {
        toast.error(detail);
      }
      loadData();
    } finally {
      setProcessing(null);
    }
  };

  // AUS — record a manually-sent bank transfer as the nominal (Capay mocked)
  const handleManualNominalSent = async () => {
    const amt = parseFloat(manualNominalAmount);
    if (!amt || amt <= 0 || amt > 10) {
      toast.error("Enter the amount sent (between A$0.01 and A$10.00)");
      return;
    }
    try {
      setProcessing("manual_nominal");
      await api.post(`/applications/${appId}/nominal-manual-sent`, {
        amount: amt,
        bank_reference: manualNominalRef?.trim() || null,
      });
      toast.success("Nominal recorded — the customer confirms the amount on the pre-funding call");
      setManualNominalDialog(false);
      setManualNominalAmount("");
      setManualNominalRef("");
      loadData();
    } catch (error) {
      const gate = settlementGateFromError(error);
      if (gate) { if (gate.code === "capay_va_required") { setManualNominalDialog(false); setSettlementGate({ ...gate, flow: "manual_nominal" }); } toast.error(gate.message, { duration: 12000 }); return; }
      toast.error(errorDetailText(error, "Failed to record the nominal"));
    } finally {
      setProcessing(null);
    }
  };

  // Open disbursement dialog and load checklist
  const openDisbursementDialog = async () => {
    setDisbursementDialog(true);
    setDisbursementStep(1);
    setPasswordInput("");
    setPasswordVerified(false);
    setConfirmAmountInput("");
    setAgreedToTerms(false);
    loadDisbursementChecklist();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Application Not Found</h2>
        <Button onClick={() => navigate("/credit-deals")} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Deals
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="workflow-checklist-page">
      {geoBlock && <GeoBlockDialog geo={geoBlock.geo} onClose={() => setGeoBlock(null)} onRetry={async () => { const r = await runGeoGate(geoBlock.flow); if (r.allowed) { setGeoBlock(null); toast.success("Location confirmed — you're in Australia. Click again to proceed."); } else setGeoBlock({ flow: geoBlock.flow, geo: r.geo }); }} />}
      <SettlementAccountDialog gate={settlementGate} user={user} applicationId={appId} onClose={() => setSettlementGate(null)}
        onCreated={(acc) => {
          setSettlementAcc(acc); setSettlementGate(null);
          toast.success(`Settlement account created — BSB ${acc?.virtual_account?.bsb} / ${acc?.virtual_account?.account_number}. ${settlementGate?.flow === "disbursement" ? "Click Disburse Now again." : settlementGate?.flow === "manual_nominal" ? "Record the nominal again." : settlementGate?.flow === "nominal" ? "Click Send via Capay again." : ""}`, { duration: 10000 });
        }} />
      <PageHeader
        title={application.company_name}
        description={[application.application_id, application.country, application.currency].filter(Boolean).join(" • ")}
        breadcrumb={
          <Link to="/credit-deals" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Deals
          </Link>
        }
      >
        {application.monday_item_id && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncFromMonday}
            disabled={syncingMonday}
            data-testid="sync-monday-btn"
            title="Offer changed on Monday? Re-pull the current offer amount + deal terms"
          >
            {syncingMonday ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sync Monday
          </Button>
        )}
        {canResetElements && !isFunded && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setResetSelection({}); setResetElementsDialog(true); }}
            data-testid="reset-elements-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset elements
          </Button>
        )}
        <CurrencyBadge currency={application.currency} amount={application.loan_amount} />
      </PageHeader>

      {/* Net-in-Hand Confirmed → Hand off to Legal (Onboarding Centre)
          Shows once Stage 5 is complete (or always for new-money deals)
          and the deal hasn't been funded yet, so the credit team gets a
          clean signal to pass the deal across. */}
      {!isFunded && (application.net_in_hand_confirmed || application.is_topup === false) && (
        <Alert className="mb-6 border-blue-300 bg-blue-50" data-testid="launch-onboarding-banner">
          <FileText className="h-5 w-5 text-blue-600" />
          <AlertTitle className="text-blue-900 flex items-center gap-2">
            Ready for Legal — Launch Onboarding Centre
            <InfoTip side="bottom">Credit checks complete. Hand this deal over to the legal team — the wizard will pre-fill loan amount, factor, term, fees, customer email and the offer-form PDF automatically from this deal.</InfoTip>
          </AlertTitle>
          <AlertDescription className="text-blue-800">
            <div className="mt-1">
              <Button
                size="sm"
                onClick={() => {
                  const dealId = application.application_id || application.sf_number;
                  if (isAus) {
                    setAusWizard({ deal: dealId, company: application.company_name || "" });
                  } else {
                    navigate(`/legal/onboarding-centre?deal=${encodeURIComponent(dealId)}`);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="launch-onboarding-from-workflow"
              >
                <FileText className="w-4 h-4 mr-1" />
                {isAus ? "Launch Onboarding Wizard" : "Launch Onboarding Centre"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Funded Banner */}
      {isFunded && (
        <Alert className="mb-6 border-emerald-300 bg-emerald-50">
          <BadgeCheck className="h-5 w-5 text-emerald-600" />
          <AlertTitle className="text-emerald-800 flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Deal Funded - Read Only Mode
            <InfoTip side="bottom">This deal has been successfully funded. All workflow stages are now locked and cannot be modified.</InfoTip>
          </AlertTitle>
          <AlertDescription className="text-emerald-700">
            <div className="mt-1 flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={processing === "download_pdf"}
                className="border-emerald-300 hover:bg-emerald-100"
                data-testid="download-pdf-btn"
              >
                {processing === "download_pdf" ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download Disbursement Summary PDF
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Progress Overview */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <WorkflowProgress application={application} labels={isAus ? { bank_verified: "Bank Details", standing_order: "Direct Debit" } : null} />
        </CardContent>
      </Card>

      {/* Consolidation Toggle & Section */}
      <Card className="mb-6 border-purple-200 bg-purple-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                id="is-consolidation"
                checked={application.is_consolidation || false}
                onCheckedChange={handleToggleConsolidation}
                disabled={isFunded || processing === "toggle_consolidation" || application.consolidation_approved}
                data-testid="consolidation-checkbox"
              />
              <Label htmlFor="is-consolidation" className="font-medium cursor-pointer">
                This is a debt consolidation / refinance deal
              </Label>
            </div>
            {application.is_consolidation && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                {application.consolidation_lenders?.length || 0} lender(s)
              </span>
            )}
          </div>
        </CardHeader>

        {application.is_consolidation && (
          <CardContent className="pt-0">
            {/* Consolidation Expiry Warning (using dynamic setting) */}
            {application.consolidation_lenders?.length > 0 && application.consolidation_updated_at && (() => {
              const updatedAt = new Date(application.consolidation_updated_at);
              const now = new Date();
              const hoursElapsed = (now - updatedAt) / (1000 * 60 * 60);
              const expiryHours = portalSettings.consolidation_expiry_hours;
              const isExpired = hoursElapsed >= expiryHours;
              
              if (isExpired) {
                return (
                  <Alert className="mb-4 border-red-300 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">Consolidation Details Expired</AlertTitle>
                    <AlertDescription className="text-red-700">
                      <p>Lender balances were entered over {expiryHours} hours ago and may have changed.</p>
                      <p className="text-xs mt-1 text-red-600">
                        Last updated: {updatedAt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                        onClick={handleResetConsolidationForReentry}
                        disabled={processing === "reset_consolidation"}
                        data-testid="reenter-balances-btn"
                      >
                        {processing === "reset_consolidation" ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Re-enter Balances
                      </Button>
                    </AlertDescription>
                  </Alert>
                );
              }
              return null;
            })()}

            {/* Consolidation Status */}
            {application.consolidation_approved && (
              <Alert className="mb-4 border-emerald-200 bg-emerald-50">
                <BadgeCheck className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-800">Consolidation Approved</AlertTitle>
                <AlertDescription className="text-emerald-700">
                  Credit has approved this consolidation package.
                </AlertDescription>
              </Alert>
            )}

            {application.consolidation_approval_pending && (
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <Clock className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">Pending Credit Approval</AlertTitle>
                <AlertDescription className="text-blue-700">
                  Consolidation is awaiting credit team approval.
                </AlertDescription>
              </Alert>
            )}

            {application.consolidation_rejected && (
              <Alert className="mb-4 border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">Consolidation Rejected</AlertTitle>
                <AlertDescription className="text-red-700">
                  {application.consolidation_rejection_reason || "No reason provided"}
                  <p className="text-xs mt-1">You can amend the lender details and resubmit.</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Existing Lenders */}
            {application.consolidation_lenders?.length > 0 && (
              <div className="space-y-3 mb-4">
                <Label className="text-sm font-medium">Lenders to Pay Off</Label>
                {application.consolidation_lenders.map((lender, idx) => (
                  <div key={lender.id} className="p-3 border rounded-lg bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{lender.lender_name}</span>
                          <span className="text-sm font-semibold text-red-600">
                            {curSym}{lender.amount?.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {lender.account_name} • ••{lender.sort_code?.slice(-2)} • ••••{lender.account_number?.slice(-4)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {lender.cop_status === "verified" ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded flex items-center gap-1" title={lender.cop_match_details}>
                            <BadgeCheck className="w-3 h-3" /> ✓ Full Match
                          </span>
                        ) : lender.cop_status === "partial_match" ? (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex flex-col items-start leading-tight" title={lender.cop_match_details} data-testid={`lender-cop-close-${idx}`}>
                            <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> ⚠ Close match</span>
                            {lender.cop_returned_name && (
                              <span className="font-mono text-[10px] font-semibold" data-testid={`lender-cop-bank-name-${idx}`}>bank: {lender.cop_returned_name}</span>
                            )}
                          </span>
                        ) : lender.cop_status === "no_match" ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded flex flex-col items-start leading-tight" title={lender.cop_match_details} data-testid={`lender-cop-nomatch-${idx}`}>
                            <span className="flex items-center gap-1"><XCircle className="w-3 h-3" /> ✗ No Match</span>
                            {lender.cop_returned_name && (
                              <span className="font-mono text-[10px] font-semibold" data-testid={`lender-cop-bank-name-${idx}`}>bank: {lender.cop_returned_name}</span>
                            )}
                          </span>
                        ) : lender.cop_status === "error" ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded flex items-center gap-1" title={lender.cop_match_details}>
                            <AlertCircle className="w-3 h-3" /> Error
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyLenderCoP(lender.id, lender.lender_name)}
                            disabled={verifyingLenderCoP === lender.id || application.consolidation_approved}
                            data-testid={`verify-cop-${idx}`}
                          >
                            {verifyingLenderCoP === lender.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Verify CoP"
                            )}
                          </Button>
                        )}
                        {!application.consolidation_approved && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveLender(lender.id, lender.lender_name)}
                            disabled={processing === `remove_lender_${lender.id}`}
                            data-testid={`remove-lender-${idx}`}
                          >
                            {processing === `remove_lender_${lender.id}` ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Lender payout via the region's payout rail — Capay (AUS) / GoCardless (UK); upgrade item 3, Aug 2026. Labels rail-aware since 12 Oct 2026 (Dean). */}
                    {(application.consolidation_approved || lender.payout_status) && (
                      <div className="mt-2 pt-2 border-t flex items-center justify-between gap-2 flex-wrap" data-testid={`lender-payout-row-${idx}`}>
                        {lender.payout_status === "paid" ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded flex items-center gap-1"
                            title={`${payoutRail} payment ${lender.payout_gc_payment_id || ""} (${lender.payout_gc_mode || ""})`} data-testid={`lender-paid-badge-${idx}`}>
                            <BadgeCheck className="w-3 h-3" /> Paid via {payoutRail}{lender.payout_gc_status ? ` · ${lender.payout_gc_status}` : ""}
                          </span>
                        ) : lender.payout_status === "paid_manually" ? (
                          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded" title={lender.payout_manual_note || ""}>
                            Paid manually{lender.payout_paid_by ? ` by ${lender.payout_paid_by}` : ""}
                          </span>
                        ) : lender.payout_status === "processing" ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> Paying via {payoutRail}…
                            </span>
                            {canDisburse && (
                              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                onClick={() => handleRefreshLenderPayout(lender)}
                                disabled={processing === `refresh_payout_${lender.id}`}
                                data-testid={`refresh-payout-${idx}`}>
                                {processing === `refresh_payout_${lender.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Check status"}
                              </Button>
                            )}
                          </div>
                        ) : lender.payout_status === "paused_insufficient_funds" ? (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded" title={lender.payout_error || ""} data-testid={`lender-payout-paused-${idx}`}>
                            ⏸ Paused — insufficient funds (request admin to add funds)
                          </span>
                        ) : lender.payout_status === "failed" ? (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded" title={lender.payout_error || ""}>
                            ✗ Payout failed
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not paid yet</span>
                        )}
                        {!["paid", "paid_manually", "processing"].includes(lender.payout_status || "") && (
                          <div className="flex items-center gap-1.5">
                            {canDisburse && (
                              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handlePayLender(lender)}
                                disabled={!["verified", "partial_match"].includes(lender.cop_status) || processing === `pay_lender_${lender.id}`}
                                title={!["verified", "partial_match"].includes(lender.cop_status) ? "Verify CoP first" : `Pay ${curSym}${lender.amount?.toLocaleString()} via ${isAus ? "Capay" : "GoCardless"}`}
                                data-testid={`pay-lender-gc-${idx}`}>
                                {processing === `pay_lender_${lender.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : (lender.payout_status === "failed" ? `Retry ${payoutRail} payout` : lender.payout_status === "paused_insufficient_funds" ? "Retry payout" : `Pay via ${payoutRail}`)}
                              </Button>
                            )}
                            {canDisburse && (
                              <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                onClick={() => handleManualPayoutLender(lender)}
                                disabled={processing === `manual_payout_${lender.id}`}
                                data-testid={`manual-payout-${idx}`}>
                                Mark paid manually
                              </Button>
                            )}
                            {noDisbursementNote}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add New Lender Form */}
            {!application.consolidation_approved && !isFunded && (
              <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
                <Label className="text-sm font-medium">Add Lender to Pay Off</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Lender Name</Label>
                    <Select
                      value={customLenderName ? "__other" : (newLender.lender_name || "")}
                      onValueChange={(v) => {
                        if (v === "__other") {
                          setCustomLenderName(true);
                          setNewLender({ ...newLender, lender_name: "" });
                        } else {
                          setCustomLenderName(false);
                          setNewLender({ ...newLender, lender_name: v });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="new-lender-name-select">
                        <SelectValue placeholder="Select from lender catalog..." />
                      </SelectTrigger>
                      <SelectContent>
                        {lenderCatalog.map((l) => (
                          <SelectItem key={l.id || l.name} value={l.name}>{l.name}</SelectItem>
                        ))}
                        <SelectItem value="__other">Other (type manually)…</SelectItem>
                      </SelectContent>
                    </Select>
                    {customLenderName && (
                      <Input
                        className="mt-2"
                        placeholder="e.g. ABC Finance"
                        value={newLender.lender_name}
                        onChange={(e) => setNewLender({...newLender, lender_name: e.target.value})}
                        data-testid="new-lender-name"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Amount ({curSym})</Label>
                    <Input
                      type="number"
                      placeholder="e.g. 3000"
                      value={newLender.amount}
                      onChange={(e) => setNewLender({...newLender, amount: e.target.value})}
                      data-testid="new-lender-amount"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">{isAus ? "BSB" : "Sort Code"}</Label>
                    <Input
                      placeholder="e.g. 123456"
                      value={newLender.sort_code}
                      onChange={(e) => setNewLender({...newLender, sort_code: e.target.value})}
                      data-testid="new-lender-sort"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Account Number</Label>
                    <Input
                      placeholder="e.g. 12345678"
                      value={newLender.account_number}
                      onChange={(e) => setNewLender({...newLender, account_number: e.target.value})}
                      data-testid="new-lender-account"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Account Name</Label>
                    <Input
                      placeholder="e.g. ABC Finance Ltd"
                      value={newLender.account_name}
                      onChange={(e) => setNewLender({...newLender, account_name: e.target.value})}
                      data-testid="new-lender-acc-name"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleAddLender}
                  disabled={addingLender}
                  variant="outline"
                  className="w-full"
                  data-testid="add-lender-btn"
                >
                  {addingLender ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Lender
                </Button>
              </div>
            )}

            {/* Net in Hand Calculation */}
            {application.consolidation_lenders?.length > 0 && (
              <div className="mt-4 p-4 border rounded-lg bg-white">
                <Label className="text-sm font-medium mb-3 block">Payment Breakdown</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Loan Amount:</span>
                    <span className="font-semibold">{curSym}{application.loan_amount?.toLocaleString()}</span>
                  </div>
                  {application.consolidation_lenders.map((lender) => (
                    <div key={lender.id} className="flex justify-between text-red-600">
                      <span>Less: {lender.lender_name}</span>
                      <span>-{curSym}{lender.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Net in Hand to Customer:</span>
                    <span className="text-emerald-600">
                      {curSym}{(application.loan_amount - (application.consolidation_total_amount || 0))?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Request Approval Button - also shows for resubmission after rejection */}
            {application.consolidation_lenders?.length > 0 && 
             !application.consolidation_approved && 
             (!application.consolidation_approval_pending || application.consolidation_rejected) && (
              <Button
                onClick={handleRequestConsolidationApproval}
                disabled={processing === "consolidation_approval"}
                className={`w-full mt-4 ${application.consolidation_rejected ? 'bg-amber-600 hover:bg-amber-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                data-testid="request-consolidation-approval"
              >
                {processing === "consolidation_approval" ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {application.consolidation_rejected 
                  ? "Resubmit Consolidation for Approval" 
                  : "Request Credit Approval for Consolidation"}
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Status Alerts */}
      {application.bank_details_status === "pending_approval" && (
        <Alert className="mb-6 border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Bank Details Pending Approval</AlertTitle>
          <AlertDescription className="text-amber-700">
            Bank details have been submitted and are awaiting credit team approval.
            <Link to="/credit-approvals" className="ml-2 underline">
              Go to Credit Approvals
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {application.bank_details_status === "rejected" && (
        <Alert className="mb-6 border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800">Bank Details Rejected</AlertTitle>
          <AlertDescription className="text-red-700">
            <p>{application.rejection_reason || "Bank details were rejected by the credit team."}</p>
            <p className="text-xs mt-2 text-red-600">You can submit new bank details or re-verify existing details below.</p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
                onClick={() => navigate(`/add-bank-details/${appId}`)}
                data-testid="submit-new-bank-details-btn"
              >
                Submit New Bank Details
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={handleClearBankRejection}
                disabled={processing === "clear_bank_rejection"}
                data-testid="retry-bank-verification-btn"
              >
                {processing === "clear_bank_rejection" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Re-verify Existing Details
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {application.disbursement_confirmed && (
        <Alert className="mb-6 border-emerald-200 bg-emerald-50">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-800">Deal Completed!</AlertTitle>
          <AlertDescription className="text-emerald-700">
            Disbursement of {application.currency === "GBP" ? "£" : "A$"}
            {application.disbursement_final_amount?.toLocaleString()} confirmed on{" "}
            {new Date(application.disbursement_confirmed_at).toLocaleDateString("en-GB")}
          </AlertDescription>
        </Alert>
      )}

      {/* Workflow Stages */}
      <div className="space-y-4">
        {stages.map((stage) => {
          const status = getStageStatus(stage.key);
          const Icon = stage.icon;
          const isTextIcon = typeof Icon === "string";
          
          return (
            <Card
              key={stage.id}
              className={status === "locked" ? "opacity-50" : ""}
              data-testid={`stage-${stage.key}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      status === "completed" ? "bg-primary text-primary-foreground" :
                      status === "in_progress" ? "bg-primary/20 text-primary" :
                      status === "failed" ? "bg-red-100 text-red-600" :
                      "bg-muted text-muted-foreground"
                    }`}>
                      {status === "completed" ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isTextIcon ? (
                        <span className="text-lg font-bold">{Icon}</span>
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-base">Stage {stage.id}: {stage.label}</CardTitle>
                      <CardDescription>
                        {status === "completed" && "Completed"}
                        {status === "in_progress" && "In Progress"}
                        {status === "pending" && "Ready to start"}
                        {status === "failed" && <span className="text-red-600 font-medium">Failed — remedy required, then redo the call</span>}
                        {status === "locked" && "Complete previous stage first"}
                      </CardDescription>
                    </div>
                  </div>
                  {status === "completed" && (
                    <StatusBadge status="completed" />
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Stage 2: Bank Verification (Confirmation of Payee) */}
                {stage.key === "bank" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    {/* Part A: Companies House Verification */}
                    <div className={`p-4 rounded-lg border-2 ${
                      chVerificationData?.verified
                        ? "bg-emerald-50 border-emerald-300"
                        : chVerificationData?.verification_status === "partial_match"
                          ? "bg-amber-50 border-amber-300"
                          : chVerificationData?.verification_status === "warning" || chVerificationData?.verification_status === "not_found"
                            ? "bg-red-50 border-red-300"
                            : "bg-blue-50 border-blue-300"
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          chVerificationData?.verified
                            ? "bg-emerald-500 text-white"
                            : chVerificationData?.verification_status === "partial_match"
                              ? "bg-amber-500 text-white"
                              : chVerificationData?.verification_status === "warning" || chVerificationData?.verification_status === "not_found"
                                ? "bg-red-500 text-white"
                                : "bg-blue-500 text-white"
                        }`}>
                          {chVerificationData?.verified ? "✓" : "A"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">Part A: Company Verification ({isAus ? "ABR / ASIC" : "Companies House"})</p>
                          <p className="text-xs text-slate-500">
                            {verifyingCompany
                              ? "Verifying company..."
                              : chVerificationData?.verified
                                ? "Company verified ✓"
                                : chVerificationData?.verification_status === "partial_match"
                                  ? "Company found - please confirm match"
                                  : chVerificationData?.verification_status === "not_found"
                                    ? "Company not found"
                                    : chVerificationData?.verification_status === "warning"
                                      ? "Verification has warnings"
                                      : isAus ? "Auto-verifies the business against ABR / ASIC" : "Auto-verifies company against UK Companies House"}
                          </p>
                        </div>
                      </div>

                      {verifyingCompany ? (
                        <div className="flex items-center gap-2 text-blue-700 py-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Verifying against {isAus ? "ABR / ASIC" : "Companies House"}...</span>
                        </div>
                      ) : chVerificationData ? (
                        <div className="space-y-3">
                          {/* Director/Signer Mismatch Warning - only show if DocuSign complete AND fraud check done */}
                          {application?.docusign_status === "completed" && 
                           application?.docusign_signers?.length > 0 &&
                           (chVerificationData.directors?.length > 0 || application?.ch_directors?.length > 0) && (() => {
                            const directors = chVerificationData.directors || application?.ch_directors || [];
                            const signerNames = application.docusign_signers || [];
                            const anyDirectorSigned = directors.some((director) => directorSignedBy(director.name, signerNames));
                            
                            if (!anyDirectorSigned) {
                              if (application.director_match_overridden) {
                                return (
                                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="director-match-overridden-note">
                                    <div className="flex items-start gap-2">
                                      <CheckCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="font-semibold text-amber-800">Director match overridden by admin</p>
                                        <p className="text-sm text-amber-700">
                                          {application.director_match_overridden_by} accepted the signature match
                                          {application.director_match_override_reason ? ` — "${application.director_match_override_reason}"` : ""}.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <p className="font-semibold text-red-800">Director Signature Warning</p>
                                      <p className="text-sm text-red-700">
                                        No registered {isAus ? "ASIC / ABR" : "Companies House"} {isAus ? "officeholder or sole trader" : "director"} appears in the DocuSign signers. 
                                        At least one signer should be {isAus ? "the registered officeholder / sole trader" : "a company director"}.
                                      </p>
                                      <p className="text-xs text-red-600 mt-1">
                                        Directors: {directors.map(d => d.name).join(", ")} | 
                                        Signers: {signerNames.join(", ")}
                                      </p>
                                      {isAdminUser && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="mt-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                                          onClick={() => setDirectorOverrideOpen(true)}
                                          data-testid="override-director-match-banner-btn"
                                        >
                                          Admin override — accept signature match
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Verification Result Details */}
                          <div className="bg-white/60 rounded-lg p-3 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm flex-1">
                                <div>
                                  <span className="text-muted-foreground">{isAus ? "Business Name (ABR/ASIC):" : "Company Name (CH):"}</span>
                                  <p className="font-medium">{chVerificationData.company_name_ch || "N/A"}</p>
                                  {isAus && (chVerificationData.is_sole_trader || application?.ch_legal_name) && (
                                    <p className="text-xs text-sky-700" data-testid="ch-sole-trader-line">Sole trader · {chVerificationData.legal_name || application?.ch_legal_name}{(chVerificationData.trading_name || application?.ch_trading_name) ? ` trading as ${chVerificationData.trading_name || application?.ch_trading_name}` : ""}</p>
                                  )}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">{isAus ? "ABN / ACN:" : "Company Number:"}</span>
                                  <p className="font-mono font-medium">{chVerificationData.company_number || "N/A"}</p>
                                </div>
                              </div>
                              {!isFunded && (chVerificationData.company_number || isAus) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs shrink-0 border-amber-400 text-amber-700 hover:bg-amber-50"
                                  onClick={() => { setCompanySearchResults(null); setShowCompanySearch(true); }}
                                  data-testid="ch-pick-different-company-btn"
                                  title="Auto-match picked the wrong Companies House record — search and select the correct company"
                                >
                                  Wrong company? Pick manually
                                </Button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                              <div>
                                <span className="text-muted-foreground">Status:</span>
                                <p className={`font-medium ${
                                  ["active", "registered"].includes(chVerificationData.company_status?.toLowerCase())
                                    ? "text-emerald-600" 
                                    : "text-red-600"
                                }`}>
                                  {chVerificationData.company_status?.toUpperCase() || "Unknown"}
                                </p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Type:</span>
                                <p className="font-medium">{chVerificationData.company_type || "N/A"}</p>
                              </div>
                              {chVerificationData.date_of_creation && (
                                <div>
                                  <span className="text-muted-foreground">Incorporated:</span>
                                  <p className="font-medium">{chVerificationData.date_of_creation}</p>
                                </div>
                              )}
                              {chVerificationData.has_insolvency_history && (
                                <div className="col-span-2">
                                  <div className="flex items-center gap-1 text-red-600">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="font-medium">Has Insolvency History</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            {chVerificationData.registered_address && (
                              <div className="text-sm pt-1 border-t border-slate-200">
                                <span className="text-muted-foreground">Registered Address:</span>
                                <p className="font-medium">{chVerificationData.registered_address}</p>
                              </div>
                            )}
                            
                            {/* Directors Section */}
                            {(chVerificationData.directors?.length > 0 || application?.ch_directors?.length > 0) && (
                              <div className="text-sm pt-2 border-t border-slate-200">
                                <span className="text-muted-foreground font-medium">Directors (for DocuSign cross-reference):</span>
                                <div className="mt-1 space-y-1">
                                  {(chVerificationData.directors || application?.ch_directors || []).map((director, idx) => {
                                    const signerNames = application?.docusign_signers || [];
                                    const isDocuSignSigner = directorSignedBy(director.name, signerNames);
                                    
                                    return (
                                      <div key={idx} className="flex items-center justify-between p-1.5 bg-slate-50 rounded">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{director.name}</span>
                                          <span className="text-xs text-muted-foreground">({director.role})</span>
                                        </div>
                                        {application?.docusign_status === "completed" && application?.docusign_signers?.length > 0 && (
                                          isDocuSignSigner ? (
                                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                              <CheckCircle className="w-3 h-3" /> Signed
                                            </span>
                                          ) : (
                                            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                                              Did not sign
                                            </span>
                                          )
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {/* AUS: officeholders unavailable from free ASIC data */}
                            {isAus && !(chVerificationData.directors?.length > 0 || application?.ch_directors?.length > 0) && (
                              <div className="text-xs text-slate-500 pt-2 border-t border-slate-200" data-testid="aus-officeholders-note">
                                Officeholders aren't available from the free ASIC data — signers are identity-verified via Sumsub KYC instead.
                              </div>
                            )}

                            {/* AUS: trust borrower → trustee capture + verification */}
                            {isAus && ((chVerificationData.company_type || application?.ch_company_type || "").toLowerCase().includes("trust")) && (
                              <div className="text-sm pt-2 border-t border-slate-200" data-testid="trustee-section">
                                <span className="text-muted-foreground font-medium">Trustee (from the trust deed):</span>
                                {application?.trustee_name ? (
                                  <div className="mt-1 flex items-center justify-between p-1.5 bg-slate-50 rounded">
                                    <div>
                                      <span className="font-medium">{application.trustee_name}</span>
                                      <span className="text-xs text-muted-foreground ml-2">
                                        · {application.trustee_entity_type || ""}{application.trustee_abn ? ` · ABN ${application.trustee_abn}` : ""}
                                      </span>
                                      {(application.trustee_warnings || []).map((w, i) => (
                                        <p key={i} className="text-xs text-amber-700">• {w}</p>
                                      ))}
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${application.trustee_verification_status === "verified" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="trustee-status-badge">
                                      {application.trustee_verification_status === "verified" ? "✓ Verified (ABR/ASIC)" : "⚠ Unconfirmed"}
                                    </span>
                                  </div>
                                ) : (
                                  <p className="text-xs text-amber-700 mt-1">
                                    No trustee recorded — payouts to a trustee-named account will stop at Credit review until the trustee is verified here.
                                  </p>
                                )}
                                <div className="flex gap-2 mt-2">
                                  <Input
                                    placeholder="Trustee name (company or individual)"
                                    value={trusteeName}
                                    onChange={(e) => setTrusteeName(e.target.value)}
                                    className="h-8 text-sm"
                                    data-testid="trustee-name-input"
                                  />
                                  <Input
                                    placeholder="ABN (optional)"
                                    value={trusteeAbn}
                                    onChange={(e) => setTrusteeAbn(e.target.value)}
                                    className="h-8 text-sm w-40"
                                    data-testid="trustee-abn-input"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleVerifyTrustee}
                                    disabled={verifyingTrustee || trusteeName.trim().length < 3}
                                    data-testid="verify-trustee-btn"
                                  >
                                    {verifyingTrustee && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                                    {verifyingTrustee ? "Checking ABR/ASIC…" : application?.trustee_name ? "Re-verify" : "Verify Trustee"}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Warnings */}
                          {chVerificationData.warnings?.length > 0 && (
                            <div className="space-y-1">
                              {chVerificationData.warnings.map((warning, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-amber-700 text-sm">
                                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                  <span>{warning}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Name not matching? Keep the original name — ask Credit to review */}
                          {!isAus && !chVerificationData.verified &&
                           chVerificationData.company_name_ch &&
                           chVerificationData.company_number &&
                           application?.company_name &&
                           chVerificationData.company_name_ch.toUpperCase().replace(/[^A-Z0-9]/g, "") !==
                             application.company_name.toUpperCase().replace(/[^A-Z0-9]/g, "") && (
                            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="company-rename-panel">
                              <p className="font-semibold text-amber-900 text-sm">Company name isn&apos;t quite matching</p>
                              <p className="text-xs text-amber-800 mt-1">
                                Companies House isn&apos;t getting a match on this name — CRN{" "}
                                <span className="font-mono">{chVerificationData.company_number}</span> is currently registered as{" "}
                                <b>&ldquo;{chVerificationData.company_name_ch}&rdquo;</b>. Companies do sometimes legally rename, so this
                                may well be fine. The application keeps its original name (in line with Monday) until Credit reviews it.
                              </p>
                              {application?.name_change_requested_at && (
                                <p className="text-[11px] text-amber-700 mt-1.5" data-testid="name-change-requested-note">
                                  ✓ Credit asked to review on {new Date(application.name_change_requested_at).toLocaleDateString("en-GB")} by {application.name_change_requested_by || "portal"}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Button
                                  size="sm"
                                  className="bg-amber-600 hover:bg-amber-700 text-white"
                                  onClick={handleRequestNameChange}
                                  disabled={requestingNameChange || verifyingCompany || isFunded}
                                  data-testid="request-name-change-btn"
                                >
                                  {requestingNameChange ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-1" />}
                                  {application?.name_change_requested_at ? "Ask Credit again" : "Request credit to change application name"}
                                </Button>
                                <span className="text-[11px] text-amber-700">…or use Send back on the Credit Approved Deals card.</span>
                              </div>
                            </div>
                          )}

                          {/* Multiple Matches - Show Selection */}
                          {chVerificationData.verification_status === "partial_match" && chVerificationData.all_matches?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm text-amber-700 font-medium">Select the correct company:</p>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {chVerificationData.all_matches.map((match, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-center justify-between p-2 bg-white rounded border border-amber-200 hover:border-amber-400 cursor-pointer"
                                    onClick={() => handleSelectCompanyMatch(match.company_number)}
                                    data-testid={`company-match-${idx}`}
                                  >
                                    <div className="text-sm">
                                      <p className="font-medium">{match.company_name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {match.company_number} • {match.company_status}
                                      </p>
                                    </div>
                                    <BadgeCheck className="w-5 h-5 text-amber-500" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Re-verify Button */}
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleManualVerifyCompany}
                              disabled={verifyingCompany || isFunded}
                              data-testid="reverify-company-btn"
                            >
                              {verifyingCompany ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                              Re-verify
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-blue-700">
                            Company verification runs automatically when you open the workflow.
                            Click below to manually trigger verification.
                          </p>
                          <Button
                            onClick={handleManualVerifyCompany}
                            disabled={verifyingCompany || isFunded}
                            variant="outline"
                            className="w-full"
                            data-testid="verify-company-btn"
                          >
                            {verifyingCompany ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Building2 className="w-4 h-4 mr-2" />
                            )}
                            Verify Company ({isAus ? "ABR / ASIC" : "Companies House"})
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Part B: Bank Verification (CoP) */}
                    <div className={`p-4 rounded-lg border-2 ${
                      application.bank_details_status === "verified"
                        ? "bg-emerald-50 border-emerald-300"
                        : application.bank_details_status === "pending_approval"
                          ? "bg-amber-50 border-amber-300"
                          : application.cop_check_failed
                            ? "bg-red-50 border-red-300"
                            : "bg-blue-50 border-blue-300"
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          application.bank_details_status === "verified"
                            ? "bg-emerald-500 text-white"
                            : application.bank_details_status === "pending_approval"
                              ? "bg-amber-500 text-white"
                              : application.cop_check_failed
                                ? "bg-red-500 text-white"
                                : "bg-blue-500 text-white"
                        }`}>
                          {application.bank_details_status === "verified" ? "✓" : "B"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">
                            Part B: Bank Account Verification (CoP)
                            {isAus && <span className="ml-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 align-middle">CAPAY CoP</span>}
                          </p>
                          <p className="text-xs text-slate-500">
                            {application.bank_details_status === "verified"
                              ? "Bank account verified ✓"
                              : application.bank_details_status === "pending_approval"
                                ? "Awaiting credit review"
                                : application.cop_check_failed
                                  ? "CoP check failed"
                                  : isAus ? "Verifies the BSB + account via Capay's Confirmation of Payee (manual fallback available)" : "Verifies bank account details via Confirmation of Payee"}
                          </p>
                        </div>
                      </div>

                    {application.bank_details_status === "verified" ? (
                      <div className="space-y-3">
                        {beneficiary && <BeneficiaryCard beneficiary={beneficiary} isAus={isAus} />}
                        <div className="p-3 bg-emerald-100 rounded-lg border border-emerald-300">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <p className="font-bold text-emerald-800">{application.cop_check_is_mock ? "✓ VERIFIED — SANDBOX MOCK (CoP accepted for testing)" : application.cop_check_provider === "manual" ? "✓ VERIFIED — Bank Details Confirmed (manual)" : "✓ FULL MATCH - Bank Account Verified"}</p>
                          </div>
                          {application.cop_check_reasons?.length > 0 && (
                            <div className="mt-2 text-sm text-emerald-700 space-y-1">
                              {application.cop_check_reasons.map((reason, idx) => (
                                <p key={idx}>• {reason}</p>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-emerald-600 mt-2">
                            Verified via {application.cop_check_provider === "manual" ? "manual check" : `${application.cop_check_provider === "capay" ? "Capay" : copProviderLabel(application.cop_check_provider)} CoP`} {application.cop_check_at ? `on ${new Date(application.cop_check_at).toLocaleDateString("en-GB")}` : ''}
                          </p>
                        </div>
                      </div>
                    ) : application.cop_check_status === "no_match" ? (
                      <div className="space-y-3">
                        {beneficiary && <BeneficiaryCard beneficiary={beneficiary} isAus={isAus} />}
                        <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                          <div className="flex items-center gap-2 mb-2">
                            <XCircle className="w-5 h-5 text-red-600" />
                            <p className="font-bold text-red-800">✗ NO MATCH - Bank Details Rejected</p>
                          </div>
                          {application.cop_check_reasons?.length > 0 && (
                            <div className="mt-2 text-sm text-red-700 space-y-1">
                              {application.cop_check_reasons.map((reason, idx) => (
                                <p key={idx}>• {reason}</p>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-red-700 mt-3 mb-3">
                            The bank account name does not match. Please verify the details and resubmit.
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={handleResetBankDetails}
                              disabled={processing === "reset_bank_details" || isFunded}
                              data-testid="reset-bank-details-btn"
                            >
                              {processing === "reset_bank_details" ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Reset & Resubmit Bank Details
                            </Button>
                            <Button variant="outline" size="sm" asChild>
                              <Link to="/approvals">Send to Credit Review</Link>
                            </Button>
                            {isAus && capayMode === "sandbox" && (
                              <Button size="sm" onClick={handleMockAcceptCop}
                                disabled={processing === "cop_check" || isFunded}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="mock-accept-cop-btn">
                                {processing === "cop_check" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                Accept CoP (Sandbox)
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : application.bank_details_status === "rejected" ? (
                      <div className="space-y-3">
                        {beneficiary && <BeneficiaryCard beneficiary={beneficiary} isAus={isAus} />}
                        <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                          <div className="flex items-center gap-2 mb-2">
                            <XCircle className="w-5 h-5 text-red-600" />
                            <p className="font-bold text-red-800">✗ REJECTED - Credit Team Rejected Bank Details</p>
                          </div>
                          {application.rejection_reason && (
                            <p className="text-sm text-red-700 mb-2">
                              <strong>Reason:</strong> {application.rejection_reason}
                            </p>
                          )}
                          <p className="text-sm text-red-700 mt-2 mb-3">
                            The Credit team has rejected these bank details. Please submit correct bank details.
                          </p>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={handleResetBankDetails}
                            disabled={processing === "reset_bank_details" || isFunded}
                            data-testid="reset-rejected-bank-btn"
                          >
                            {processing === "reset_bank_details" ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Reset & Resubmit Bank Details
                          </Button>
                        </div>
                      </div>
                    ) : application.bank_details_status === "pending_approval" ? (
                      <div className="space-y-3">
                        {beneficiary && <BeneficiaryCard beneficiary={beneficiary} isAus={isAus} />}
                        <div className="p-3 bg-amber-100 rounded-lg border border-amber-300">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <p className="font-bold text-amber-800">
                              {application.cop_check_status === "name_divergence"
                                ? "⚠ NAME DIVERGENCE - Credit Review Required"
                                : "⚠ CLOSE MATCH - Credit Review Required"}
                            </p>
                          </div>
                          {application.cop_check_returned_name && application.cop_check_status !== "name_divergence" && (
                            <div className="mb-2 p-2 rounded bg-white/70 border border-amber-200 text-sm" data-testid="cop-bank-registered-name">
                              <p className="text-amber-900">Bank holds this account as <span className="font-mono font-bold">{application.cop_check_returned_name}</span></p>
                              {application.cop_check_submitted_name && (
                                <p className="text-xs text-amber-700">You entered: <span className="font-mono">{application.cop_check_submitted_name}</span> — decide whether to continue or correct the details.</p>
                              )}
                            </div>
                          )}
                          {application.cop_check_reasons?.length > 0 && (
                            <div className="mt-2 text-sm text-amber-700 space-y-1">
                              {application.cop_check_reasons.map((reason, idx) => (
                                <p key={idx}>• {reason}</p>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-amber-700 mt-3 mb-3">
                            Bank details partially matched. Sent to Credit team for manual approval.
                          </p>
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" asChild>
                              <Link to="/approvals">View in Approvals</Link>
                            </Button>
                            {isAus && capayMode === "sandbox" && (
                              <Button size="sm" onClick={handleMockAcceptCop}
                                disabled={processing === "cop_check" || isFunded}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="mock-accept-cop-btn">
                                {processing === "cop_check" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                Accept CoP (Sandbox)
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={handleResetBankDetails}
                              disabled={processing === "reset_bank_details" || isFunded}
                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                            >
                              {processing === "reset_bank_details" ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Reset & Resubmit
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : application.cop_check_failed ? (
                      <div className="space-y-3">
                        {beneficiary && <BeneficiaryCard beneficiary={beneficiary} isAus={isAus} />}
                        <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <p className="font-bold text-red-800">✗ CoP Check Failed</p>
                          </div>
                          {application.cop_check_returned_name && (
                            <div className="mb-2 p-2 rounded bg-white/70 border border-red-200 text-sm" data-testid="cop-bank-registered-name">
                              <p className="text-red-900">Bank holds this account as <span className="font-mono font-bold">{application.cop_check_returned_name}</span></p>
                              {application.cop_check_submitted_name && (
                                <p className="text-xs text-red-700">You entered: <span className="font-mono">{application.cop_check_submitted_name}</span></p>
                              )}
                            </div>
                          )}
                          {application.cop_check_reasons?.length > 0 && (
                            <div className="mt-2 text-sm text-red-700 space-y-1">
                              {application.cop_check_reasons.map((reason, idx) => (
                                <p key={idx}>• {reason}</p>
                              ))}
                            </div>
                          )}
                          <p className="text-sm text-red-700 mt-3 mb-3">
                            The Confirmation of Payee check could not verify the bank details.
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              variant="destructive" 
                              size="sm" 
                              onClick={handleResetBankDetails}
                              disabled={processing === "reset_bank_details" || isFunded}
                              data-testid="reset-failed-bank-btn"
                            >
                              {processing === "reset_bank_details" ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Reset & Resubmit Bank Details
                            </Button>
                            <Button variant="outline" size="sm" asChild>
                              <Link to="/approvals">Send to Credit Review</Link>
                            </Button>
                            {isAus && capayMode === "sandbox" && (
                              <Button size="sm" onClick={handleMockAcceptCop}
                                disabled={processing === "cop_check" || isFunded}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="mock-accept-cop-btn">
                                {processing === "cop_check" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                                Accept CoP (Sandbox)
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* CoP Explanation */}
                        <div className="p-3 bg-white/60 rounded-lg">
                          <p className="text-sm text-blue-700">
                            {isAus
                              ? "Bank details are verified automatically via Capay's Confirmation of Payee — a full name match against the BSB + account verifies instantly, close matches go to Credit for review. Manual verification stays available as a fallback."
                              : "Bank details will be verified automatically via GoCardless' Confirmation of Payee service. If the account name matches, it's verified instantly. Partial matches go to Credit for review."}
                          </p>
                        </div>
                        
                        {/* Run CoP Check Button */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleRunCoPCheck()}
                            disabled={processing === "cop_check" || !beneficiary || isFunded}
                            data-testid="run-cop-check-btn"
                          >
                            {processing === "cop_check" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Building2 className="w-4 h-4 mr-2" />
                            {beneficiary ? "Run Confirmation of Payee Check" : "Add Bank Details First"}
                          </Button>
                          {isAus && beneficiary && (
                            <Button
                              variant="outline"
                              onClick={() => handleRunCoPCheck(true)}
                              disabled={processing === "cop_check" || isFunded}
                              data-testid="manual-verify-bank-btn"
                            >
                              Mark Verified Manually
                            </Button>
                          )}
                          {isAus && beneficiary && capayMode === "sandbox" && (
                            <Button
                              onClick={handleMockAcceptCop}
                              disabled={processing === "cop_check" || isFunded}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid="mock-accept-cop-btn"
                            >
                              {processing === "cop_check" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                              Accept CoP (Sandbox)
                            </Button>
                          )}
                        </div>
                        
                        {!beneficiary && (
                          <div className="flex flex-wrap gap-2 items-center">
                            <Button
                              variant="outline"
                              onClick={handlePullContractBankDetails}
                              disabled={pullingBank || isFunded}
                              data-testid="pull-contract-bank-btn"
                            >
                              {pullingBank ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                              Pull Bank Details from Contract
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => navigate(`/add-bank-details/${appId}`)}
                              disabled={isFunded}
                              data-testid="add-bank-details-btn"
                            >
                              <Building2 className="w-4 h-4 mr-2" />
                              Add Bank Details
                            </Button>
                            <p className="text-xs text-muted-foreground w-full">
                              &ldquo;Pull from Contract&rdquo; re-uses the bank details keyed into the contract wizard when the docs were raised — queues them for credit approval and runs the CoP name check.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                )}

                {/* Stage 1: Contract & DocuSign — Parts 1 & 2 side-by-side */}
                {stage.key === "contract" && status !== "locked" && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    {/* Part 1: Check Monday.com Status for "Doc's Out" */}
                    <div className={`p-4 rounded-lg border-2 ${
                      application.monday_status?.toLowerCase() === "doc's out"
                        ? "bg-emerald-50 border-emerald-300" 
                        : "bg-blue-50 border-blue-300"
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          application.monday_status?.toLowerCase() === "doc's out"
                            ? "bg-emerald-500 text-white" 
                            : "bg-blue-500 text-white"
                        }`}>
                          {application.monday_status?.toLowerCase() === "doc's out" ? "✓" : "1"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                            Part 1: Contract Drafted &amp; Issued
                            <InfoTip>Contracts are drafted in DocuPilot and issued via DocuSign by the legal team. This portal will detect when the Monday.com status changes to &quot;Doc&apos;s Out&quot;.</InfoTip>
                          </p>
                          <p className="text-xs text-slate-500">
                            {application.monday_status?.toLowerCase() === "doc's out"
                              ? "Documents have been issued by the legal team"
                              : `Waiting for legal team to issue documents (Current: ${application.monday_status || 'Unknown'})`}
                          </p>
                        </div>
                      </div>
                      
                      {application.monday_status?.toLowerCase() !== "doc's out" ? (
                        <div className="space-y-3">
                          {/* Contract wizard pops out right here — no page change */}
                          <Button
                            onClick={() => {
                              const dealId = application.application_id || application.sf_number;
                              if (isAus) {
                                setAusWizard({ deal: dealId, company: application.company_name || "" });
                              } else {
                                setWizardDealId(dealId);
                              }
                            }}
                            className="w-full bg-purple-600 hover:bg-purple-700"
                            data-testid="go-to-onboarding-centre-btn"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {isAus ? "Launch Onboarding Wizard" : "Launch Contract Wizard"}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                          
                          <Button
                            onClick={handleCheckMondayStatus}
                            disabled={checkingMondayStatus}
                            variant="outline"
                            className="w-full"
                            data-testid="check-monday-status-btn"
                          >
                            {checkingMondayStatus ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Check Monday.com Status
                          </Button>
                          {application.monday_status && (
                            <p className="text-center text-sm text-muted-foreground">
                              Current status: <span className="font-medium">{application.monday_status}</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-emerald-700">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Documents Issued</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCheckMondayStatus}
                            disabled={checkingMondayStatus}
                          >
                            {checkingMondayStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Part 2: DocuSign Tracking */}
                    <div className={`p-4 rounded-lg border-2 ${
                      application.monday_status?.toLowerCase() !== "doc's out"
                        ? "bg-slate-50 border-slate-200 opacity-60" 
                        : application.docusign_status === "signed" || application.docusign_status === "completed"
                          ? "bg-emerald-50 border-emerald-300"
                          : application.docusign_status === "sent" || application.docusign_status === "delivered" || application.docusign_status === "viewed"
                            ? "bg-amber-50 border-amber-300"
                            : "bg-blue-50 border-blue-300"
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          application.docusign_status === "signed" || application.docusign_status === "completed"
                            ? "bg-emerald-500 text-white"
                            : application.docusign_status && application.docusign_status !== "not_sent"
                              ? "bg-amber-500 text-white"
                              : application.monday_status?.toLowerCase() === "doc's out"
                                ? "bg-blue-500 text-white"
                                : "bg-slate-300 text-slate-500"
                        }`}>
                          {application.docusign_status === "signed" || application.docusign_status === "completed" ? "✓" : "2"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">Part 2: DocuSign Status</p>
                          <p className="text-xs text-slate-500">
                            {application.docusign_status === "signed" || application.docusign_status === "completed" ? "Contract signed by customer ✓" :
                             application.docusign_status === "viewed" ? "Customer viewing contract" :
                             application.docusign_status === "delivered" ? "Delivered to customer" :
                             application.docusign_status === "sent" ? "Sent, awaiting customer action" :
                             application.monday_status?.toLowerCase() === "doc's out" ? "Search DocuSign for the envelope" :
                             "Waiting for documents to be issued"}
                          </p>
                        </div>
                      </div>
                      
                      {/* Same-IP fraud alert — different signers, same IP address */}
                      {application?.docusign_same_ip_alert?.alert && (
                        <div className="mb-3 p-3 rounded-lg border-2 border-red-400 bg-red-50" data-testid="same-ip-fraud-alert">
                          <div className="flex items-start gap-2">
                            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-bold text-red-800">HIGH-RISK FRAUD ALERT — same IP address, different signers</p>
                              {(application.docusign_same_ip_alert.groups || []).map((g, gi) => (
                                <p key={gi} className="text-red-700 text-xs mt-1" data-testid={`same-ip-group-${gi}`}>
                                  <span className="font-semibold">{g.signers.join(", ")}</span> all signed from{" "}
                                  <span className="font-mono">{g.ip}</span>
                                </p>
                              ))}
                              <p className="text-[11px] text-red-600 mt-1.5">
                                Review the DocuSign certificate of completion before funding.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* DocuSign Progress Sub-steps with Timestamps */}
                      {application.monday_status?.toLowerCase() === "doc's out" && (
                        <div className="mb-4 space-y-2">
                          {/* Envelope ID Label */}
                          {application.docusign_envelope_id && (
                            <div className="px-2 py-1.5 bg-slate-100 rounded text-xs">
                              <span className="text-muted-foreground">Envelope ID: </span>
                              <span className="font-mono text-slate-700">{application.docusign_envelope_id}</span>
                            </div>
                          )}
                          
                          {/* Progress Steps with Timestamps */}
                          <div className="px-2 py-3 bg-white/50 rounded-lg">
                            <div className="flex items-start justify-between">
                              {/* Issued */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                  application.docusign_status && application.docusign_status !== "not_sent"
                                    ? "bg-emerald-500 text-white" : "bg-slate-200"
                                }`}>
                                  {application.docusign_status && application.docusign_status !== "not_sent" ? "✓" : "1"}
                                </div>
                                <p className="text-[10px] mt-1 font-medium">Issued</p>
                                {application.docusign_sent_at && (
                                  <p className="text-[9px] text-muted-foreground mt-0.5 text-center">
                                    {new Date(application.docusign_sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}<br/>
                                    {new Date(application.docusign_sent_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                              <div className={`h-0.5 flex-1 mx-1 mt-3 ${
                                ["delivered", "viewed", "signed", "completed"].includes(application.docusign_status)
                                  ? "bg-emerald-500" : "bg-slate-200"
                              }`} />
                              {/* Delivered/Viewed */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                  ["viewed", "signed", "completed"].includes(application.docusign_status)
                                    ? "bg-emerald-500 text-white" 
                                    : ["sent", "delivered"].includes(application.docusign_status) ? "bg-amber-400 text-white" : "bg-slate-200"
                                }`}>
                                  {["viewed", "signed", "completed"].includes(application.docusign_status) ? "✓" : "2"}
                                </div>
                                <p className="text-[10px] mt-1 font-medium">Viewed</p>
                                {application.docusign_viewed_at && (
                                  <p className="text-[9px] text-muted-foreground mt-0.5 text-center">
                                    {new Date(application.docusign_viewed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}<br/>
                                    {new Date(application.docusign_viewed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                              <div className={`h-0.5 flex-1 mx-1 mt-3 ${["signed", "completed"].includes(application.docusign_status) ? "bg-emerald-500" : "bg-slate-200"}`} />
                              {/* Signed */}
                              <div className="flex flex-col items-center flex-1">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                  ["signed", "completed"].includes(application.docusign_status) ? "bg-emerald-500 text-white" : "bg-slate-200"
                                }`}>
                                  {["signed", "completed"].includes(application.docusign_status) ? "✓" : "3"}
                                </div>
                                <p className="text-[10px] mt-1 font-medium">Completed</p>
                                {(application.docusign_last_signed_at || application.docusign_signed_at) && ["signed", "completed"].includes(application.docusign_status) && (
                                  <p className="text-[9px] text-muted-foreground mt-0.5 text-center">
                                    {new Date(application.docusign_last_signed_at || application.docusign_signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}<br/>
                                    {new Date(application.docusign_last_signed_at || application.docusign_signed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Signing Progress - Who needs to sign / Who has signed */}
                            {application.docusign_recipients?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-xs font-medium text-slate-600 mb-2">Signing Progress:</p>
                                <div className="space-y-1.5">
                                  {application.docusign_recipients
                                    .sort((a, b) => {
                                      // Sort by signed_at if both have it, pending ones at top
                                      if (!a.signed_at && b.signed_at) return -1;
                                      if (a.signed_at && !b.signed_at) return 1;
                                      if (a.signed_at && b.signed_at) return new Date(a.signed_at) - new Date(b.signed_at);
                                      return 0;
                                    })
                                    .map((recipient, idx) => {
                                      const isPending = !["completed", "signed"].includes(recipient.status);
                                      return (
                                        <div key={idx} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${
                                          isPending ? "bg-amber-50 border border-amber-200" : "bg-slate-50"
                                        }`}>
                                          <div className="flex items-center gap-2">
                                            {isPending ? (
                                              <Clock className="w-3 h-3 text-amber-500" />
                                            ) : (
                                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                                            )}
                                            <span className={isPending ? "font-medium text-amber-800" : "text-slate-600"}>
                                              {recipient.name || recipient.email}
                                            </span>
                                            <span className="text-slate-400">({recipient.role})</span>
                                          </div>
                                          <div className="text-right">
                                            {isPending ? (
                                              <span className="text-amber-600 font-medium">Awaiting signature</span>
                                            ) : recipient.signed_at ? (
                                              <span className="text-slate-500">
                                                {new Date(recipient.signed_at).toLocaleString('en-GB', { 
                                                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                                                })}
                                              </span>
                                            ) : (
                                              <span className="text-emerald-600">Signed</span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}
                            
                            {/* Pending Recipients Warning */}
                            {application.docusign_pending_recipients?.length > 0 && (
                              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs font-medium text-amber-800">Awaiting signatures from:</p>
                                    <p className="text-xs text-amber-700">
                                      {application.docusign_pending_recipients.map(r => r.name || r.email).join(", ")}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* DocuSign Actions */}
                      {application.monday_status?.toLowerCase() === "doc's out" && (
                        <>
                          {(!application.docusign_status || application.docusign_status === "not_sent") && (
                            <div className="space-y-3">
                              <Button
                                onClick={handleSearchDocuSign}
                                disabled={searchingDocuSign}
                                className="w-full"
                                style={{ backgroundColor: "#3B70E0" }}
                                data-testid="search-docusign-btn"
                              >
                                {searchingDocuSign ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <FileSignature className="w-4 h-4 mr-2" />
                                )}
                                Search DocuSign for Envelope
                              </Button>
                              <p className="text-xs text-center text-muted-foreground">
                                Searches by SF# or company name in envelope subject
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleLinkDocuSignEnvelope}
                                disabled={processing === "docusign_link"}
                                className="w-full text-xs"
                                data-testid="link-docusign-btn"
                              >
                                {processing === "docusign_link" ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : null}
                                Link Envelope by ID (Manual)
                              </Button>
                            </div>
                          )}
                          
                          {/* Envelope found, tracking status */}
                          {application.docusign_status && application.docusign_status !== "not_sent" && !["signed", "completed"].includes(application.docusign_status) && (
                            <div className="space-y-3">
                              <div className="p-3 bg-white/50 rounded-lg">
                                <p className="text-xs text-muted-foreground mb-2 truncate">
                                  Envelope: {application.docusign_envelope_id || "N/A"}
                                </p>
                                {application.docusign_subject && (
                                  <p className="text-xs text-slate-600 mb-1 truncate" title={application.docusign_subject} data-testid="docusign-envelope-subject">{application.docusign_subject}</p>
                                )}
                                <p className="text-sm font-medium">
                                  Status: <span className="text-amber-600 capitalize">{application.docusign_status}</span>
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  className="flex-1"
                                  onClick={handleRefreshDocuSignStatus}
                                  disabled={processing === "docusign_refresh"}
                                  data-testid="refresh-docusign-btn"
                                >
                                  {processing === "docusign_refresh" ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                  )}
                                  Refresh Status
                                </Button>
                                <Button
                                  variant="ghost"
                                  className="text-xs text-slate-600"
                                  onClick={handleSearchDocuSign}
                                  disabled={searchingDocuSign}
                                  title="Wrong envelope linked? Search DocuSign again by SF number / company"
                                  data-testid="research-docusign-btn"
                                >
                                  {searchingDocuSign ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                                  Wrong envelope? Search again
                                </Button>
                              </div>
                              {/* Mock simulation buttons - only show for mock envelopes */}
                              {canSimulateDocuSign && application.docusign_envelope_id?.startsWith("MOCK-") && (
                                <div className="pt-2 border-t">
                                  <p className="text-xs text-muted-foreground mb-2">Mock Testing:</p>
                                  <div className="flex gap-2">
                                    {application.docusign_status === "sent" && (
                                      <>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() => handleSimulateDocuSignProgress("viewed")}
                                          disabled={processing === "docusign_status"}
                                          data-testid="simulate-viewed-btn"
                                        >
                                          Simulate: Viewed
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                          onClick={() => handleSimulateDocuSignProgress("signed")}
                                          disabled={processing === "docusign_status"}
                                          data-testid="simulate-signed-btn"
                                        >
                                          Simulate: Signed
                                        </Button>
                                      </>
                                    )}
                                    {(application.docusign_status === "viewed" || application.docusign_status === "delivered") && (
                                      <Button
                                        onClick={() => handleSimulateDocuSignProgress("signed")}
                                        disabled={processing === "docusign_status"}
                                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                                        size="sm"
                                        data-testid="simulate-signed-btn"
                                      >
                                        {processing === "docusign_status" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        Simulate: Signed
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Signed state */}
                          {["signed", "completed"].includes(application.docusign_status) && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between p-3 bg-emerald-100 rounded-lg">
                                <div className="flex items-center gap-2 text-emerald-700">
                                  <CheckCircle className="w-5 h-5" />
                                  <div>
                                    <span className="font-medium">Contract Signed</span>
                                    {application.docusign_signed_at && (
                                      <p className="text-xs opacity-80">
                                        {new Date(application.docusign_signed_at).toLocaleString("en-GB")}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleViewSigningDetails}
                                    disabled={loadingSigningDetails}
                                    title="View signing details & fraud check"
                                  >
                                    {loadingSigningDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRefreshDocuSignStatus}
                                    disabled={processing === "docusign_refresh"}
                                    title="Refresh status from DocuSign"
                                  >
                                    {processing === "docusign_refresh" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Fraud alert banner */}
                              {application.docusign_fraud_flags?.length > 0 && (
                                <Alert className={`border-2 ${
                                  application.docusign_risk_level === "critical" ? "border-red-500 bg-red-50" :
                                  application.docusign_risk_level === "high" ? "border-orange-500 bg-orange-50" :
                                  "border-amber-500 bg-amber-50"
                                }`}>
                                  <ShieldAlert className={`h-4 w-4 ${
                                    application.docusign_risk_level === "critical" ? "text-red-600" :
                                    application.docusign_risk_level === "high" ? "text-orange-600" :
                                    "text-amber-600"
                                  }`} />
                                  <AlertTitle className={`${
                                    application.docusign_risk_level === "critical" ? "text-red-800" :
                                    application.docusign_risk_level === "high" ? "text-orange-800" :
                                    "text-amber-800"
                                  }`}>
                                    ⚠️ Fraud Alert - {application.docusign_risk_level?.toUpperCase()} Risk
                                  </AlertTitle>
                                  <AlertDescription className="text-sm">
                                    {application.docusign_fraud_flags.map((flag, idx) => (
                                      <p key={idx} className="mt-1">• {flag.message}</p>
                                    ))}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleViewSigningDetails}
                                      className="mt-2"
                                    >
                                      View Full Details
                                    </Button>
                                  </AlertDescription>
                                </Alert>
                              )}
                              
                              {/* View Details Button */}
                              {!application.docusign_envelope_id?.startsWith("MOCK-") && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleViewSigningDetails}
                                  disabled={loadingSigningDetails}
                                  className="w-full"
                                  data-testid="view-signing-details-btn"
                                >
                                  {loadingSigningDetails ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Eye className="w-4 h-4 mr-2" />
                                  )}
                                  View Signing Details & Fraud Check
                                </Button>
                              )}
                              
                              {/* Upload to LoanDisk Button - show when envelope complete & loan validated */}
                              {["signed", "completed"].includes(application.docusign_status) && 
                               application.loandisk_validated && 
                               !application.docusign_uploaded_to_loandisk && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={handleUploadToLoanDisk}
                                  disabled={processing === "upload_loandisk"}
                                  className="w-full bg-blue-600 hover:bg-blue-700"
                                  data-testid="upload-to-loandisk-btn"
                                >
                                  {processing === "upload_loandisk" ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  Upload Documents to LoanDisk
                                </Button>
                              )}
                              
                              {/* Already uploaded indicator */}
                              {application.docusign_uploaded_to_loandisk && (
                                <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded text-sm text-emerald-700">
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Documents uploaded to LoanDisk (may take up to 30 mins to appear)</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Stage 3: Collection Method — Standing Order (live) | DD | VRP (built, not in use) */}
                {stage.key === "standing" && status !== "locked" && isAus && (
                  <BecsMandatePanel
                    application={application}
                    onRefresh={loadData}
                    isFunded={isFunded}
                  />
                )}
                {stage.key === "standing" && status !== "locked" && !isAus && (
                  <Tabs defaultValue="standing_order" className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="standing_order" data-testid="collection-tab-so">Standing Order</TabsTrigger>
                      <TabsTrigger value="dd" data-testid="collection-tab-dd">Direct Debit</TabsTrigger>
                      <TabsTrigger value="vrp" data-testid="collection-tab-vrp">VRP</TabsTrigger>
                    </TabsList>
                    <TabsContent value="standing_order" className="mt-4">
                  <div className="space-y-4">
                    {application.standing_order_initiated ? (
                      <div className="p-4 bg-emerald-50 rounded-lg space-y-2" data-testid="so-sent-panel">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <p className="font-medium text-emerald-800">{application.standing_order_email_skipped ? "Standing Order marked as sent" : "Standing Order Email Sent"}</p>
                          </div>
                          {!isAus && (
                            <Button variant="outline" size="sm" onClick={handleShowStandingOrderPreview}
                              disabled={loadingEmailPreview || processing === "standing"} data-testid="so-resend-btn"
                              title="Re-send the standing order request (same preview — change the recipient, cc, start date or attachments first if needed)">
                              {loadingEmailPreview || processing === "standing" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                              Resend email
                            </Button>
                          )}
                        </div>
                        <p className="text-sm text-emerald-700">
                          Sent to: {application.standing_order_email_sent_to || "customer"}<br/>
                          {new Date(application.standing_order_initiated_at).toLocaleString("en-GB")}
                          {application.standing_order_resend_count > 0 && application.standing_order_last_sent_at && (
                            <span className="block text-xs text-emerald-600" data-testid="so-last-resent">
                              Re-sent {application.standing_order_resend_count}× — last {new Date(application.standing_order_last_sent_at).toLocaleString("en-GB")}{application.standing_order_last_sent_by ? ` by ${application.standing_order_last_sent_by}` : ""}
                            </span>
                          )}
                        </p>
                        <div className="text-sm text-muted-foreground pt-2 border-t">
                          <p>Payment: {curSym}{application.standing_order_payment_amount?.toFixed(2)} {application.standing_order_payment_frequency}</p>
                          <p>Starting: {ukDate(application.standing_order_first_payment_date)}</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Check prerequisites */}
                        {!["sent", "delivered", "viewed", "signed", "completed"].includes(application.docusign_status) && (
                          <Alert className="border-amber-200 bg-amber-50">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-700">
                              Documents must be issued via DocuSign before sending the standing order request (no need to wait for them to come back signed).
                            </AlertDescription>
                          </Alert>
                        )}
                        
                        {["sent", "delivered", "viewed", "signed", "completed"].includes(application.docusign_status) && application.bank_details_status !== "verified" && (
                          <Alert className="border-amber-200 bg-amber-50">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-700">
                              Bank details must be verified (CoP) before sending standing order request.
                            </AlertDescription>
                          </Alert>
                        )}
                        
                        {/* Show buttons only if prerequisites are met */}
                        {["sent", "delivered", "viewed", "signed", "completed"].includes(application.docusign_status) && application.bank_details_status === "verified" && (
                          <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                              Send the customer an email requesting they set up a standing order for their repayments.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleShowStandingOrderPreview}
                                disabled={loadingEmailPreview || processing === "standing"}
                                data-testid="initiate-standing-order-btn"
                              >
                                {loadingEmailPreview || processing === "standing" ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-2" />
                                )}
                                Send Standing Order Request
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <StandingOrderUpload application={application} onUploaded={loadData} />
                    </TabsContent>
                    <TabsContent value="dd" className="mt-4">
                      <GCCollectionPanel application={application} method="dd" onRefresh={loadData} />
                    </TabsContent>
                    <TabsContent value="vrp" className="mt-4">
                      <GCCollectionPanel application={application} method="vrp" onRefresh={loadData} />
                    </TabsContent>
                  </Tabs>
                )}

                {/* Stage 4: LoanDisk */}
                {stage.key === "loandisk" && status !== "locked" && (
                  <div className="space-y-4">
                    {/* Show Top Up or New App badge */}
                    <div className="flex items-center gap-2 mb-2">
                      {application.is_topup ? (
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800">
                          Top Up
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                          New Application
                        </span>
                      )}
                      {application.company_name_clean && (
                        <span className="text-sm text-muted-foreground">
                          {application.company_name_clean}
                        </span>
                      )}
                    </div>

                    {application.loandisk_validated ? (
                      /* Validated - show confirmed loan details with option to search again */
                      <div className="space-y-3">
                        <div className="p-4 bg-emerald-50 rounded-lg space-y-2">
                          <p className="font-medium text-emerald-800">LoanDisk Validated ✓</p>
                          <p className="text-sm text-emerald-700">
                            Loan ID: {application.loandisk_loan_id}
                          </p>
                          {application.loandisk_loan_data && (
                            <div className="text-sm text-emerald-700">
                              <p>Amount: {curSym}{parseFloat(application.loandisk_loan_data.loan_principal_amount || 0).toLocaleString()}</p>
                              <p>Term: {application.loandisk_loan_data.loan_duration} {application.loandisk_loan_data.loan_duration_period}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Search Again option */}
                        <div className="pt-3 border-t">
                          <p className="text-sm text-muted-foreground mb-2">Need to re-search or link a different loan?</p>
                          <div className="flex gap-2">
                            <Input
                              placeholder={isAus ? "Search by SFA number..." : "Search by SF number..."}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="flex-1"
                              data-testid="loandisk-revalidate-search-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => handleSearchLoanDisk()}
                              disabled={processing === "loandisk_search"}
                              data-testid="revalidate-search-btn"
                            >
                              {processing === "loandisk_search" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              <Search className="w-4 h-4 mr-2" />
                              Search Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : validationResults && selectedLoanForValidation ? (
                      /* Show Validation Results - Side by Side Comparison */
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="font-medium text-blue-800 mb-1">Loan Found - Verify Details</p>
                          <p className="text-sm text-blue-700">Compare LoanDisk data against Monday.com application</p>
                        </div>
                        
                        {/* Validation Table */}
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="px-4 py-2 text-left font-medium">Field</th>
                                <th className="px-4 py-2 text-left font-medium">LoanDisk Value</th>
                                <th className="px-4 py-2 text-left font-medium">Monday.com Value</th>
                                <th className="px-4 py-2 text-center font-medium">Match</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {validationResults.validation_results.map((result, idx) => (
                                <tr key={idx} className={result.critical && !result.match ? "bg-red-50" : ""}>
                                  <td className="px-4 py-3 font-medium">
                                    {result.field}
                                    {result.critical && <span className="text-red-500 ml-1">*</span>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="font-mono text-sm">{result.loan_value || "N/A"}</span>
                                    {result.error_message && (
                                      <p className="text-xs text-red-600 mt-1 font-normal">{result.error_message}</p>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 font-mono text-sm">{result.monday_value || "N/A"}</td>
                                  <td className="px-4 py-3 text-center">
                                    {result.match ? (
                                      <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                                    ) : result.is_stale ? (
                                      <Clock className="w-5 h-5 text-red-500 mx-auto" />
                                    ) : result.critical ? (
                                      <AlertCircle className="w-5 h-5 text-red-500 mx-auto" />
                                    ) : (
                                      <AlertCircle className="w-5 h-5 text-amber-500 mx-auto" />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <p className="text-xs text-muted-foreground">
                          <span className="text-red-500">*</span> Critical fields must match for validation
                        </p>
                        
                        {/* Validation Summary & Actions */}
                        <div className={`p-4 rounded-lg ${validationResults.all_valid ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {validationResults.all_valid ? (
                              <>
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                                <p className="font-medium text-emerald-800">All validations passed!</p>
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                <p className="font-medium text-red-800">Discrepancies found - Cannot confirm automatically</p>
                              </>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">
                            {validationResults.all_valid 
                              ? "Click 'Confirm Loan is on LoanDisk' to complete this step."
                              : "There are discrepancies between LoanDisk and Monday.com data. Request a manual review from Credit."}
                          </p>
                          
                          <div className="flex gap-3">
                            {validationResults.all_valid ? (
                              <Button
                                onClick={handleConfirmValidation}
                                disabled={processing === "loandisk_confirm"}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="confirm-validation-btn"
                              >
                                {processing === "loandisk_confirm" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm Loan is on LoanDisk
                              </Button>
                            ) : (
                              <Button
                                onClick={() => handleRequestDiscrepancyReview(validationResults.validation_results)}
                                disabled={processing === "loandisk_discrepancy"}
                                variant="destructive"
                                data-testid="request-discrepancy-review-btn"
                              >
                                {processing === "loandisk_discrepancy" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                <AlertCircle className="w-4 h-4 mr-2" />
                                Require Collections Manual Review
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              onClick={handleClearSearch}
                            >
                              Search Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : loandiskLoans.length > 0 ? (
                      /* Multiple loans found - select one */
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="font-medium text-blue-800 mb-2">
                            Found {loandiskLoans.length} loan(s) - Select one to validate
                          </p>
                        </div>
                        
                        <div className="space-y-3">
                          {loandiskLoans.map((loan, idx) => (
                            <div
                              key={loan.loan_id || loan.id || idx}
                              className="p-4 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                              onClick={() => handleSelectLoanForValidation(loan)}
                              data-testid={`loan-option-${idx}`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">{loan.borrower_business_name || loan.business_name || 'Unknown Borrower'}</p>
                                  <p className="text-sm text-muted-foreground">
                                    SF#: {loan.loan_application_id || 'N/A'}
                                    {loan.loan_id && ` • Loan ID: ${loan.loan_id}`}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    Released: {loan.loan_released_date || 'N/A'}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold">
                                    {curSym}{parseFloat(loan.loan_principal_amount || loan.loan_amount || 0).toLocaleString()}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {loan.loan_duration} {loan.loan_duration_period}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <Button variant="outline" onClick={handleClearSearch}>
                          Clear & Search Again
                        </Button>
                      </div>
                    ) : loandiskAutoSearchDone && loandiskSearchResult && !loandiskSearchResult.found ? (
                      /* Auto-search done, no loan found - show request flow */
                      <div className="space-y-4">
                        {/* Auto-search result - not found */}
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="font-medium text-amber-800 mb-2">
                            Loan Not Found in LoanDisk
                          </p>
                          <p className="text-sm text-amber-700">
                            We automatically searched for {application.application_id} but couldn&apos;t find it.
                            {" "}Add it directly below.
                          </p>
                        </div>
                        
                        {/* Direct creation wizard — gated on signed contracts / CoP / SO */}
                        <LoanGate application={application} onOpen={() => setLoanDiskWizardOpen(true)}
                          testId="add-loan-to-loandisk-btn" />
                        
                        {/* Re-Search button to re-run automatic search */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              setLoandiskAutoSearchDone(false);
                              setLoandiskSearchResult(null);
                              // Re-trigger the auto search
                              autoSearchLoanDisk();
                            }}
                            disabled={processing === "loandisk_search"}
                            data-testid="re-search-loandisk-btn"
                          >
                            {processing === "loandisk_search" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Re-Search LoanDisk
                          </Button>
                        </div>
                        
                        {/* Manual search option */}
                        <div className="pt-3 border-t">
                          <p className="text-sm text-muted-foreground mb-2">Or search manually:</p>
                          <div className="flex gap-2">
                            <Input
                              placeholder={isAus ? "Search by SFA number..." : "Search by SF number..."}
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="flex-1"
                              data-testid="loandisk-search-input"
                            />
                            <Button
                              onClick={() => handleSearchLoanDisk()}
                              disabled={processing === "loandisk_search"}
                              data-testid="search-loandisk-btn"
                            >
                              {processing === "loandisk_search" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              Search
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Initial state or loading - show search UI */
                      <div className="space-y-4">
                        {/* Show pending request status if exists */}
                        {application.loandisk_request_pending ? (
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="font-medium text-blue-800 mb-2">
                              Request Sent to Collections
                            </p>
                            <p className="text-sm text-blue-700">
                              Waiting for collections team to add {application.application_id} to LoanDisk. 
                            </p>
                          </div>
                        ) : !loandiskAutoSearchDone ? (
                          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <p className="text-sm text-slate-600">
                                Checking if loan exists in LoanDisk...
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
                            <p className="font-medium text-amber-800">
                              Loan Not Added Yet
                            </p>
                            <p className="text-sm text-amber-700">
                              The loan goes up together with the signed DocuSign contracts and the Standing Order —
                              borrower and loan details are autofilled from this deal.
                            </p>
                            <LoanGate application={application} onOpen={() => setLoanDiskWizardOpen(true)}
                              testId="add-loan-to-loandisk-btn-2" />
                          </div>
                        )}
                        
                        {/* Search input */}
                        <div className="flex gap-2">
                          <Input
                            placeholder={isAus ? "Search by SFA number (e.g. SFA-1234)..." : "Search by SF number (e.g. SF-12345)..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="flex-1"
                            data-testid="loandisk-search-input"
                          />
                          <Button
                            onClick={() => handleSearchLoanDisk()}
                            disabled={processing === "loandisk_search"}
                            data-testid="search-loandisk-btn"
                          >
                            {processing === "loandisk_search" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Search
                          </Button>
                        </div>
                        
                        {/* Quick search buttons */}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSearchLoanDisk(application.application_id)}
                            disabled={processing === "loandisk_search"}
                          >
                            Search &quot;{application.application_id}&quot;
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSearchLoanDisk(application.application_id?.replace("SF-", ""))}
                            disabled={processing === "loandisk_search"}
                          >
                            Search &quot;{application.application_id?.replace("SF-", "")}&quot;
                          </Button>
                        </div>
                        
                        <p className="text-xs text-muted-foreground">
                          Search by {isAus ? "SFA" : "SF"} number to find the loan in LoanDisk
                        </p>

                        {loandiskSearchResult && !loandiskSearchResult.found && (
                          <div className="p-3 bg-slate-100 rounded-lg">
                            <p className="text-sm text-slate-700">
                              No loan found for &quot;{loandiskSearchResult.search_term}&quot;. 
                              {" "}Use &apos;Add Loan to LoanDisk&apos; to create it directly.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Net in Hand Stage (Top Ups only) */}
                {stage.key === "netinhand" && status !== "locked" && (
                  <div className="space-y-4">
                    {/* Show locked state for funded deals */}
                    {application.net_in_hand_confirmed && isFunded ? (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-emerald-800">Net in Hand Confirmed ✓</p>
                          <div className="flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-600">
                            <Lock className="w-4 h-4" />
                            <span>Locked</span>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-emerald-700">
                          <p>Existing Loan: {application.existing_loan_sf}</p>
                          <div className="flex justify-between">
                            <span>New Loan Amount:</span>
                            <span className="font-mono">{curSym}{(application.loan_amount ?? 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span>Less: LoanDisk Balance:</span>
                            <span className="font-mono">-{curSym}{(application.existing_loan_outstanding ?? 0).toLocaleString()}</span>
                          </div>
                          {application.todays_airtable_payment > 0 && (
                            <div className="flex justify-between text-emerald-600">
                              <span>Plus: Today&apos;s Airtable Payment:</span>
                              <span className="font-mono">+{curSym}{(application.todays_airtable_payment ?? 0).toLocaleString()}</span>
                            </div>
                          )}
                          {/* Consolidation Lenders */}
                          {application.is_consolidation && application.consolidation_lenders?.length > 0 && (
                            <>
                              {application.consolidation_lenders.map((lender, idx) => (
                                <div key={idx} className="flex justify-between text-red-600">
                                  <span>Less: {lender.lender_name || lender.name}</span>
                                  <span className="font-mono">-{curSym}{parseFloat(lender.amount || 0).toLocaleString()}</span>
                                </div>
                              ))}
                            </>
                          )}
                          <div className="pt-2 border-t border-emerald-300 flex justify-between font-semibold">
                            <span>Net in Hand:</span>
                            <span className="font-mono">{curSym}{(application.confirmed_net_in_hand ?? 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ) : application.net_in_hand_confirmed && !netInHandExpired ? (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-emerald-800">Net in Hand Confirmed ✓</p>
                          {/* Timer display */}
                          {netInHandSecondsRemaining !== null && (
                            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-mono ${
                              netInHandSecondsRemaining < 120 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                            }`}>
                              <Clock className="w-4 h-4" />
                              <span>{formatTimeRemaining(netInHandSecondsRemaining)}</span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-emerald-700">
                          <p>Existing Loan: {application.existing_loan_sf}</p>
                          <div className="flex justify-between">
                            <span>New Loan Amount:</span>
                            <span className="font-mono">{curSym}{(application.loan_amount ?? 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span>Less: LoanDisk Balance:</span>
                            <span className="font-mono">-{curSym}{(application.existing_loan_outstanding ?? 0).toLocaleString()}</span>
                          </div>
                          {application.todays_airtable_payment > 0 && (
                            <div className="flex justify-between text-emerald-600">
                              <span>Plus: Today&apos;s Airtable Payment:</span>
                              <span className="font-mono">+{curSym}{(application.todays_airtable_payment ?? 0).toLocaleString()}</span>
                            </div>
                          )}
                          {/* Consolidation Lenders */}
                          {application.is_consolidation && application.consolidation_lenders?.length > 0 && (
                            <>
                              {application.consolidation_lenders.map((lender, idx) => (
                                <div key={idx} className="flex justify-between text-red-600">
                                  <span>Less: {lender.lender_name || lender.name}</span>
                                  <span className="font-mono">-{curSym}{parseFloat(lender.amount || 0).toLocaleString()}</span>
                                </div>
                              ))}
                            </>
                          )}
                          <div className="pt-2 border-t border-emerald-300 flex justify-between font-semibold">
                            <span>Net in Hand:</span>
                            <span className="font-mono">{curSym}{(application.confirmed_net_in_hand ?? 0).toLocaleString()}</span>
                          </div>
                        </div>
                        {/* Recalculate button - only show if not funded */}
                        <div className="mt-3 pt-3 border-t border-emerald-300">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleResetNetInHand}
                            disabled={processing === "reset_net_in_hand"}
                            className="w-full"
                          >
                            {processing === "reset_net_in_hand" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Recalculate Net in Hand
                          </Button>
                        </div>
                      </div>
                    ) : application.net_in_hand_confirmed && netInHandExpired ? (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-300">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                          <p className="font-medium text-amber-800">Net in Hand Expired</p>
                        </div>
                        <p className="text-sm text-amber-700 mb-3">
                          The validity period has passed. Please recalculate the Net in Hand before disbursement.
                        </p>
                        <div className="p-3 bg-amber-100 rounded mb-3">
                          <div className="text-sm text-amber-800">
                            <p className="mb-1"><strong>Previous calculation:</strong></p>
                            <p>Existing Loan: {application.existing_loan_sf}</p>
                            <p>Net in Hand: {curSym}{(application.confirmed_net_in_hand ?? 0).toLocaleString()}</p>
                          </div>
                        </div>
                        <Button
                          onClick={handleResetNetInHand}
                          disabled={processing === "reset_net_in_hand"}
                          className="w-full bg-amber-600 hover:bg-amber-700"
                        >
                          {processing === "reset_net_in_hand" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Recalculate Net in Hand
                        </Button>
                      </div>
                    ) : application.net_in_hand_approval_pending ? (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="font-medium text-blue-800">Pending Credit Manager Approval</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Waiting for approval in the Approvals queue.
                        </p>
                      </div>
                    ) : application.net_in_hand_rejected ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <p className="font-medium text-red-800">Rejected by Credit Manager</p>
                          <p className="text-sm text-red-700 mt-1">
                            {application.net_in_hand_rejection_reason || "No reason provided"}
                          </p>
                          <p className="text-xs text-red-600 mt-2">
                            You can search again and resubmit for approval.
                          </p>
                        </div>
                        
                        {/* Allow re-search after rejection */}
                        <div className="space-y-2">
                          <Label>Search Again</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter existing SF number (e.g. SF-12345)"
                              value={existingSFSearch}
                              onChange={(e) => setExistingSFSearch(e.target.value)}
                              className="flex-1"
                              data-testid="existing-sf-input-retry"
                            />
                            <Button
                              onClick={handleSearchExistingLoanBySF}
                              disabled={existingLoanSearching || !existingSFSearch.trim()}
                              data-testid="search-existing-sf-btn-retry"
                            >
                              {existingLoanSearching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              <Search className="w-4 h-4 mr-2" />
                              Search
                            </Button>
                          </div>
                        </div>
                        
                        <Button
                          variant="outline"
                          onClick={handleSearchExistingLoanByName}
                          disabled={existingLoanSearching}
                          className="w-full"
                          data-testid="search-by-name-btn-retry"
                        >
                          {existingLoanSearching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Search by Company Name ({application.company_name?.replace(" (TU)", "")})
                        </Button>
                        
                        {/* Show existing loans found after retry search */}
                        {existingLoans.length > 0 && (
                          <div className="space-y-3">
                            <Label>Select Existing Loan</Label>
                            {existingLoans.map((loan, idx) => {
                              const balance = loan.outstanding_balance_parsed || parseFloat(loan.outstanding_balance || 0);
                              const isSelected = selectedExistingLoan?.loan_id === loan.loan_id;
                              return (
                                <div
                                  key={loan.loan_id || idx}
                                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                    isSelected ? "border-primary bg-primary/5" : "hover:border-primary/50"
                                  }`}
                                  onClick={() => handleSelectExistingLoan(loan)}
                                  data-testid={`existing-loan-retry-${idx}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium">{loan.borrower_business_name || loan.borrower_name}</p>
                                      <p className="text-sm text-muted-foreground">
                                        SF#: {loan.loan_application_id || "N/A"} • Loan ID: {loan.loan_id}
                                      </p>
                                      {loan.is_restructured && (
                                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300" data-testid="restructured-badge-retry">
                                          RESTRUCTURED — balance rolled into replacement loan
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                                      <p className={`font-semibold ${loan.is_restructured ? "text-amber-700 line-through" : "text-red-600"}`}>{curSym}{balance.toLocaleString()}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Show selected loan and resubmit option */}
                        {selectedExistingLoan && (
                          <div className="p-4 border rounded-lg bg-slate-50">
                            <Label className="text-sm font-medium mb-2 block">Selected Loan</Label>
                            <p className="text-sm">{selectedExistingLoan.borrower_business_name}</p>
                            <p className="text-sm text-muted-foreground">Balance: {curSym}{(selectedExistingLoan.outstanding_balance_parsed || 0).toLocaleString()}</p>
                            <Button
                              onClick={() => handleRequestNetInHandApproval("Resubmission after rejection")}
                              disabled={processing === "net_in_hand_approval"}
                              className="w-full mt-3"
                              data-testid="resubmit-nih-approval"
                            >
                              {processing === "net_in_hand_approval" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              Resubmit for Approval
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="font-medium text-amber-800 mb-2">Find Existing Loan</p>
                          <p className="text-sm text-amber-700">
                            Search for the customer&apos;s existing loan to calculate the net in hand amount.
                          </p>
                        </div>
                        
                        {/* Search by SF Number */}
                        <div className="space-y-2">
                          <Label>Search by Existing SF Number</Label>
                          <div className="flex gap-2">
                            <Input
                              placeholder="Enter existing SF number (e.g. SF-12345)"
                              value={existingSFSearch}
                              onChange={(e) => setExistingSFSearch(e.target.value)}
                              className="flex-1"
                              data-testid="existing-sf-input"
                            />
                            <Button
                              onClick={handleSearchExistingLoanBySF}
                              disabled={existingLoanSearching || !existingSFSearch.trim()}
                              data-testid="search-existing-sf-btn"
                            >
                              {existingLoanSearching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              <Search className="w-4 h-4 mr-2" />
                              Search
                            </Button>
                          </div>
                        </div>
                        
                        {/* Or search by company name */}
                        <div className="text-center text-muted-foreground text-sm">or</div>
                        
                        <Button
                          variant="outline"
                          onClick={handleSearchExistingLoanByName}
                          disabled={existingLoanSearching}
                          className="w-full"
                          data-testid="search-by-name-btn"
                        >
                          {existingLoanSearching && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Search by Company Name ({application.company_name?.replace(" (TU)", "")})
                        </Button>
                        
                        {/* Show existing loans found */}
                        {existingLoans.length > 0 && (
                          <div className="space-y-3">
                            <Label>Select Existing Loan</Label>
                            {existingLoans.map((loan, idx) => {
                              const balance = loan.outstanding_balance_parsed || parseFloat(loan.outstanding_balance || 0);
                              const isSelected = selectedExistingLoan?.loan_id === loan.loan_id;
                              return (
                                <div
                                  key={loan.loan_id || idx}
                                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                    isSelected ? "border-primary bg-primary/5" : "hover:border-primary/50"
                                  }`}
                                  onClick={() => handleSelectExistingLoan(loan)}
                                  data-testid={`existing-loan-${idx}`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium">{loan.borrower_business_name || loan.borrower_name}</p>
                                      <p className="text-sm text-muted-foreground">
                                        SF#: {loan.loan_application_id || "N/A"} • Loan ID: {loan.loan_id}
                                      </p>
                                      {loan.is_restructured && (
                                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300" data-testid="restructured-badge">
                                          RESTRUCTURED — balance rolled into replacement loan
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                                      <p className={`font-semibold ${loan.is_restructured ? "text-amber-700 line-through" : "text-red-600"}`}>{curSym}{balance.toLocaleString()}</p>
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <div className="mt-3 pt-3 border-t">
                                      <CheckCircle className="w-4 h-4 text-primary inline mr-2" />
                                      <span className="text-sm text-primary font-medium">Selected</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Calculation Preview */}
                        {selectedExistingLoan && (
                          <div className="p-4 bg-slate-50 rounded-lg border space-y-2">
                            <p className="font-medium text-slate-700">Net in Hand Calculation</p>
                            <p className="text-xs text-slate-500 mb-2">
                              Formula: New Loan Amount − LoanDisk Balance + Today&apos;s Airtable Payment{application.is_consolidation ? " − Consolidation Payoffs" : ""} − Test Payment
                            </p>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>New Loan Amount:</span>
                                <span className="font-mono">{curSym}{application.loan_amount?.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between text-red-600">
                                <span>Less: LoanDisk Balance:</span>
                                <span className="font-mono">
                                  -{curSym}{(selectedExistingLoan.outstanding_balance_parsed || 
                                     parseFloat(selectedExistingLoan.outstanding_balance || 0)).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between text-emerald-600">
                                <span>Plus: Today&apos;s Airtable Payment:</span>
                                <span className="font-mono">
                                  +{curSym}{(airtablePayments.total_amount || 0).toLocaleString()}
                                </span>
                              </div>
                              {airtablePayments.found && airtablePayments.payments?.length > 0 && (
                                <div className="mt-2 p-2 bg-emerald-50 rounded text-xs">
                                  <p className="font-medium text-emerald-700 mb-1">Airtable Payments Found:</p>
                                  {airtablePayments.payments.map((p, i) => (
                                    <div key={i} className="flex justify-between text-emerald-600">
                                      <span>{p.description}</span>
                                      <span>{curSym}{p.amount?.toLocaleString()}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Consolidation Lenders Deduction */}
                              {application.is_consolidation && application.consolidation_lenders?.length > 0 && (
                                <>
                                  <div className="flex justify-between text-red-600 pt-1">
                                    <span>Less: Consolidation Payoffs:</span>
                                    <span className="font-mono">
                                      -{curSym}{(application.consolidation_lenders.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0)).toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="mt-1 p-2 bg-red-50 rounded text-xs">
                                    <p className="font-medium text-red-700 mb-1">Lenders to Pay Off:</p>
                                    {application.consolidation_lenders.map((lender, i) => (
                                      <div key={i} className="flex justify-between text-red-600">
                                        <span>{lender.name}</span>
                                        <span>{curSym}{parseFloat(lender.amount || 0).toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {/* Test Payment Line Item */}
                              <div className={`flex justify-between pt-1 ${application.test_payment_confirmed ? "text-red-600" : "text-amber-600"}`}>
                                <span>Less: Test Payment:</span>
                                <span className="font-mono">
                                  {application.test_payment_confirmed 
                                    ? `-${curSym}${(application.test_payment_confirmed_amount || application.test_payment_amount || 0).toLocaleString()}`
                                    : "(TBC at funding)"}
                                </span>
                              </div>
                              {!application.test_payment_confirmed && (
                                <p className="text-xs text-amber-600 italic">
                                  Test payment amount will be deducted once confirmed by customer
                                </p>
                              )}
                              <div className="pt-2 border-t flex justify-between font-semibold">
                                <span>Net in Hand {!application.test_payment_confirmed && "(Estimated)"}:</span>
                                <span className="font-mono text-emerald-700">
                                  {curSym}{(application.loan_amount - 
                                    (selectedExistingLoan.outstanding_balance_parsed || 
                                     parseFloat(selectedExistingLoan.outstanding_balance || 0)) +
                                    (airtablePayments.total_amount || 0) -
                                    (application.is_consolidation ? (application.consolidation_lenders?.reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0) || 0) : 0) -
                                    (application.test_payment_confirmed ? (application.test_payment_confirmed_amount || application.test_payment_amount || 0) : 0)
                                  ).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-3">
                          {/* Consolidation Expired Warning - blocks Net in Hand confirmation */}
                          {application.is_consolidation && isConsolidationExpired() && (
                            <Alert className="border-red-300 bg-red-50">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                              <AlertTitle className="text-red-800">Consolidation Balances Expired</AlertTitle>
                              <AlertDescription className="text-red-700 text-sm">
                                Cannot confirm Net in Hand - lender balances are over {portalSettings.consolidation_expiry_hours} hours old. 
                                Please re-enter the current balances before proceeding.
                              </AlertDescription>
                            </Alert>
                          )}
                          
                          <div className="flex gap-3">
                          {selectedExistingLoan ? (
                            <Button
                              onClick={handleConfirmNetInHand}
                              disabled={processing === "net_in_hand_confirm" || (application.is_consolidation && isConsolidationExpired())}
                              className="flex-1"
                              data-testid="confirm-net-in-hand-btn"
                            >
                              {processing === "net_in_hand_confirm" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Confirm Net in Hand
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => handleRequestNetInHandApproval("Existing loan not found in LoanDisk")}
                              disabled={processing === "net_in_hand_approval"}
                              className="flex-1"
                              data-testid="request-nih-approval-btn"
                            >
                              {processing === "net_in_hand_approval" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                              <AlertCircle className="w-4 h-4 mr-2" />
                              Can&apos;t Find Loan? Request Approval
                            </Button>
                          )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Pre-Funding Call Stage */}
                {/* Nominal (Test) Payment Stage — 5a/6a */}
                {stage.key === "nominal" && status !== "locked" && (
                  <div className="space-y-4">
                    {application.test_payment_confirmed ? (
                      <div className="p-4 bg-emerald-50 rounded-lg" data-testid="nominal-verified-banner">
                        <p className="font-medium text-emerald-800 flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Nominal Payment Verified ✓
                        </p>
                        <p className="text-sm text-emerald-700 mt-1">
                          Amount confirmed
                          {application.test_payment_confirmed_at && ` on ${new Date(application.test_payment_confirmed_at).toLocaleString("en-GB")}`}
                          {application.test_payment_id && ` · Ref: ${application.test_payment_id}`}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(() => {
                          const outstanding = [
                            !application.assigned_to && "Funding specialist not assigned",
                            !(application.docusign_status === "signed" || application.docusign_status === "completed") && "DocuSign contract not completed",
                            application.bank_details_status !== "verified" && (isAus ? "Bank details not verified" : "Bank details not verified (CoP)"),
                            !application.standing_order_initiated && (isAus ? "Direct Debit (BECS) mandate not active" : "Payment / standing order not initiated"),
                            !application.loandisk_validated && "LoanDisk not validated",
                            // Top-ups: Net in Hand must be confirmed BEFORE the
                            // nominal goes out (upgrade list item 6, Aug 2026)
                            application.is_topup && !application.net_in_hand_confirmed && "Net in Hand not confirmed",
                          ].filter(Boolean);
                          return outstanding.length > 0 ? (
                            <Alert className="border-amber-200 bg-amber-50" data-testid="nominal-outstanding-alert">
                              <AlertCircle className="h-4 w-4 text-amber-600" />
                              <AlertDescription className="text-amber-700">
                                The nominal payment unlocks once the earlier steps are complete. Still outstanding:
                                <ul className="mt-1 list-disc list-inside">
                                  {outstanding.map((o) => <li key={o}>{o}</li>)}
                                </ul>
                              </AlertDescription>
                            </Alert>
                          ) : null;
                        })()}

                        {!application.test_payment_sent ? (
                          <div className="space-y-2">
                            {canDisburse && (isAus ? (
                              <>
                                <div className={`flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${settlementAcc?.virtual_account?.account_number ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="settlement-account-status">
                                  <Landmark className="w-3.5 h-3.5" />
                                  {settlementAcc === undefined ? "Checking settlement account…"
                                    : settlementAcc?.virtual_account?.account_number
                                      ? <span>Settlement account ready — BSB <span className="font-mono">{settlementAcc.virtual_account.bsb}</span> / <span className="font-mono">{settlementAcc.virtual_account.account_number}</span>{settlementAcc.mode === "sandbox" ? " (sandbox)" : ""}</span>
                                      : <span>No Capay settlement account yet — it is created automatically when the nominal is sent (the send is refused if it can't be).</span>}
                                  {!settlementAcc?.virtual_account?.account_number && settlementAcc !== undefined && (
                                    <Button size="sm" variant="outline" className="h-6 px-2 text-xs ml-auto" onClick={() => openSettlementGate("manual")} data-testid="settlement-account-create-btn">Create now</Button>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    onClick={() => handleSendTestPayment(false)}
                                    disabled={processing === "test_payment" || !application.assigned_to || !canSendNominal}
                                    title={!canSendNominal ? `Only the assigned user (${application.assigned_to}) can send the nominal` : undefined}
                                    data-testid="capay-nominal-btn"
                                  >
                                    {processing === "test_payment" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                    <DollarSign className="w-4 h-4 mr-2" />
                                    Send via Capay
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => setManualNominalDialog(true)}
                                    disabled={!application.assigned_to}
                                    data-testid="manual-nominal-btn"
                                  >
                                    <Banknote className="w-4 h-4 mr-2" />
                                    Mark Nominal Sent (Manual Bank Transfer)
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <Button
                                  onClick={() => handleSendTestPayment(false)}
                                  disabled={processing === "test_payment" || !application.assigned_to || !canSendNominal}
                                  title={!canSendNominal ? `Only the assigned user (${application.assigned_to}) can send the nominal` : undefined}
                                  data-testid="send-test-payment-btn"
                                >
                                  {processing === "test_payment" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                  <DollarSign className="w-4 h-4 mr-2" />
                                  Send Nominal Payment
                                </Button>
                              </>
                            ))}
                            {noDisbursementNote}
                            {isAus ? (
                              <p className="text-xs text-muted-foreground">
                                Capay sends a small hidden amount to the customer's verified account (sandbox/live per
                                Portal Settings → Capay). If Capay is unavailable, transfer it from the bank and record it
                                manually instead. The customer confirms the amount on the pre-funding call.
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                A small hidden amount is sent to the customer's verified account.
                                The pre-funding call stage unlocks once it has been sent.
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="p-4 bg-blue-50 rounded-lg">
                              <p className="font-medium text-blue-800">Nominal Payment Sent — Amount Hidden</p>
                              <p className="text-sm text-blue-700">
                                Ref: {application.test_payment_id}{application.test_payment_sandbox && " (sandbox)"}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {isAus
                                  ? "The amount is hidden — sent via Capay (DM'd to the verifier) or recorded from a manual bank transfer. The customer confirms it during the pre-funding call, or if they email it in, enter it here to confirm. Disbursement stays locked until it's confirmed."
                                  : "The amount was DM'd to the verifier on Slack — you can't see it here. The customer can confirm it during the pre-funding call, or if they email it in, enter it here to confirm. Disbursement stays locked until it's confirmed."}
                              </p>
                              {!nominalStatus && ["pending_approval", "verifying", "PENDING APPROVAL"].includes(application.test_payment_gc_status) && !application.test_payment_sandbox && (
                                <p className="text-xs mt-2 font-medium text-amber-700" data-testid="test-payment-pending-approval">
                                  {isAus
                                    ? "Capay status: PENDING APPROVAL — the money has NOT left Capay yet. Approve it in the Capay client portal, or use Reset workflow elements → Test payment to re-send fresh."
                                    : `GoCardless status: ${application.test_payment_gc_status === "verifying" ? "VERIFYING" : "PENDING APPROVAL"} — the money has NOT left GoCardless yet. Approve it in the GC dashboard (Payments → Sent), or use Reset workflow elements → Test payment to cancel it and re-send fresh.`}
                                </p>
                              )}
                              {(application.test_payment_attempts || 0) > 0 && (
                                <p className={`text-xs mt-2 font-medium ${application.test_payment_attempts >= 3 ? "text-red-600" : "text-amber-600"}`}
                                  data-testid="test-payment-attempts">
                                  {application.test_payment_attempts >= 3
                                    ? "3 incorrect attempts — re-send a new test payment to continue"
                                    : `${application.test_payment_attempts} incorrect attempt${application.test_payment_attempts !== 1 ? "s" : ""} — ${3 - application.test_payment_attempts} left`}
                                </p>
                              )}
                              {/* Live rail status — what GC/Capay says right now */}
                              <div className="mt-3 p-2.5 bg-white/80 border border-blue-200 rounded" data-testid="nominal-live-status">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <span className="text-xs font-semibold text-gray-700">Payment status</span>
                                  <span className="flex items-center gap-2">
                                    {nominalStatus?.status && (
                                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded border ${nominalBadgeCls(nominalStatus.status)}`} data-testid="nominal-status-badge">
                                        {nominalStatus.status_label || nominalStatus.status}
                                      </span>
                                    )}
                                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={refreshNominalStatus}
                                      disabled={nominalStatusLoading} title="Refresh live status" data-testid="refresh-nominal-status-btn">
                                      {nominalStatusLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                    </Button>
                                  </span>
                                </div>
                                {nominalStatus?.explanation && (
                                  <p className="text-xs text-gray-600 mt-1" data-testid="nominal-status-explanation">{nominalStatus.explanation}</p>
                                )}
                                {nominalStatus?.cop_result && !["full_match"].includes(nominalStatus.cop_result) && (
                                  <p className="text-xs text-red-600 mt-1 font-medium" data-testid="nominal-status-cop">
                                    CoP result: {String(nominalStatus.cop_result).replace(/_/g, " ")}
                                  </p>
                                )}
                                {nominalStatus?.error && (
                                  <p className="text-xs text-amber-700 mt-1" data-testid="nominal-status-error">{nominalStatus.error}</p>
                                )}
                                {nominalStatus?.checked_at && (
                                  <p className="text-[10px] text-muted-foreground mt-1">
                                    Checked {new Date(nominalStatus.checked_at).toLocaleTimeString("en-GB")}{nominalStatus.mode === "sandbox" ? " · sandbox" : ""}
                                  </p>
                                )}
                                {nominalStatus?.cancellable && (
                                  <Button size="sm" variant="outline"
                                    className="mt-2 h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
                                    onClick={handleCancelNominal}
                                    disabled={processing === "cancel_nominal"}
                                    data-testid="cancel-nominal-btn">
                                    {processing === "cancel_nominal" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                                    Cancel payment
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {(application.test_payment_attempts || 0) < 3 && canDisburse && (
                                <Button
                                  onClick={() => setTestPaymentDialog(true)}
                                  data-testid="confirm-test-payment-btn"
                                >
                                  Confirm Nominal Payment
                                </Button>
                              )}
                              {canDisburse && (
                                <Button
                                  variant={(application.test_payment_attempts || 0) >= 3 ? "default" : "outline"}
                                  onClick={() => handleSendTestPayment(true)}
                                  disabled={processing === "test_payment" || !canSendNominal}
                                  title={!canSendNominal ? `Only the assigned user (${application.assigned_to}) can re-send the nominal` : undefined}
                                  data-testid="resend-test-payment-btn"
                                >
                                  {processing === "test_payment" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                  {isAus ? "Re-send via Capay" : "Re-send New Test Payment"}
                                </Button>
                              )}
                              {canDisburse && isAus && (
                                <Button
                                  variant="outline"
                                  onClick={() => setManualNominalDialog(true)}
                                  data-testid="rerecord-manual-nominal-btn"
                                >
                                  Re-record Manual Nominal
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {stage.key === "prefunding" && status !== "locked" && (
                  <div className="space-y-4">
                    {application.prefunding_call_completed ? (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-emerald-800 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              Pre-Funding Call Completed — PASSED ✓
                            </p>
                            <p className="text-sm text-emerald-700 mt-1">
                              Completed by: {application.prefunding_call_by} on {new Date(application.prefunding_call_at).toLocaleString("en-GB")}
                            </p>
                            {application.prefunding_call_notes && (
                              <p className="text-sm text-emerald-600 mt-2">
                                <strong>Notes:</strong> {application.prefunding_call_notes}
                              </p>
                            )}
                            {application.prefunding_call_recording && (
                              <p className="text-sm text-emerald-600 mt-1">
                                <strong>Recording:</strong> {application.prefunding_call_recording}
                              </p>
                            )}
                            <ExtraCallsPanel appId={appId} application={application} user={user} onChanged={loadData} />
                          </div>
                          <div className="flex flex-col gap-2 items-end shrink-0">
                            {!application.prefunding_call_recording && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleFetchAircallRecording}
                                disabled={processing === "fetch_recording"}
                                data-testid="fetch-aircall-recording-btn"
                                title="Attach the Aircall recording of this call (available a few minutes after hanging up)"
                              >
                                {processing === "fetch_recording" ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Phone className="w-4 h-4 mr-1" />
                                )}
                                Fetch Aircall Recording
                              </Button>
                            )}
                            {!isFunded && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleResetPrefundingCall}
                                disabled={processing === "reset_prefunding"}
                              >
                                {processing === "reset_prefunding" ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                )}
                                Redo Call
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : application.prefunding_call_failed ? (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3" data-testid="prefunding-failed-panel">
                        <div>
                          <p className="font-medium text-red-800 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5" />
                            Pre-Funding Call FAILED
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {application.prefunding_call_failed_by}
                            {application.prefunding_call_failed_at && ` · ${new Date(application.prefunding_call_failed_at).toLocaleString("en-GB")}`}
                          </p>
                          <ul className="mt-2 list-disc list-inside text-sm text-red-700" data-testid="prefunding-failed-items">
                            {(application.prefunding_call_failed_items || []).map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                          <p className="text-xs text-red-600 mt-2">
                            Disbursement stays locked. Remedy the items above, then redo the FULL call —
                            previous answers are not carried over.
                          </p>
                        </div>
                        {!isFunded && (
                          <Button
                            onClick={handleRedoFailedCall}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="redo-prefunding-call-btn"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            Redo Pre-Funding Call
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {!application.test_payment_sent && !application.test_payment_confirmed && (
                          <Alert className="border-amber-200 bg-amber-50" data-testid="prefunding-locked-alert">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-700">
                              Send the nominal payment before starting the
                              pre-funding call — the customer confirms receipt of it on the call.
                            </AlertDescription>
                          </Alert>
                        )}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between gap-4" data-testid="prefunding-summary-card">
                          <div>
                            <div className="flex items-center gap-2">
                              <Phone className="w-5 h-5 text-blue-600" />
                              <h4 className="font-medium text-blue-800">Pre-Funding Call</h4>
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {application.is_topup ? "Top-Up" : "New Deal"}
                              </span>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                Graded pass/fail
                              </span>
                            </div>
                            <p className="text-sm text-blue-700 mt-1" data-testid="prefunding-progress-text">
                              {prefundingAnsweredRequired}/{prefundingRequiredQs.length} required questions answered
                              {prefundingRecording && " · recording uploaded"}
                            </p>
                          </div>
                          <Button
                            onClick={openPrefundingDialog}
                            disabled={isFunded || (!application.test_payment_sent && !application.test_payment_confirmed)}
                            className="bg-blue-600 hover:bg-blue-700 shrink-0"
                            data-testid="open-prefunding-dialog-btn"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {application.prefunding_call_draft
                              ? "Resume Saved Call"
                              : Object.keys(prefundingResponses).length > 0 ? "Continue Call Checklist" : "Start Pre-Funding Call"}
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="mt-3">
                      <PrefundingSmsScheduler application={application} onChanged={loadData} />
                    </div>
                    <PreFundingCallDialog
                      open={prefundingDialogOpen}
                      onOpenChange={handlePrefundingDialogChange}
                      application={application}
                      questions={prefundingQuestions}
                      responses={prefundingResponses}
                      notes={prefundingNotes}
                      setNotes={setPrefundingNotes}
                      recording={prefundingRecording}
                      uploadingRecording={uploadingRecording}
                      isFunded={isFunded}
                      processing={processing}
                      critical={prefundingCritical}
                      setCritical={setPrefundingCritical}
                      onCheckboxChange={handlePrefundingCheckboxChange}
                      onValueChange={handlePrefundingValueChange}
                      onUploadRecording={handleUploadRecording}
                      onComplete={handleCompletePrefundingCall}
                      onSaveDraft={() => { handleSavePrefundingDraft(false); setPrefundingDialogOpen(false); }}
                      draftInfo={prefundingDraftInfo}
                    />
                  </div>
                )}

                {/* Disbursement Stage */}
                {stage.key === "disbursement" && status !== "locked" && (
                  <div className="space-y-4">
                    {application.disbursement_confirmed ? (
                      <div className="p-4 bg-emerald-50 rounded-lg">
                        <p className="font-medium text-emerald-800">Disbursement Completed</p>
                        <p className="text-sm text-emerald-700">
                          Type: {application.disbursement_type === "refinance" ? "Refinance" : "New Loan"} • 
                          Amount: {application.currency === "GBP" ? "£" : "A$"}
                          {application.disbursement_final_amount?.toLocaleString()}
                          {application.disbursement_reference && ` • Ref: ${application.disbursement_reference}`}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* STRICT LOCK — every prior step must be complete before disbursing */}
                        {!canProceedToDisbursement() && (
                          <Alert className="border-red-200 bg-red-50" data-testid="disbursement-locked-alert">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <AlertTitle className="text-red-800">Disbursement Locked</AlertTitle>
                            <AlertDescription className="text-red-700 text-sm">
                              Every prior step must be complete before the loan can be disbursed — this list updates as steps finish:
                              <ul className="mt-2 space-y-1" data-testid="disburse-lock-checklist">
                                {[
                                  { done: !!application.assigned_to, label: "Funding specialist assigned" },
                                  { done: application.docusign_status === "signed" || application.docusign_status === "completed", label: "DocuSign contract completed" },
                                  { done: application.bank_details_status === "verified", label: isAus ? "Bank details verified (manual)" : "Bank details verified (CoP)" },
                                  { done: !!application.standing_order_initiated, label: isAus ? "Direct Debit (BECS) mandate active" : "Payment / standing order initiated" },
                                  { done: !!application.loandisk_validated, label: "LoanDisk loan validated" },
                                  ...(application.is_topup ? [{ done: !!application.net_in_hand_confirmed, label: "Net in Hand confirmed" }] : []),
                                  {
                                    done: !!application.test_payment_confirmed,
                                    label: !application.test_payment_confirmed && application.test_payment_sent
                                      ? "Nominal payment sent — awaiting customer confirmation"
                                      : "Nominal payment sent & confirmed by customer",
                                  },
                                  {
                                    done: !!application.prefunding_call_completed && !application.prefunding_call_failed,
                                    failed: !!application.prefunding_call_failed,
                                    label: application.prefunding_call_failed
                                      ? "Pre-funding call FAILED — remedy the issues and redo the full call"
                                      : "Pre-funding call passed",
                                  },
                                  ...(application.is_consolidation ? [
                                    { done: !!application.consolidation_approved, label: "Consolidation approved by credit" },
                                    {
                                      done: !isConsolidationExpired(),
                                      failed: isConsolidationExpired(),
                                      label: isConsolidationExpired()
                                        ? `Consolidation lender amounts EXPIRED (over ${portalSettings.consolidation_expiry_hours} hours old)`
                                        : "Consolidation lender amounts current",
                                    },
                                  ] : []),
                                ].map((item, idx) => (
                                  <li key={idx} className={`flex items-start gap-2 ${item.done ? "text-emerald-700" : item.failed ? "text-red-800 font-semibold" : ""}`}>
                                    {item.done
                                      ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                      : <XCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${item.failed ? "text-red-600" : "text-slate-400"}`} />}
                                    <span className={item.done ? "line-through opacity-75" : ""}>{item.label}</span>
                                  </li>
                                ))}
                              </ul>
                            </AlertDescription>
                          </Alert>
                        )}

                        {application.disbursement_gc_send_status === "paused_insufficient_funds" && !application.disbursement_confirmed && (
                          <div className="p-4 bg-amber-50 rounded-lg border border-amber-300 space-y-3" data-testid="disbursement-paused-banner">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                              <div>
                                <p className="font-medium text-amber-800">Disbursement Paused — Insufficient Funds</p>
                                <p className="text-sm text-amber-700 mt-1">
                                  The payout account doesn't have enough funds to send this disbursement. No money was sent.
                                  Please request admin to add funds, then retry.
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={openDisbursementDialog}
                              disabled={!application.disbursement_manager_approved || !canProceedToDisbursement()}
                              className="w-full bg-amber-600 hover:bg-amber-700"
                              data-testid="retry-disbursement-btn"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Retry Disbursement
                            </Button>
                          </div>
                        )}

                        {application.test_payment_confirmed && (disbursementCalc ? (
                          <div className="space-y-4">
                            {/* Disbursement Calculation Summary */}
                            <div className="p-4 bg-slate-50 rounded-lg border">
                              <p className="font-medium text-slate-800 mb-3">
                                Disbursement Calculation
                              </p>
                              <div className="space-y-2 text-sm">
                                {/* For Top Ups - show Net in Hand based calculation */}
                                {(disbursementCalc.is_topup || application.is_topup) ? (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Net in Hand:</span>
                                      <span className="font-mono font-medium">
                                        {application.currency === "GBP" ? "£" : "A$"}
                                        {(disbursementCalc.confirmed_net_in_hand || 0).toLocaleString()}
                                      </span>
                                    </div>
                                    {disbursementCalc.test_payment > 0 && (
                                      <div className="flex justify-between text-red-600">
                                        <span>Less: Test Payment:</span>
                                        <span className="font-mono font-medium">
                                          -{application.currency === "GBP" ? "£" : "A$"}
                                          {disbursementCalc.test_payment?.toFixed(2)}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Loan Amount:</span>
                                      <span className="font-mono font-medium">
                                        {application.currency === "GBP" ? "£" : "A$"}
                                        {disbursementCalc.loan_amount?.toLocaleString()}
                                      </span>
                                    </div>
                                    {disbursementCalc.test_payment > 0 && (
                                      <div className="flex justify-between text-red-600">
                                        <span>Less: Test Payment:</span>
                                        <span className="font-mono font-medium">
                                          -{application.currency === "GBP" ? "£" : "A$"}
                                          {disbursementCalc.test_payment?.toFixed(2)}
                                        </span>
                                      </div>
                                    )}
                                    {disbursementCalc.consolidation_total > 0 && (
                                      <div className="flex justify-between text-red-600" data-testid="disb-less-lender-payoffs">
                                        <span>Less: Lender payoffs (paid separately):</span>
                                        <span className="font-mono font-medium">
                                          -{application.currency === "GBP" ? "£" : "A$"}
                                          {disbursementCalc.consolidation_total?.toLocaleString()}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                                
                                <div className="pt-2 border-t flex justify-between font-semibold">
                                  <span>Disbursement Amount:</span>
                                  <span className="font-mono text-emerald-700">
                                    {application.currency === "GBP" ? "£" : "A$"}
                                    {disbursementCalc.calculated_disbursement?.toLocaleString()}
                                  </span>
                                </div>
                                
                                <div className="flex justify-between text-muted-foreground">
                                  <span>Reference:</span>
                                  <span className="font-mono">{disbursementCalc.reference}</span>
                                </div>
                                
                                <div className="flex justify-between text-muted-foreground">
                                  <span>Type:</span>
                                  <span>{(disbursementCalc.disbursement_type === "refinance" || disbursementCalc.disbursement_type === "TopUp" || application.is_topup) ? "Top Up / Refinance" : "New Loan"}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Discrepancy Warning - only show if not yet approved */}
                            {disbursementCalc.has_discrepancy && !application.disbursement_manager_approved && (
                              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                                  <div>
                                    <p className="font-medium text-amber-800">Note</p>
                                    <p className="text-sm text-amber-700 mt-1">
                                      {disbursementCalc.discrepancy_details}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Approval Status */}
                            {application.disbursement_approval_pending && (
                              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="font-medium text-blue-800">Pending Manager Approval</p>
                                <p className="text-sm text-blue-700">
                                  Waiting for approval in the Approvals queue.
                                </p>
                              </div>
                            )}
                            
                            {application.disbursement_manager_approved && (
                              <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                                <p className="font-medium text-emerald-800">Approved by Manager ✓</p>
                                <p className="text-sm text-emerald-700">
                                  Approved by: {application.disbursement_manager_approved_by}
                                </p>
                                {application.disbursement_manager_approved_at && (
                                  <p className="text-xs text-emerald-600 mt-1">
                                    {new Date(application.disbursement_manager_approved_at).toLocaleString('en-GB')}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {application.disbursement_approval_expired && (
                              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <p className="font-medium text-amber-800">⚠️ Approval Expired</p>
                                <p className="text-sm text-amber-700">
                                  The previous approval has expired because checklist items are no longer valid.
                                  Please re-request approval after updating the required items.
                                </p>
                              </div>
                            )}
                            
                            {application.disbursement_rejected && (
                              <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-3">
                                <div>
                                  <p className="font-medium text-red-800">Rejected by the disbursement approver</p>
                                  <p className="text-sm text-red-700">
                                    {application.disbursement_rejection_reason || "No reason provided"}
                                  </p>
                                </div>
                                <p className="text-xs text-red-600">
                                  Address the issue above and click below to resubmit for approval.
                                </p>
                                <Button
                                  onClick={openDisbursementDialog}
                                  disabled={!canProceedToDisbursement() || processing === "disbursement"}
                                  className="w-full bg-amber-600 hover:bg-amber-700"
                                  data-testid="resubmit-disbursement-btn"
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Resubmit for Disbursement Approval
                                </Button>
                              </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex flex-col gap-3">
                              <div className="flex gap-3">
                              {/* Always require approval before disbursement */}
                              {!application.disbursement_manager_approved && !application.disbursement_approval_pending ? (
                                <Button
                                  onClick={handleRequestDisbursementApproval}
                                  disabled={processing === "disbursement_approval" || !canProceedToDisbursement()}
                                  className="bg-blue-600 hover:bg-blue-700"
                                  data-testid="request-disbursement-approval-btn"
                                >
                                  {processing === "disbursement_approval" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                  <Send className="w-4 h-4 mr-2" />
                                  Request Manager Approval
                                </Button>
                              ) : application.disbursement_approval_pending ? (
                                <div className="flex-1 p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                                  <p className="text-blue-800 font-medium">⏳ Awaiting Manager Approval</p>
                                  <p className="text-xs text-blue-600 mt-1">Check the Approvals page for status</p>
                                </div>
                              ) : canDisburse ? (
                                <Button
                                  onClick={openDisbursementDialog}
                                  disabled={
                                    !application.assigned_to ||
                                    processing === "disbursement" || 
                                    !application.disbursement_manager_approved ||
                                    !canProceedToDisbursement()
                                  }
                                  data-testid="confirm-disbursement-btn"
                                >
                                  {processing === "disbursement" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Confirm Disbursement
                                </Button>
                              ) : noDisbursementNote}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Always require approval for new loans too */}
                            {!application.disbursement_manager_approved && !application.disbursement_approval_pending ? (
                              <Button
                                onClick={handleRequestDisbursementApproval}
                                disabled={processing === "disbursement_approval" || !canProceedToDisbursement()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="request-disbursement-approval-btn"
                              >
                                {processing === "disbursement_approval" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                <Send className="w-4 h-4 mr-2" />
                                Request Manager Approval
                              </Button>
                            ) : application.disbursement_approval_pending ? (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                                <p className="text-blue-800 font-medium">⏳ Awaiting Manager Approval</p>
                                <p className="text-xs text-blue-600 mt-1">Check the Approvals page for status</p>
                              </div>
                            ) : canDisburse ? (
                              <Button
                                onClick={openDisbursementDialog}
                                disabled={!application.assigned_to || !canProceedToDisbursement() || !application.disbursement_manager_approved}
                                data-testid="confirm-disbursement-btn"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm Disbursement
                              </Button>
                            ) : noDisbursementNote}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Stage 7: Payment Tracking — post-funding event */}
                {stage.key === "tracking" && (
                  <div className="space-y-3" data-testid="payment-tracking-stage">
                    {!application.disbursement_confirmed ? (
                      <div className="p-4 bg-slate-50 rounded-lg border text-sm text-muted-foreground" data-testid="tracking-locked-note">
                        Payment tracking unlocks once the loan has been funded — after disbursement you can follow the money here.
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div>
                            <p className="font-medium text-emerald-800">Deal funded — post-funding payment tracking</p>
                            <p className="text-xs text-emerald-700 mt-0.5">
                              {isAus
                                ? "Live status pulled from Capay for payouts sent from the portal — manual bank transfers show no tracking."
                                : "Live status pulled from GoCardless for payments sent from the portal in LIVE mode."}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" onClick={loadTracking} disabled={trackingLoading} data-testid="refresh-tracking-btn">
                            {trackingLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                            Refresh
                          </Button>
                        </div>
                        {trackingLoading && !tracking && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground p-3">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading payment tracking…
                          </div>
                        )}
                        {(tracking?.payments || []).map((p) => (
                          <div key={p.kind} className="p-4 rounded-lg border bg-white space-y-1.5" data-testid={`tracking-${p.kind}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <p className="font-medium text-slate-800">{p.label}</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={p.mode === "live" ? "border-emerald-300 text-emerald-700" : "border-slate-300 text-slate-500"}>
                                  {p.mode === "live" ? "LIVE" : "SANDBOX"}
                                </Badge>
                                {p.tracking_available && p.status && (
                                  <Badge className={
                                    ["paid", "COMPLETED"].includes(p.status) ? "bg-emerald-600" :
                                    ["failed", "cancelled", "bounced", "FAILED", "CANCELLED", "REJECTED"].includes(p.status) ? "bg-red-600" : "bg-blue-600"
                                  } data-testid={`tracking-${p.kind}-status`}>
                                    {p.status_label || p.status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {p.tracking_available ? (
                              <div className="text-xs text-muted-foreground space-y-0.5">
                                {p.amount != null && <p>Amount: {curSym}{Number(p.amount).toLocaleString()}</p>}
                                {p.payment_id && <p className="font-mono">{p.rail === "capay" ? "Capay ref" : "GC ref"}: {p.payment_id}</p>}
                                {p.reference_no && <p className="font-mono">Capay payment no: {p.reference_no}</p>}
                                {p.cop_result && <p>Payee name check: {String(p.cop_result).replace(/_/g, " ")}</p>}
                                {p.last_checked_at && <p>Last checked: {new Date(p.last_checked_at).toLocaleString("en-GB")}</p>}
                                {p.error && <p className="text-red-600">Tracking error: {p.error}</p>}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground italic" data-testid={`tracking-${p.kind}-unavailable`}>
                                {p.reason || "No tracking available"}
                              </p>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Test Payment Confirmation Dialog */}
      {/* Reset Workflow Elements Dialog */}
      <Dialog open={directorOverrideOpen} onOpenChange={setDirectorOverrideOpen}>
        <DialogContent className="max-w-md" data-testid="director-override-dialog">
          <DialogHeader>
            <DialogTitle>Admin override — director signature match</DialogTitle>
            <DialogDescription>
              Use this when the director genuinely signed but the name check fails on formatting
              (e.g. Companies House "PRESCOTT, John" vs DocuSign "Jon Prescott"). The override is
              audit-logged with your name and unblocks nominal + disbursement payments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="director-override-reason">Reason (required)</Label>
            <Textarea
              id="director-override-reason"
              placeholder='e.g. "John Prescott signed via DocuSign as Jon Prescott — same person, name shortened"'
              value={directorOverrideReason}
              onChange={(e) => setDirectorOverrideReason(e.target.value)}
              rows={3}
              data-testid="director-override-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDirectorOverrideOpen(false)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleOverrideDirectorMatch}
              disabled={overridingDirector}
              data-testid="director-override-confirm-btn"
            >
              {overridingDirector ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Override & unblock payments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetElementsDialog} onOpenChange={setResetElementsDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Reset Workflow Elements
            </DialogTitle>
            <DialogDescription>
              Tick the elements to reset so the paperwork can be redone and re-validated
              (e.g. after DocuSign docs were deleted &amp; re-issued).
            </DialogDescription>
          </DialogHeader>
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700 text-xs">
              This only clears the portal's records — nothing is voided or deleted in DocuSign,
              GoCardless or LoanDisk. Handle the provider side first if needed. Every reset is audit-logged.
            </AlertDescription>
          </Alert>
          <div className="space-y-2 py-2">
            {RESET_ELEMENTS.map((el) => {
              const status = application ? el.status(application) : null;
              const isMoney = MONEY_RESET_KEYS.includes(el.key);
              const lockedNote = !isMoney
                ? null
                : disbursementIsLocked(application)
                  ? "Funded or being sent — can no longer be reset"
                  : !canDisburse
                    ? "Needs the disbursement permission"
                    : null;
              const resettable = status !== null && !lockedNote;
              return (
                <label
                  key={el.key}
                  className={`flex items-start gap-3 p-3 rounded-lg border ${resettable ? "cursor-pointer hover:bg-slate-50" : "opacity-50 bg-slate-50"}`}
                  data-testid={`reset-element-row-${el.key}`}
                >
                  <Checkbox
                    checked={!!resetSelection[el.key]}
                    disabled={!resettable}
                    onCheckedChange={(v) => setResetSelection((prev) => ({ ...prev, [el.key]: !!v }))}
                    className="mt-0.5"
                    data-testid={`reset-element-checkbox-${el.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{el.label}</p>
                    <p className={`text-xs ${resettable ? "text-slate-600" : "text-muted-foreground"}`}>
                      {resettable ? status : (lockedNote || "Nothing to reset")}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{el.hint}</p>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetElementsDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetElements}
              disabled={!resetSelectedKeys.length || processing === "reset_elements"}
              data-testid="reset-selected-btn"
            >
              {processing === "reset_elements" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset selected ({resetSelectedKeys.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUS — record the manually-sent nominal (Capay mocked) */}
      <Dialog open={manualNominalDialog} onOpenChange={setManualNominalDialog}>
        <DialogContent className="max-w-md" data-testid="manual-nominal-dialog">
          <DialogHeader>
            <DialogTitle>Record Manual Nominal Payment</DialogTitle>
            <DialogDescription>
              Enter the exact amount you transferred from the bank to the customer's verified account.
              It stays hidden afterwards — the customer confirms it on the pre-funding call.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="manual-nominal-amount">Amount sent (A$)</Label>
              <Input
                id="manual-nominal-amount"
                type="number"
                step="0.01"
                min="0.01"
                max="10"
                value={manualNominalAmount}
                onChange={(e) => setManualNominalAmount(e.target.value)}
                placeholder="e.g. 0.35"
                data-testid="manual-nominal-amount-input"
              />
              <p className="text-[11px] text-muted-foreground">Between A$0.01 and A$10.00</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-nominal-ref">Bank reference (optional)</Label>
              <Input
                id="manual-nominal-ref"
                value={manualNominalRef}
                onChange={(e) => setManualNominalRef(e.target.value)}
                placeholder="e.g. NAB receipt number"
                data-testid="manual-nominal-ref-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualNominalDialog(false)}>Cancel</Button>
            <Button
              onClick={handleManualNominalSent}
              disabled={processing === "manual_nominal" || !manualNominalAmount}
              data-testid="manual-nominal-confirm-btn"
            >
              {processing === "manual_nominal" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Record Nominal Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testPaymentDialog} onOpenChange={setTestPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Test Payment</DialogTitle>
            <DialogDescription>
              Enter the exact amount the customer received to verify the bank account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirmed-amount">Amount Received</Label>
              <Input
                id="confirmed-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={confirmedAmount}
                onChange={(e) => setConfirmedAmount(e.target.value)}
                data-testid="confirmed-amount-input"
              />
              <p className="text-xs text-muted-foreground">
                This must match the test payment amount exactly.
              </p>
              {isAus && application?.test_payment_rail === "capay" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="capay-fee-note">
                  Confirm the amount that LANDED in the customer&apos;s account. The Capay statement shows
                  the debit INCLUDING Capay&apos;s payment fee (~A$2) — that is NOT the nominal amount.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestPaymentDialog(false)}>
              Cancel
            </Button>
            {canDisburse && (
              <Button
                onClick={handleConfirmTestPayment}
                disabled={!confirmedAmount || processing === "confirm_test"}
                data-testid="submit-confirm-test-btn"
              >
                {processing === "confirm_test" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disbursement Confirmation Dialog */}
      {/* LoanDisk direct-creation wizard (Stage 4) */}
      {application && (
        <LoanDiskCreateWizard
          application={application}
          open={loanDiskWizardOpen}
          onOpenChange={setLoanDiskWizardOpen}
          onCreated={loadData}
        />
      )}

      {/* Formal Disbursement Dialog - Multi-Step Process */}
      <Dialog open={disbursementDialog} onOpenChange={(open) => {
        setDisbursementDialog(open);
        if (!open) {
          // Reset state when dialog closes
          setDisbursementStep(1);
          setPasswordInput("");
          setPasswordVerified(false);
          setConfirmAmountInput("");
          setAgreedToTerms(false);
          setDisbursementChecklist(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {disbursementStep === 1 && "Step 1: Pre-Disbursement Checklist"}
              {disbursementStep === 2 && ((user?.disbursement_limit || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0
                ? "Step 2: Self-authorise (within your limit)"
                : "Step 2: Request Manager Approval")}
              {disbursementStep === 3 && "Step 3: Password Verification"}
              {disbursementStep === 4 && "Step 4: Final Confirmation"}
              <WizardDebugButton className="ml-auto mr-6" />
            </DialogTitle>
            <DialogDescription>
              {disbursementStep === 1 && "Verify all requirements are met before proceeding"}
              {disbursementStep === 2 && ((user?.disbursement_limit || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0
                ? "Loan amount is within your limit — self-authorise to skip manager approval"
                : "Submit request for manager sign-off")}
              {disbursementStep === 3 && "Re-enter your password to authorize"}
              {disbursementStep === 4 && "Type the amount and confirm to execute disbursement"}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step Progress Bar */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  disbursementStep > step ? "bg-emerald-500 text-white" :
                  disbursementStep === step ? "bg-blue-500 text-white" :
                  "bg-slate-200 text-slate-500"
                }`}>
                  {disbursementStep > step ? "✓" : step}
                </div>
                {step < 4 && <div className={`w-8 h-0.5 ${disbursementStep > step ? "bg-emerald-500" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>
          
          <div className="space-y-4 py-4">
            {/* Step 1: Checklist */}
            {disbursementStep === 1 && (
              <div className="space-y-4">
                {!disbursementChecklist ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    <span>Loading checklist...</span>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {Object.entries(disbursementChecklist.checklist).map(([key, item]) => (
                        <div key={key} data-testid={`disb-checklist-${key}`} className={`flex items-center justify-between p-3 rounded-lg border ${
                          item.informational ? "bg-slate-50 border-slate-200" :
                          item.complete ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                        }`}>
                          <div className="flex items-center gap-3">
                            {item.complete ? (
                              <CheckCircle className={`w-5 h-5 ${item.informational ? "text-slate-500" : "text-emerald-600"}`} />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600" />
                            )}
                            <div>
                              <p className={`font-medium ${item.informational ? "text-slate-700" : item.complete ? "text-emerald-800" : "text-red-800"}`}>
                                {item.label}
                              </p>
                              <p className="text-xs text-muted-foreground">{item.details}</p>
                            </div>
                          </div>
                          {key === "director_signed" && !item.complete && isAdminUser && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 border-amber-400 text-amber-700 hover:bg-amber-50"
                              onClick={() => setDirectorOverrideOpen(true)}
                              data-testid="override-director-match-btn"
                            >
                              Admin override
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {!disbursementChecklist.all_complete && (
                      <Alert className="border-red-300 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <AlertTitle className="text-red-800">Cannot Proceed</AlertTitle>
                        <AlertDescription className="text-red-700">
                          All checklist items must be complete before requesting disbursement approval.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Step 2: Request Approval / Show Approval Status */}
            {disbursementStep === 2 && (
              <div className="space-y-4">
                {application?.disbursement_manager_approved ? (
                  <Alert className="border-emerald-300 bg-emerald-50">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <AlertTitle className="text-emerald-800">Manager Approved ✓</AlertTitle>
                    <AlertDescription className="text-emerald-700">
                      Approved by: {application.disbursement_manager_approved_by}<br/>
                      Approved at: {new Date(application.disbursement_manager_approved_at).toLocaleString('en-GB')}
                    </AlertDescription>
                  </Alert>
                ) : application?.disbursement_approval_pending ? (
                  <Alert className="border-amber-300 bg-amber-50">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">Awaiting Manager Approval</AlertTitle>
                    <AlertDescription className="text-amber-700">
                      Your disbursement request has been submitted and is awaiting manager sign-off.
                      <p className="text-xs mt-1">Requested: {new Date(application.disbursement_approval_requested_at).toLocaleString('en-GB')}</p>
                    </AlertDescription>
                  </Alert>
                ) : application?.disbursement_rejected ? (
                  <Alert className="border-red-300 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-800">Disbursement Rejected</AlertTitle>
                    <AlertDescription className="text-red-700">
                      Reason: {application.disbursement_rejection_reason}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    {/* Self-authorisation notice — user's disbursement_limit
                        covers this loan_amount, so we auto-approve on request */}
                    {(user?.disbursement_limit || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0 && (
                      <Alert className="border-emerald-300 bg-emerald-50">
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                        <AlertTitle className="text-emerald-800">Self-authorisation available</AlertTitle>
                        <AlertDescription className="text-emerald-700">
                          Loan amount {curSym}{Number(application.loan_amount).toLocaleString()} is within your {curSym}{Number(user.disbursement_limit).toLocaleString()} disbursement limit — no manager approval required.
                          Click below to self-authorise and continue to password verification.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                      <p className="font-medium text-slate-700">Disbursement Summary</p>
                      {disbursementCalc && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">To Customer:</span>
                            <span className="font-mono font-medium text-emerald-600">
                              {curSym}{disbursementCalc.calculated_disbursement?.toLocaleString()}
                            </span>
                          </div>
                          {application?.is_consolidation && application?.consolidation_lenders?.length > 0 && (
                            <>
                              <div className="pt-2 border-t">
                                <p className="text-sm font-medium text-slate-600 mb-2">Consolidation Payoffs <span className="font-normal text-xs text-slate-500">(paid separately from the Consolidation panel — not sent by the final step)</span>:</p>
                                {application.consolidation_lenders.map((lender, idx) => (
                                  <div key={idx} className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">{lender.lender_name || lender.name}:{["paid", "paid_manually"].includes(lender.payout_status || "") && <span className="ml-1 text-[10px] text-emerald-700">✓ already paid</span>}</span>
                                    <span className="font-mono text-red-600">{curSym}{parseFloat(lender.amount || 0).toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          <div className="pt-2 border-t flex justify-between">
                            <span className="font-semibold">TOTAL DISBURSEMENT:</span>
                            <span className="font-mono text-lg font-bold text-blue-700">
                              {curSym}{(disbursementCalc.calculated_disbursement + 
                                 (application?.consolidation_lenders?.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0) || 0)
                              ).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <Alert className="border-blue-200 bg-blue-50">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-700">
                        {(user?.disbursement_limit || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0
                          ? "Clicking below self-authorises this disbursement (within your limit) and continues to password verification."
                          : "Clicking \"Request Approval\" will send this disbursement to a manager for sign-off. You will be notified when approved."}
                      </AlertDescription>
                    </Alert>
                  </>
                )}
              </div>
            )}
            
            {/* Step 3: Password Verification */}
            {disbursementStep === 3 && (
              <div className="space-y-4">
                {passwordVerified ? (
                  <Alert className="border-emerald-300 bg-emerald-50">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <AlertTitle className="text-emerald-800">Password Verified ✓</AlertTitle>
                    <AlertDescription className="text-emerald-700">
                      Your identity has been confirmed. Proceed to final confirmation.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700">
                        For security, please re-enter your password to authorize this disbursement.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password-verify">Your Password</Label>
                      <Input
                        id="password-verify"
                        type="password"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        placeholder="Enter your password"
                        data-testid="disbursement-password-input"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Step 4: Final Confirmation */}
            {disbursementStep === 4 && (
              <div className="space-y-4">
                <Alert className="border-red-300 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertTitle className="text-red-800">FINAL STEP - IRREVERSIBLE</AlertTitle>
                  <AlertDescription className="text-red-700">
                    {isAus
                      ? `This sends the CUSTOMER payout (${curSym}${(disbursementCalc?.calculated_disbursement || 0).toLocaleString()}) via Capay (or falls back to the manual bank flow if Capay is unavailable) and completes the deal — it cannot be undone.`
                      : "This action will transfer funds and cannot be undone."}
                    {application?.consolidation_lenders?.length > 0 && (payLendersNow && allLendersPayable ? ` The ${unpaidLenders.length} ticked lender payoff${unpaidLenders.length > 1 ? "s" : ""} follow once the customer payout is confirmed.` : " Lender payoffs are NOT sent by this step.")}
                  </AlertDescription>
                </Alert>
                {application?.consolidation_lenders?.length > 0 && (() => {
                  // Dean (12 Oct 2026): the team paid a lender twice-by-hand out of fear this step re-sends them — spell out what leaves now.
                  // Paul SF-37219: and let the operator send the unpaid payoffs WITH the disbursement instead of a second trip to the panel.
                  const unpaid = unpaidLenders;
                  const unpaidTotal = unpaid.reduce((s, l) => s + parseFloat(l.amount || 0), 0);
                  return unpaid.length > 0 ? (
                    <div className={`rounded-lg border p-3 space-y-2 ${payLendersNow && allLendersPayable ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="disb-lenders-unpaid-note">
                      <label className={`flex items-start gap-2 ${allLendersPayable ? "cursor-pointer" : "cursor-not-allowed opacity-80"}`}>
                        <input type="checkbox" className="mt-1" checked={payLendersNow && allLendersPayable} disabled={!allLendersPayable}
                          onChange={(e) => setPayLendersNow(e.target.checked)} data-testid="disb-pay-lenders-toggle" />
                        <span className="text-sm">
                          <span className={`font-medium ${payLendersNow && allLendersPayable ? "text-emerald-800" : "text-amber-800"}`}>
                            Also pay the {unpaid.length} unpaid lender payoff{unpaid.length > 1 ? "s" : ""} now via {payoutRail} ({curSym}{unpaidTotal.toLocaleString()})
                          </span>
                          <span className={`block text-xs mt-0.5 ${payLendersNow && allLendersPayable ? "text-emerald-700" : "text-amber-700"}`}>
                            {payLendersNow && allLendersPayable
                              ? <>Sent one by one right after the customer payout is confirmed — {unpaid.map((l) => l.lender_name || l.name).join(", ")}. Each lender is still guarded against double-pay; if the customer payout pauses or fails, nothing goes to the lenders.</>
                              : allLendersPayable
                                ? <>Unticked — this step pays the customer only. Pay {unpaid.map((l) => l.lender_name || l.name).join(", ")} from the Consolidation panel (<b>Pay via {payoutRail}</b> or <b>Mark paid manually</b>) before or after this step; they are never sent twice.</>
                                : <>Can't be sent with this step yet — {unpayableLenders.map((l) => `${l.lender_name || l.name}: ${lenderPayableReason(l)}`).join("; ")}. Fix that in the Consolidation panel, or pay them there afterwards.</>}
                          </span>
                        </span>
                      </label>
                    </div>
                  ) : (
                    <Alert className="border-emerald-300 bg-emerald-50" data-testid="disb-lenders-paid-note">
                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                      <AlertTitle className="text-emerald-800">Lender payoffs already paid — not sent again</AlertTitle>
                      <AlertDescription className="text-emerald-700 text-sm">
                        Every lender below has already been paid (via {payoutRail} or manually). This step sends only the customer amount.
                      </AlertDescription>
                    </Alert>
                  );
                })()}
                {isAus && (
                  <Alert className="border-blue-200 bg-blue-50" data-testid="aus-manual-disburse-note">
                    <AlertDescription className="text-blue-700 text-sm">
                      Australian payouts are dispatched via <b>Capay</b> (sandbox/live per Portal Settings → API
                      Credentials → Capay). If Capay is not configured or the send fails, the deal completes on the
                      manual bank-transfer flow — never silently. LoanDisk renewal (top-ups), Monday → Funded with
                      the Sydney funding date, and the disbursement PDF all run automatically.
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="p-4 bg-slate-100 rounded-lg border-2 border-slate-300 space-y-3">
                  <p className="font-semibold text-center text-lg">Payment Summary</p>
                  
                  <div className="space-y-2">
                    {/* Show test payment deduction */}
                    {disbursementCalc?.test_payment > 0 && (
                      <div className="text-xs text-slate-600 mb-2 p-2 bg-slate-50 rounded">
                        <p className="font-medium">Calculation:</p>
                        <p>{application?.is_topup ? "Net in Hand" : "Loan Amount"}: {curSym}{(disbursementCalc?.is_topup ? disbursementCalc?.confirmed_net_in_hand : disbursementCalc?.loan_amount)?.toLocaleString()}</p>
                        <p className="text-red-600">Less Test Payment: -{curSym}{disbursementCalc?.test_payment?.toFixed(2)}</p>
                        {!disbursementCalc?.is_topup && disbursementCalc?.consolidation_total > 0 && (
                          <p className="text-red-600" data-testid="disb-calc-less-lenders">Less Lender payoffs: -{curSym}{disbursementCalc?.consolidation_total?.toLocaleString()}</p>
                        )}
                        <p className="font-semibold">= {curSym}{disbursementCalc?.calculated_disbursement?.toLocaleString()} to customer</p>
                      </div>
                    )}
                    
                    <div className="flex justify-between p-2 bg-emerald-50 rounded" data-testid="disb-to-customer-row">
                      <span>To Customer ({application?.company_name}) — <b>sent now via {isAus ? "Capay" : "GoCardless"}</b>:</span>
                      <span className="font-mono font-bold text-emerald-700">
                        {curSym}{disbursementCalc?.calculated_disbursement?.toLocaleString()}
                      </span>
                    </div>
                    
                    {application?.consolidation_lenders?.length > 0 && (
                      <p className="text-[11px] uppercase tracking-wide text-slate-500 pt-1">
                        {payLendersNow && allLendersPayable ? `Lender payoffs — sent with this step via ${payoutRail}, right after the customer payout` : "Lender payoffs — paid separately from the Consolidation panel, not by this step"}
                      </p>
                    )}
                    {application?.consolidation_lenders?.map((lender, idx) => {
                      const paid = lender.payout_status === "paid";
                      const manual = lender.payout_status === "paid_manually";
                      const paidOn = lender.payout_paid_at ? new Date(lender.payout_paid_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
                      return (
                        <div key={idx} className={`flex justify-between items-center gap-2 p-2 rounded ${paid || manual ? "bg-slate-50" : "bg-amber-50"}`} data-testid={`disb-lender-row-${idx}`}>
                          <span className="min-w-0">
                            <span className={paid || manual ? "text-slate-500" : ""}>To {lender.lender_name || lender.name}:</span>
                            {paid ? (
                              <span className="ml-2 text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1" data-testid={`disb-lender-paid-${idx}`}>
                                <BadgeCheck className="w-3 h-3" /> already paid via {payoutRail}{paidOn ? ` · ${paidOn}` : ""}{lender.payout_gc_payment_id ? ` · ${lender.payout_gc_payment_id}` : ""}
                              </span>
                            ) : manual ? (
                              <span className="ml-2 text-[11px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded" data-testid={`disb-lender-paid-${idx}`}>
                                already paid manually{lender.payout_paid_by ? ` by ${lender.payout_paid_by}` : ""}{paidOn ? ` · ${paidOn}` : ""}
                              </span>
                            ) : payLendersNow && allLendersPayable ? (
                              <span className="ml-2 text-[11px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded" data-testid={`disb-lender-unpaid-${idx}`}>
                                sent with this step via {payoutRail}
                              </span>
                            ) : (
                              <span className="ml-2 text-[11px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded" data-testid={`disb-lender-unpaid-${idx}`}>
                                not paid yet — not sent by this step
                              </span>
                            )}
                          </span>
                          <span className={`font-mono font-bold ${paid || manual ? "text-slate-500 line-through decoration-slate-400" : "text-amber-700"}`}>
                            {curSym}{parseFloat(lender.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                    
                    <div className="p-3 bg-blue-100 rounded-lg border-2 border-blue-300 space-y-1" data-testid="disb-total-row">
                      <div className="flex justify-between">
                        <span className="font-bold text-lg">DEAL TOTAL (approved):</span>
                        <span className="font-mono font-bold text-xl text-blue-800">
                          {curSym}{((disbursementCalc?.calculated_disbursement || 0) + 
                             (application?.consolidation_lenders?.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0) || 0)
                          ).toLocaleString()}
                        </span>
                      </div>
                      {application?.consolidation_lenders?.length > 0 && (
                        <p className="text-xs text-blue-800" data-testid="disb-leaving-now">
                          Leaving now: <b>{curSym}{(disbursementCalc?.calculated_disbursement || 0).toLocaleString()}</b> to the customer · lender payoffs {curSym}{(application.consolidation_lenders.reduce((sum, l) => sum + parseFloat(l.amount || 0), 0)).toLocaleString()} are handled on the Consolidation panel.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="confirm-amount" className="font-medium">
                      Type the DEAL TOTAL amount to confirm ({curSym}):
                    </Label>
                    {application?.consolidation_lenders?.length > 0 && (
                      <p className="text-xs text-muted-foreground -mt-1">The approved deal total shown above (customer + lender payoffs). Only the customer amount is sent by this step.</p>
                    )}
                    <Input
                      id="confirm-amount"
                      type="number"
                      value={confirmAmountInput}
                      onChange={(e) => setConfirmAmountInput(e.target.value)}
                      placeholder="Enter total amount"
                      className="text-lg font-mono"
                      data-testid="confirm-amount-input"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agree-terms"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      data-testid="agree-terms-checkbox"
                    />
                    <label htmlFor="agree-terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                      I confirm this disbursement is correct and understand that funds will be transferred immediately upon clicking {isAus ? <>&quot;RECORD DISBURSEMENT&quot;</> : <>&quot;DISBURSE NOW&quot;</>}.
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {disbursementStep > 1 && disbursementStep < 4 && (
              <Button variant="outline" onClick={() => setDisbursementStep(s => s - 1)} className="sm:mr-auto">
                ← Back
              </Button>
            )}
            
            <Button variant="outline" onClick={() => setDisbursementDialog(false)}>
              Cancel
            </Button>
            
            {/* Step 1: Continue to Approval */}
            {disbursementStep === 1 && (
              <Button
                onClick={() => setDisbursementStep(2)}
                disabled={!disbursementChecklist?.all_complete}
                data-testid="checklist-continue-btn"
              >
                Continue →
              </Button>
            )}
            
            {/* Step 2: Request Approval or Continue */}
            {disbursementStep === 2 && (
              application?.disbursement_manager_approved ? (
                <Button onClick={() => setDisbursementStep(3)} data-testid="approval-continue-btn">
                  Continue →
                </Button>
              ) : application?.disbursement_approval_pending ? (
                <Button disabled>Awaiting Approval...</Button>
              ) : (
                <Button
                  onClick={handleRequestDisbursementApproval}
                  disabled={processing === "request_disbursement_approval"}
                  className={
                    (((isAus ? user?.disbursement_limit_aud : user?.disbursement_limit)) || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }
                  data-testid="request-approval-btn"
                >
                  {processing === "request_disbursement_approval" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {(((isAus ? user?.disbursement_limit_aud : user?.disbursement_limit)) || 0) >= (application?.loan_amount || 0) && (application?.loan_amount || 0) > 0
                    ? "Self-authorise & Continue →"
                    : "Request Manager Approval"}
                </Button>
              )
            )}
            
            {/* Step 3: Verify Password */}
            {disbursementStep === 3 && (
              passwordVerified ? (
                <Button onClick={() => setDisbursementStep(4)} data-testid="password-continue-btn">
                  Continue →
                </Button>
              ) : canDisburse ? (
                <Button
                  onClick={handleVerifyPasswordForDisbursement}
                  disabled={!passwordInput || processing === "verify_password"}
                  data-testid="verify-password-btn"
                >
                  {processing === "verify_password" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Verify Password
                </Button>
              ) : noDisbursementNote
            )}
            
            {/* Step 4: Final Disburse */}
            {disbursementStep === 4 && canDisburse && (
              <Button
                onClick={handleExecuteDisbursement}
                disabled={
                  !agreedToTerms || 
                  !confirmAmountInput ||
                  processing === "execute_disbursement"
                }
                className="bg-red-600 hover:bg-red-700 font-bold"
                data-testid="execute-disbursement-btn"
              >
                {processing === "execute_disbursement" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isAus ? "🔒 RECORD DISBURSEMENT" : "🔒 DISBURSE NOW"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Select LoanDisk Loan Dialog */}
      <Dialog open={selectLoanDialog} onOpenChange={setSelectLoanDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Existing Loan</DialogTitle>
            <DialogDescription>
              Found existing loans in LoanDisk for this borrower. Select one to link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {loandiskLoans.map((loan, index) => (
              <div
                key={loan.loan_id || loan.id || index}
                className="p-4 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                onClick={() => handleLinkLoan(loan.loan_id || loan.id)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{loan.borrower_name || loan.business_name || 'Unknown Borrower'}</p>
                    <p className="text-sm text-muted-foreground">
                      Loan ID: {loan.loan_id || loan.id}
                      {loan.loan_application_id && ` • Ref: ${loan.loan_application_id}`}
                    </p>
                  </div>
                  {loan.loan_amount && (
                    <p className="font-semibold">
                      {curSym}{parseFloat(loan.loan_amount).toLocaleString()}
                    </p>
                  )}
                </div>
                {loan.loan_status && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Status: {loan.loan_status}
                  </p>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectLoanDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standing Order Email Preview Dialog */}
      <Dialog open={standingOrderDialog} onOpenChange={setStandingOrderDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Standing Order Request Email
            </DialogTitle>
            <DialogDescription>
              Preview the email that will be sent to request a standing order setup.
            </DialogDescription>
          </DialogHeader>
          
          {standingOrderPreview && (
            <div className="space-y-4">
              {/* Email panel — real send from applications@ */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex gap-2 items-center">
                  <span className="font-medium w-20 shrink-0">From:</span>
                  <span className="text-muted-foreground">{standingOrderPreview.from_mailbox || "applications@simplyfunded.co.uk"}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="font-medium w-20 shrink-0">To:</span>
                  <Input
                    value={soRecipient}
                    onChange={(e) => setSoRecipient(e.target.value)}
                    placeholder="customer@…  (send to yourself to test)"
                    className="h-8 text-sm"
                    data-testid="so-recipient-input"
                  />
                </div>
                <div className="flex gap-2 items-start">
                  <span className="font-medium w-20 shrink-0 mt-1.5">CC:</span>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex flex-wrap gap-1">
                      {soCc.map((cc) => (
                        <Badge key={cc} variant="secondary" className="text-xs gap-1" data-testid={`so-cc-${cc}`}>
                          {cc}
                          {standingOrderPreview.contact_email === cc && <span className="text-[9px] text-purple-600">(contact)</span>}
                          <button type="button" className="ml-0.5 hover:text-red-600"
                            onClick={() => setSoCc((prev) => prev.filter((c) => c !== cc))}>×</button>
                        </Badge>
                      ))}
                      {soCc.length === 0 && <span className="text-xs text-muted-foreground">No CC</span>}
                    </div>
                    <div className="flex gap-1.5 items-center">
                      {standingOrderPreview.contact_email && !soCc.includes(standingOrderPreview.contact_email) && (
                        <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                          data-testid="so-cc-add-contact"
                          onClick={() => setSoCc((prev) => [...prev, standingOrderPreview.contact_email])}>
                          + Contact — {standingOrderPreview.contact_email}
                        </Button>
                      )}
                      <Input
                        placeholder="type an email + Enter"
                        className="h-7 text-xs flex-1"
                        data-testid="so-cc-free-input"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const v = e.target.value.trim();
                            if (v && v.includes("@") && !soCc.includes(v)) { setSoCc((prev) => [...prev, v]); e.target.value = ""; }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="font-medium w-20 shrink-0 mt-1.5">BCC:</span>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex flex-wrap gap-1">
                      {soBcc.map((b) => (
                        <Badge key={b} variant="outline" className="text-xs gap-1 border-slate-300" data-testid={`so-bcc-${b}`}>
                          {b}
                          <button type="button" className="ml-0.5 hover:text-red-600"
                            onClick={() => setSoBcc((prev) => prev.filter((c) => c !== b))}>×</button>
                        </Badge>
                      ))}
                      {soBcc.length === 0 && <span className="text-xs text-muted-foreground">No BCC — internal people go here</span>}
                    </div>
                    <div className="flex gap-1.5 items-center">
                      <Select value="" onValueChange={(v) => { if (v && !soBcc.includes(v)) setSoBcc((prev) => [...prev, v]); }}>
                        <SelectTrigger className="h-7 w-[210px] text-xs" data-testid="so-bcc-user-select">
                          <SelectValue placeholder="+ Add portal user…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-56">
                          {portalUsers.filter((u) => u.email && !soBcc.includes(u.email)).map((u) => (
                            <SelectItem key={u.email} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="or type an email + Enter"
                        className="h-7 text-xs flex-1"
                        data-testid="so-bcc-free-input"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const v = e.target.value.trim();
                            if (v && v.includes("@") && !soBcc.includes(v)) { setSoBcc((prev) => [...prev, v]); e.target.value = ""; }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="font-medium w-20 shrink-0">Start date:</span>
                  <Input
                    type="date"
                    value={soStartDate}
                    onChange={(e) => handleSoStartDateChange(e.target.value)}
                    className="h-8 text-sm w-44"
                    data-testid="so-start-date-input"
                  />
                  <span className="text-xs text-muted-foreground">
                    auto-set 2 working days ahead — shown in the email as "{standingOrderPreview.first_payment_display}"
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="font-medium w-20 shrink-0 mt-1.5">Attach:</span>
                  <div className="flex-1 space-y-1.5" data-testid="so-attachments-section">
                    {soAttachments.map((a) => (
                      <div key={a.att_id} className="flex items-center gap-2 text-xs bg-white border rounded px-2 py-1"
                        data-testid={`so-att-${a.att_id}`}>
                        <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />
                        <button type="button"
                          onClick={() => handleSoAttachmentOpen(a.att_id, a.name)}
                          className="text-blue-600 hover:underline truncate flex-1 text-left"
                          title="Open to check it's the right file"
                          data-testid={`so-att-preview-${a.att_id}`}>
                          {a.name}
                        </button>
                        <span className="text-[10px] text-muted-foreground shrink-0">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                        <button type="button" className="text-slate-400 hover:text-red-600 shrink-0"
                          title="Remove attachment"
                          onClick={() => handleSoAttachmentDelete(a.att_id, a.name)}
                          data-testid={`so-att-delete-${a.att_id}`}>×</button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <input type="file" multiple className="hidden" id="so-att-file-input"
                        data-testid="so-att-file-input"
                        onChange={(e) => { handleSoAttachmentUpload(e.target.files); e.target.value = ""; }} />
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                        disabled={soAttUploading}
                        onClick={() => document.getElementById("so-att-file-input")?.click()}
                        data-testid="so-att-add-btn">
                        {soAttUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Paperclip className="w-3 h-3 mr-1" />}
                        + Attach file
                      </Button>
                      <span className="text-[10px] text-muted-foreground">
                        goes out with this email — saved on the deal for re-sends (click a file to check it)
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <span className="font-medium w-20 shrink-0">Subject:</span>
                  <span>{standingOrderPreview.subject}</span>
                </div>
              </div>
              
              {/* Payment summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600 font-medium">Payment Amount</p>
                  <p className="text-lg font-bold text-emerald-700">{curSym}{standingOrderPreview.payment_amount?.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Frequency</p>
                  <p className="text-lg font-bold text-blue-700">{standingOrderPreview.payment_frequency}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">Start Date</p>
                  <p className="text-lg font-bold text-purple-700">{ukDate(standingOrderPreview.first_payment_date)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Reference</p>
                  <p className="text-lg font-bold text-slate-700">{standingOrderPreview.reference}</p>
                </div>
              </div>
              
              {/* SimplyFunded bank details */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">Payment Account Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Bank:</span> {standingOrderPreview.sf_bank_details?.bank_name}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Account Name:</span> {standingOrderPreview.sf_bank_details?.account_name}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sort Code:</span> {standingOrderPreview.sf_bank_details?.sort_code}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Account No:</span> {standingOrderPreview.sf_bank_details?.account_number}
                  </div>
                </div>
              </div>
              
              {/* Email preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 border-b">
                  <span className="text-sm font-medium">Email Preview</span>
                </div>
                <div 
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={sanitizedHtmlProps(standingOrderPreview.html)}
                />
              </div>
              
              {standingOrderPreview.is_topup && (
                <Alert className="border-purple-200 bg-purple-50">
                  <AlertCircle className="h-4 w-4 text-purple-600" />
                  <AlertDescription className="text-purple-700">
                    This is a top-up - the email includes a reminder to cancel their existing standing order.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setStandingOrderDialog(false)}>
              Cancel
            </Button>
            {!application?.standing_order_initiated && (
            <Button
              variant="outline"
              onClick={() => handleInitiateStandingOrder(true)}
              disabled={processing === "standing"}
              data-testid="so-mark-sent-no-email"
              title="Transition mode: the SO email is being sent by the external automation — just mark this step complete"
            >
              <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
              Mark as sent (no email)
            </Button>
            )}
            <Button 
              onClick={() => handleInitiateStandingOrder(false)}
              disabled={processing === "standing"}
              data-testid="confirm-send-standing-order"
            >
              {processing === "standing" ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {application?.standing_order_initiated ? "Resend Email" : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signing Details Dialog */}
      <Dialog open={signingDetailsOpen} onOpenChange={setSigningDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5" />
              DocuSign Signing Details
            </DialogTitle>
            <DialogDescription>
              Envelope: {signingDetails?.envelope_id}
            </DialogDescription>
          </DialogHeader>
          
          {signingDetails && (
            <div className="space-y-4">
              {/* Fraud Alert Banner */}
              {signingDetails.needs_review && (
                <Alert className={`border-2 ${
                  signingDetails.risk_level === "critical" ? "border-red-500 bg-red-50" :
                  signingDetails.risk_level === "high" ? "border-orange-500 bg-orange-50" :
                  "border-amber-500 bg-amber-50"
                }`}>
                  <ShieldAlert className={`h-5 w-5 ${
                    signingDetails.risk_level === "critical" ? "text-red-600" :
                    signingDetails.risk_level === "high" ? "text-orange-600" :
                    "text-amber-600"
                  }`} />
                  <AlertTitle className="text-lg font-bold">
                    🚨 REVIEW REQUIRED - {signingDetails.risk_level?.toUpperCase()} RISK
                  </AlertTitle>
                  <AlertDescription className="mt-2">
                    <div className="space-y-2">
                      {signingDetails.fraud_flags.map((flag, idx) => (
                        <div key={idx} className={`p-2 rounded ${
                          flag.severity === "critical" ? "bg-red-100" :
                          flag.severity === "high" ? "bg-orange-100" :
                          "bg-amber-100"
                        }`}>
                          <p className="font-medium">
                            {flag.severity === "critical" ? "🔴" : flag.severity === "high" ? "🟠" : "🟡"} {flag.type.replace(/_/g, " ").toUpperCase()}
                          </p>
                          <p className="text-sm">{flag.message}</p>
                        </div>
                      ))}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              
              {/* Signers Section */}
              {signingDetails.broker_check && (signingDetails.broker_check.name || signingDetails.broker_check.emails?.length > 0) && (
                <div className="text-xs text-slate-600 border rounded-lg px-3 py-2 bg-slate-50" data-testid="fraud-broker-check">
                  <span className="font-semibold text-slate-800">Broker-as-witness check:</span>{" "}
                  witnesses compared against {signingDetails.broker_check.name || "the deal's broker"}
                  {signingDetails.broker_check.emails?.length > 0 && <> · {signingDetails.broker_check.emails.join(", ")}</>}
                  {signingDetails.broker_check.domains?.length > 0 && <> · domains {signingDetails.broker_check.domains.map(d => `@${d}`).join(", ")}</>}
                  {!signingDetails.fraud_flags?.some(f => f.type?.startsWith("broker_witness")) && <span className="text-emerald-700"> — no match ✓</span>}
                </div>
              )}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-blue-600" />
                  Signers ({signingDetails.signers?.length || 0})
                </h3>
                <div className="space-y-3">
                  {signingDetails.signers?.map((signer, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-base">{signer.name}</p>
                          <p className="text-sm text-muted-foreground">{signer.email || "No email"}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          signer.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                          signer.status === "sent" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {signer.status}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">IP Address:</span>
                          <p className="font-mono">{signer.ip_address || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Location:</span>
                          <p>{signer.latitude && signer.longitude ? `${signer.latitude}, ${signer.longitude}` : "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Signed At:</span>
                          <p>{signer.signed_at ? new Date(signer.signed_at).toLocaleString("en-GB") : "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Witnesses Section */}
              {signingDetails.witnesses?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-600" />
                    Witnesses ({signingDetails.witnesses.length})
                  </h3>
                  <div className="space-y-3">
                    {signingDetails.witnesses.map((witness, idx) => (
                      <div key={idx} className="p-3 bg-purple-50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-base">{witness.name}</p>
                            <p className="text-sm text-muted-foreground">{witness.email || "No email"}</p>
                            {witness.title && <p className="text-sm italic">{witness.title}</p>}
                          </div>
                          <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">
                            Witness for: {witness.witness_for || "Unknown"}
                          </span>
                        </div>
                        {witness.address && (
                          <p className="text-xs mt-1 text-muted-foreground">{witness.address}</p>
                        )}
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">IP Address:</span>
                            <p className="font-mono">{witness.ip_address || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Location:</span>
                            <p>{witness.latitude && witness.longitude ? `${witness.latitude}, ${witness.longitude}` : "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Witnessed At:</span>
                            <p>{witness.signed_at ? new Date(witness.signed_at).toLocaleString("en-GB") : "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Override Info */}
              {signingDetails.risk_override && (
                <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                  <p className="font-semibold text-blue-800 flex items-center gap-2">
                    <BadgeCheck className="w-5 h-5" />
                    Risk Overridden
                  </p>
                  <div className="mt-2 text-sm text-blue-700 space-y-1">
                    <p><strong>Original Risk:</strong> {signingDetails.risk_override.original_risk_level?.toUpperCase()}</p>
                    <p><strong>Overridden By:</strong> {signingDetails.risk_override.overridden_by_name || signingDetails.risk_override.overridden_by}</p>
                    <p><strong>Role:</strong> {signingDetails.risk_override.overridden_by_role}</p>
                    <p><strong>Reason:</strong> {signingDetails.risk_override.reason}</p>
                    <p><strong>Date:</strong> {new Date(signingDetails.risk_override.overridden_at).toLocaleString("en-GB")}</p>
                  </div>
                </div>
              )}
              
              {/* Risk Level Summary */}
              <div className={`p-4 rounded-lg ${
                signingDetails.risk_override ? "bg-blue-100 border-2 border-blue-400" :
                signingDetails.risk_level === "critical" ? "bg-red-100 border-2 border-red-500" :
                signingDetails.risk_level === "high" ? "bg-orange-100 border-2 border-orange-500" :
                signingDetails.risk_level === "medium" ? "bg-amber-100 border-2 border-amber-500" :
                "bg-emerald-100 border-2 border-emerald-500"
              }`}>
                <p className="font-semibold text-center">
                  Overall Risk Level: 
                  <span className={`ml-2 px-3 py-1 rounded ${
                    signingDetails.risk_override ? "bg-blue-600 text-white" :
                    signingDetails.risk_level === "critical" ? "bg-red-600 text-white" :
                    signingDetails.risk_level === "high" ? "bg-orange-600 text-white" :
                    signingDetails.risk_level === "medium" ? "bg-amber-600 text-white" :
                    "bg-emerald-600 text-white"
                  }`}>
                    {signingDetails.risk_override 
                      ? `${signingDetails.risk_level?.toUpperCase()} (OVERRIDDEN)` 
                      : signingDetails.risk_level?.toUpperCase() || "LOW"}
                  </span>
                </p>
                
                {/* Override Button */}
                {signingDetails.needs_review && !signingDetails.risk_override && (
                  <div className="mt-3 text-center">
                    <p className="text-xs text-muted-foreground mb-2">
                      {signingDetails.risk_level === "high" 
                        ? "Sales TL or higher can override this risk"
                        : signingDetails.risk_level === "critical"
                        ? "CRITICAL risks cannot be overridden - escalate for manual review"
                        : "Funding Specialist can override this risk"}
                    </p>
                    {signingDetails.risk_level !== "critical" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOverrideRisk}
                        disabled={loadingSigningDetails}
                        className="border-2"
                      >
                        {loadingSigningDetails && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Override Risk
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigningDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contract wizard pop-out — same Generate Documents wizard as the Onboarding Centre */}
      {wizardDealId && (
        <DocumentGeneration
          wizardOnly
          wizardDeal={wizardDealId}
          onWizardClose={() => { setWizardDealId(null); loadData(); }}
        />
      )}

      {/* AUS Onboarding wizard pop-out — same wizard as the AUS Onboarding Centre */}
      {ausWizard && (
        <AUSDocumentGeneration
          wizardOnly
          wizardDeal={ausWizard.deal}
          wizardCompany={ausWizard.company}
          onWizardClose={() => { setAusWizard(null); loadData(); }}
        />
      )}

      {/* Manual Companies House picker — used when the auto-match latched
          onto the wrong company (bug reported by Paul Richards, 23 Jul 2026:
          Frydays Fish Bar (Yate) was matched to an unrelated "Frydays Fish
          Bar Ltd" and the wrong company flowed into LoanDisk). */}
      <CompanySearchModal
        open={showCompanySearch}
        onOpenChange={setShowCompanySearch}
        isAus={isAus}
        initialQuery={application?.company_name || ""}
        results={companySearchResults}
        setResults={setCompanySearchResults}
        currentCrn={application?.ch_company_number || application?.company_registration_number}
        onPick={handleSelectCompanyMatch}
      />
    </div>
  );
}


function CompanySearchModal({ open, onOpenChange, isAus, initialQuery, results, setResults, currentCrn, onPick }) {
  const [q, setQ] = useState(initialQuery || "");
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  useEffect(() => { if (open) { setQ(initialQuery || ""); setSearchError(""); } }, [open, initialQuery]);
  const registry = isAus ? "ABR / ASIC" : "Companies House";
  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      if (isAus) {
        // AUS deals search the Australian registers — NEVER UK Companies House
        const r = await searchAusBusinesses(q.trim());
        const errs = Object.entries(r.errors || {});
        setSearchError(errs.length ? errs.map(([src, msg]) => `${src.toUpperCase()}: ${msg}`).join(" · ") : "");
        if (errs.length && !(r.businesses || []).length) toast.error(`Registry lookup failed — ${errs.map(([s, m]) => `${s.toUpperCase()}: ${m}`).join("; ")}`, { duration: 9000 });
        setResults((r.businesses || []).map((b) => ({
          company_name: b.business_name,
          company_number: b.abn || b.acn,
          company_status: b.status,
          address_snippet: [b.entity_type, b.is_sole_trader ? "Sole trader" : null, b.state, b.postcode, b.source]
            .filter(Boolean).join(" · "),
        })));
      } else {
        const r = await searchCompaniesHouse(q.trim(), 15);
        setResults(r.items || r.results || []);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || `${registry} search failed`);
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="ch-manual-search-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-4 h-4" /> Pick the correct {isAus ? "business from ABR / ASIC" : "company from Companies House"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            The auto-match got the wrong company. Search {registry} and pick the correct record —
            we&apos;ll refresh the borrower name{isAus ? "" : ", directors"} and registered address on this deal.
          </p>
          <div className="flex items-center gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") run(); }}
              placeholder={isAus ? "Business name, ABN or ACN…" : "Company name or CRN…"} data-testid="ch-manual-search-input" autoFocus />
            <Button onClick={run} disabled={loading || !q.trim()} data-testid="ch-manual-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </Button>
          </div>
          <div className="max-h-[380px] overflow-y-auto divide-y border rounded">
            {searchError && (
              <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 m-2" data-testid="ch-manual-search-error">
                Registry error — {searchError}. If it mentions the ABR GUID, add it under Portal Settings → API Credentials → ABR.
              </p>
            )}
            {(results || []).length === 0 && !loading && !searchError && (
              <p className="text-xs text-muted-foreground p-3">No results yet — search above.</p>
            )}
            {(results || []).map((item, i) => {
              const crn = item.company_number || item.company_number_ch;
              const isCurrent = crn && currentCrn && crn === currentCrn;
              return (
                <div key={i} className="p-2.5 flex items-start justify-between gap-2 hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{item.company_name || item.title}</p>
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-mono">{crn}</span>
                      {item.company_status && <span> · {item.company_status}</span>}
                      {item.date_of_creation && <span> · inc. {item.date_of_creation}</span>}
                    </p>
                    {item.address_snippet && (
                      <p className="text-[10px] text-muted-foreground truncate">{item.address_snippet}</p>
                    )}
                  </div>
                  <Button size="sm" variant={isCurrent ? "outline" : "default"}
                    disabled={loading || !crn} onClick={() => onPick(crn)}
                    data-testid={`ch-pick-${crn}`}>
                    {isCurrent ? "Current" : "Pick"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

